import React, { useState, useEffect } from 'react';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import usePermissions from './usePermissions';
import { 
    FileSpreadsheet, ArrowRight, Search, Eye, Printer, Filter, X, Check, 
    ChevronDown, Download, FileText, RefreshCw, Users, Building, Briefcase,
    Calendar, User, Package, Phone, CreditCard, TrendingUp, TrendingDown,
    Plane, CheckCircle, Clock, AlertCircle, Image, Repeat, Fingerprint, Barcode, ListPlus
} from 'lucide-react';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import {
    getSyncedBarcodeSettings,
    getBarcodeProfileSettingsById,
    printPassportBarcodeWithSettings
} from './PassportManagement';
import WorkListPickerModal from './WorkListPickerModal';


const PassportReport = () => {
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const dataPermissions = permissions?.['البيانات'] || {};
    const workListPermissions = permissions?.['إدارة قوائم العمل'] || {};
    const workListPermissionGroupDefined = permissions && ('إدارة قوائم العمل' in permissions);
    const canUseWorkLists = (!Object.prototype.hasOwnProperty.call(dataPermissions, 'قوائم العمل') || dataPermissions['قوائم العمل'] === true) &&
        (!workListPermissionGroupDefined || workListPermissions['إضافة أشخاص'] === true);

    // ✅ صلاحيات مركز التقارير - الجوازات
    const rptFilters = permissions?.['مركز التقارير - الجوازات - الفلاتر'] || {};
    const rptButtons = permissions?.['مركز التقارير - الجوازات - الأزرار'] || {};
    // إذا لم يوجد القسم أصلاً في الـ permissions → اعرض كل شيء (مستخدم قديم)
    // إذا وُجد القسم → اعرض فقط المحدد بـ true
    const filtersGroupDefined = permissions && ('مركز التقارير - الجوازات - الفلاتر' in permissions);
    const buttonsGroupDefined = permissions && ('مركز التقارير - الجوازات - الأزرار' in permissions);
    const hasFilterPermission = (key) => Object.prototype.hasOwnProperty.call(rptFilters, key);
    // ✅ يدعم صلاحيات تفصيلية جديدة مع الحفاظ على المستخدمين القدام
    // لو الصلاحية التفصيلية موجودة صراحةً: نلتزم بها.
    // لو غير موجودة عند مستخدم قديم: نرجع لصلاحية المجموعة القديمة كـ fallback.
    const canShowFilter = (key, fallbackKeys = []) => {
        if (!filtersGroupDefined) return true;
        if (hasFilterPermission(key)) return !!rptFilters[key];
        return fallbackKeys.some(fallbackKey => !!rptFilters[fallbackKey]);
    };
    const canShowAnyFilter = (keys, fallbackKeys = []) => keys.some(key => canShowFilter(key, fallbackKeys));
    const canShowBtn = (key, fallbackKeys = []) => {
        if (!buttonsGroupDefined) return true;
        if (Object.prototype.hasOwnProperty.call(rptButtons, key)) return !!rptButtons[key];
        return fallbackKeys.some(fallbackKey => !!rptButtons[fallbackKey]);
    };

    // الصلاحيات الحساسة الجديدة لا تستخدم توافق المستخدمين القدامى أو أي صلاحية بديلة.
    // يظل الزر مخفيًا حتى يتم تفعيل الصلاحية نفسها صراحةً للمستخدم.
    const canShowStrictBtn = (key) =>
        Object.prototype.hasOwnProperty.call(rptButtons, key) && rptButtons[key] === true;

    const [isLoading, setIsLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [professions, setProfessions] = useState([]);
    const [delegates, setDelegates] = useState([]);
    const [savedLists, setSavedLists] = useState([]);

    const [selectedPassports, setSelectedPassports] = useState([]);

    // إضافة أشخاص محددين من أي نتيجة/قائمة ظاهرة إلى قوائم العمل.
    const [showWorkListPeopleModal, setShowWorkListPeopleModal] = useState(false);
    const [workListPeopleSearch, setWorkListPeopleSearch] = useState('');
    const [workListPeoplePage, setWorkListPeoplePage] = useState(1);
    const [workListSelectedPassportIds, setWorkListSelectedPassportIds] = useState(new Set());
    const [showWorkListPicker, setShowWorkListPicker] = useState(false);
    const [workListPickerItems, setWorkListPickerItems] = useState([]);

    // طباعة باركود جماعية من طرق الطباعة المحفوظة فقط (بدون نافذة ضبط الباركود)
    const [showBarcodeBatchPrintModal, setShowBarcodeBatchPrintModal] = useState(false);
    const [barcodeBatchProfiles, setBarcodeBatchProfiles] = useState([]);
    const [barcodeBatchBaseSettings, setBarcodeBatchBaseSettings] = useState(null);
    const [barcodeBatchProfilesLoading, setBarcodeBatchProfilesLoading] = useState(false);
    const [barcodeBatchPrinting, setBarcodeBatchPrinting] = useState(false);
    const [barcodeBatchPrintingProfileId, setBarcodeBatchPrintingProfileId] = useState('');
    const [barcodeBatchProgress, setBarcodeBatchProgress] = useState(null); // { current, total, name, printed }
    const [barcodeBatchStopRequested, setBarcodeBatchStopRequested] = useState(false);
    const barcodeBatchCancelRef = React.useRef(false);
    const barcodeBatchIdRef = React.useRef('');

    // طباعة التقرير الاحترافي عبر Masar Print Agent على الشبكة
    const [showAgentPrintModal, setShowAgentPrintModal] = useState(false);
    const [agentPrintAgents, setAgentPrintAgents] = useState([]);
    const [agentPrintLoading, setAgentPrintLoading] = useState(false);
    const [agentPrintSending, setAgentPrintSending] = useState(false);
    const [agentPrintStatus, setAgentPrintStatus] = useState(null);
    const [agentPrintForm, setAgentPrintForm] = useState({
        agentId: '',
        printerName: '',
        copies: 1
    });
    const [showFilters, setShowFilters] = useState(true);
    const [toast, setToast] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchStats, setSearchStats] = useState(null);
    const [delegateSearchTerm, setDelegateSearchTerm] = useState('');
    const [savedListSearchTerm, setSavedListSearchTerm] = useState(''); // ✅ جديد

    const [showExportColumns, setShowExportColumns] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState('');
const [professionSearchTerm, setProfessionSearchTerm] = useState('');
const [travelProfessionSearchTerm, setTravelProfessionSearchTerm] = useState('');
const [prevCompanySearchTerm, setPrevCompanySearchTerm] = useState('');


const [zipLoading, setZipLoading] = React.useState(false);
const [zipProgress, setZipProgress] = React.useState(null); // {current, total}
const [delegateZipLoading, setDelegateZipLoading] = React.useState(false);
const [delegateZipProgress, setDelegateZipProgress] = React.useState(null); // {current, total, delegateName}
const CHUNK_SIZE = 120; // نفس طريقة التصدير العادية لكن بحجم جزء آمن
const ANDROID_CHUNK_SIZE = 40; // تطبيق الموبايل/WebView يحتاج دفعات أصغر
const ZIP_DOWNLOAD_RETRIES = 3;
const ZIP_REQUEST_TIMEOUT_MS = 8 * 60 * 1000;

// ✅ نافذة اختيار نوع الصور للـ ZIP
const [showImageTypeModal, setShowImageTypeModal] = React.useState(false);
const [selectedImageTypes, setSelectedImageTypes] = React.useState({
  passport: true,
  personal: true,
  license: false,
  licenseBack: false,
  idCard: false,
  idCardBack: false,
  companyContract: false,
  ministryContract: false,
  visaFile: false,
  otherAttachments: false,
});
const imageTypeOptions = [
  { key: 'passport',          label: '🛂 صور الجوازات',        field: 'PassportImagePath'     },
  { key: 'personal',          label: '🤳 صور شخصية',            field: 'PersonalImagePath'     },
  { key: 'license',           label: '🚗 رخصة (أمامي)',         field: 'LicenseImagePath'      },
  { key: 'licenseBack',       label: '🚗 رخصة (خلفي)',          field: 'LicenseBackImagePath'  },
  { key: 'idCard',            label: '🪪 بطاقة (أمامي)',         field: 'IDImagePath'           },
  { key: 'idCardBack',        label: '🪪 بطاقة (خلفي)',          field: 'IDBackImagePath'       },
  { key: 'companyContract',   label: '🏢 عقد الشركة',           field: 'CompanyContractPath'   },
  { key: 'ministryContract',  label: '🏛️ عقد الوزارة',          field: 'MinistryContractPath'  },
  { key: 'visaFile',          label: '✈️ التأشيرة',              field: 'VisaFilePath'          },
  { key: 'otherAttachments',  label: '📎 مرفقات أخرى',           field: 'OtherAttachmentsPath'  },
];

// ✅ أدوات حفظ آمنة للتطبيق على الموبايل:
// WebView لا يتعامل دائمًا بشكل صحيح مع روابط blob أو الضغط على a.download.
// عند فتح النظام داخل تطبيق مسار يتم الحفظ مباشرة من خلال MasarAndroidBridge.
const isMasarAndroidBridgeAvailable = () => {
    try {
        return typeof window !== 'undefined' &&
            !!window.MasarAndroidBridge &&
            typeof window.MasarAndroidBridge.saveBase64File === 'function';
    } catch {
        return false;
    }
};

const isAndroidWebViewExport = () => {
    try {
        const ua = navigator.userAgent || '';
        return isMasarAndroidBridgeAvailable() || (/Android/i.test(ua) && (/\bwv\b/i.test(ua) || /Version\/\d+.*Chrome/i.test(ua)));
    } catch {
        return false;
    }
};

const sanitizeDownloadFileName = (name, fallback = 'Masar-file') => {
    const cleaned = String(name || fallback)
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/[\u0000-\u001f\u007f]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    return cleaned || fallback;
};

const saveBlobFile = async (blob, fileName, mimeType = 'application/octet-stream') => {
    const safeName = sanitizeDownloadFileName(fileName);
    const safeMime = blob?.type || mimeType || 'application/octet-stream';

    // داخل تطبيق Android: احفظ مباشرة في Downloads/Masar بدون الاعتماد على رابط blob.
    if (isMasarAndroidBridgeAvailable()) {
        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = String(reader.result || '');
                const encoded = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
                encoded ? resolve(encoded) : reject(new Error('تعذر تجهيز الملف للحفظ'));
            };
            reader.onerror = () => reject(new Error('تعذر قراءة الملف'));
            reader.readAsDataURL(blob);
        });
        window.MasarAndroidBridge.saveBase64File(safeName, safeMime, base64);
        return 'android-bridge';
    }

    // على الكمبيوتر/المتصفح العادي: نفس طريقة التحميل القديمة.
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return 'browser';
};

const saveBase64FileToAndroid = (fileName, mimeType, base64) => {
    if (!isMasarAndroidBridgeAvailable()) return false;
    window.MasarAndroidBridge.saveBase64File(sanitizeDownloadFileName(fileName), mimeType, base64);
    return true;
};

const waitForExportRender = () => new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const escapeHtml = (value) => String(value ?? '-')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

function chunk(arr, size) {
    const safeSize = Math.max(1, Number(size) || 1);
    const out = [];
    for (let i = 0; i < arr.length; i += safeSize) out.push(arr.slice(i, i + safeSize));
    return out;
  }

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const getZipErrorMessage = async (resp) => {
    try {
      const contentType = resp.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await resp.json();
        return data?.details || data?.error || `HTTP ${resp.status}`;
      }
      const text = await resp.text();
      return text || `HTTP ${resp.status}`;
    } catch {
      return `HTTP ${resp.status}`;
    }
  };

  const isRetryableZipError = (err) => {
    const msg = String(err?.message || '').toLowerCase();
    return msg.includes('failed to fetch') ||
      msg.includes('network') ||
      msg.includes('timeout') ||
      msg.includes('aborted') ||
      msg.includes('502') || msg.includes('503') || msg.includes('504');
  };

  async function fetchZipBlobWithRetry(payload, attemptLabel) {
    let lastError = null;

    for (let attempt = 1; attempt <= ZIP_DOWNLOAD_RETRIES; attempt++) {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutId = controller ? setTimeout(() => controller.abort(), ZIP_REQUEST_TIMEOUT_MS) : null;

      try {
        // مهم: requestKey يجعل كل طلب رابط مختلف حتى لا يتعامل معه أي برنامج تحميل خارجي كرابط مكرر.
        const requestKey = `${Date.now()}-${attempt}-${Math.random().toString(36).slice(2)}`;
        const url = `${config.REPORTS_API_URL}/api/reports/passports/export-images-zip?requestKey=${encodeURIComponent(requestKey)}&label=${encodeURIComponent(attemptLabel || 'zip')}`;

        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/octet-stream, application/json',
                'X-Masar-Zip-Fetch': '1'
          },
          cache: 'no-store',
          signal: controller?.signal,
          body: JSON.stringify(payload)
        });

        if (!resp.ok) {
          const details = await getZipErrorMessage(resp);
          const error = new Error(`تعذر إنشاء الملف (${resp.status}) - ${details}`);
          error.status = resp.status;
          throw error;
        }

        const downloadedBlob = await resp.blob();
        if (!downloadedBlob || downloadedBlob.size === 0) {
          throw new Error('تم إنشاء ملف ZIP فارغ');
        }

        // نعيد نوع الملف ZIP للتحميل المحلي، حتى لو السيرفر أرسله بنوع مخصص لتفادي برامج التحميل الخارجية.
        return new Blob([downloadedBlob], { type: 'application/zip' });
      } catch (err) {
        lastError = err;
        const retryable = isRetryableZipError(err) || (err?.status >= 500 && err?.status < 600);
        if (attempt < ZIP_DOWNLOAD_RETRIES && retryable) {
          showToast?.(`${attemptLabel}: محاولة إعادة الاتصال ${attempt + 1}/${ZIP_DOWNLOAD_RETRIES}...`, 'warning');
          await sleep(700 * attempt);
          continue;
        }
        break;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }

    const raw = String(lastError?.message || 'تعذر الاتصال بالسيرفر');
    if (raw.toLowerCase().includes('failed to fetch')) {
      throw new Error('انقطع الاتصال بالسيرفر أثناء تجهيز ملف الصور. سيتم تقسيم الجزء تلقائيًا إلى أجزاء أصغر بدل إلغاء التصدير.');
    }
    if (raw.toLowerCase().includes('aborted')) {
      throw new Error('انتهت مهلة تجهيز ملف الصور. سيتم تقسيم الجزء تلقائيًا إلى أجزاء أصغر بدل إلغاء التصدير.');
    }
    throw new Error(raw);
  }

  async function downloadZip(ids, partIndex, partsTotal, imageTypes = []) {
    const attemptLabel = `الجزء ${partIndex + 1} من ${partsTotal}`;
    const blob = await fetchZipBlobWithRetry({ passportIds: ids, imageTypes }, attemptLabel);
    const dateStr = new Date().toLocaleDateString('ar-EG').replace(/[\\/:*?"<>|]+/g, '-');
    const part = partsTotal > 1 ? `-جزء-${partIndex + 1}من${partsTotal}` : '';
    await saveBlobFile(blob, `صور-الجوازات${part}-${dateStr}.zip`, 'application/zip');
  }


  async function downloadZipBatchWithLabel(ids, label, imageTypes = []) {
    const safeLabel = sanitizeDownloadFileName(label || 'جزء', 'جزء');
    const blob = await fetchZipBlobWithRetry({ passportIds: ids, imageTypes }, safeLabel);
    const dateStr = new Date().toLocaleDateString('ar-EG').replace(/[\/:*?"<>|]+/g, '-');
    await saveBlobFile(blob, `صور-الجوازات-${safeLabel}-${dateStr}.zip`, 'application/zip');
  }

  async function downloadZipAdaptive(ids, label, imageTypes = [], failedItems = []) {
    try {
      await downloadZipBatchWithLabel(ids, label, imageTypes);
      return;
    } catch (err) {
      if (ids.length > 1) {
        const mid = Math.ceil(ids.length / 2);
        showToast?.(`${label}: سيتم تقسيم الجزء تلقائيًا إلى أجزاء أصغر`, 'warning');
        await sleep(300);
        await downloadZipAdaptive(ids.slice(0, mid), `${label}-أ`, imageTypes, failedItems);
        await sleep(300);
        await downloadZipAdaptive(ids.slice(mid), `${label}-ب`, imageTypes, failedItems);
        return;
      }

      failedItems.push({ id: ids[0], error: err?.message || 'تعذر التصدير' });
      showToast?.(`تعذر تصدير صور الجواز ID ${ids[0]} وسيتم إضافته في تقرير الملاحظات`, 'warning');
    }
  }

  async function saveImageZipFailuresReport(failedItems = []) {
    if (!Array.isArray(failedItems) || failedItems.length === 0) return;
    const header = 'PassportID,Error';
    const rows = failedItems.map(item => {
      const id = String(item.id || '').replace(/"/g, '""');
      const error = String(item.error || '').replace(/"/g, '""');
      return `"${id}","${error}"`;
    });
    const csv = '\uFEFF' + [header, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const dateStr = new Date().toLocaleDateString('ar-EG').replace(/[\/:*?"<>|]+/g, '-');
    await saveBlobFile(blob, `تقرير-صور-لم-يتم-تصديرها-${dateStr}.csv`, 'text/csv');
  }
  

  // الانتقال للتكرار الذكي
const navigateToSmartRepeat = () => {
    if (selectedPassports.length === 0) {
        showToast('يرجى عرض نتائج البحث أولاً', 'warning');
        return;
    }
    
    // جمع كل البيانات المطلوبة
    navigate('/smart-repeat', {
        state: {
            passportData: selectedPassports
        }
    });
};


  // يفتح النافذة أولاً، التصدير الفعلي يحدث بعد الضغط على "تصدير" في النافذة
  function handleExportImagesZip() {
    if (!Array.isArray(selectedPassports) || selectedPassports.length === 0) {
      showToast?.('مفيش نتائج حالية للتصدير. اعمل بحث الأول.', 'warning');
      return;
    }
    setShowImageTypeModal(true);
  }

  async function getJsonResponse(resp) {
    try {
      return await resp.json();
    } catch {
      return null;
    }
  }

  async function startImageZipExportJob(passportIds, imageTypes) {
    const resp = await fetch(`${config.REPORTS_API_URL}/api/reports/passports/export-images-zip/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({ passportIds, imageTypes })
    });

    const data = await getJsonResponse(resp);
    if (!resp.ok || !data?.success) {
      throw new Error(data?.details || data?.error || `تعذر بدء تجهيز الملف (${resp.status})`);
    }
    return data;
  }

  async function readImageZipExportJobStatus(jobId) {
    const requestKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const resp = await fetch(`${config.REPORTS_API_URL}/api/reports/passports/export-images-zip/status/${encodeURIComponent(jobId)}?requestKey=${encodeURIComponent(requestKey)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      cache: 'no-store'
    });

    const data = await getJsonResponse(resp);
    if (!resp.ok || !data?.success) {
      throw new Error(data?.details || data?.error || `تعذر قراءة حالة التصدير (${resp.status})`);
    }
    return data;
  }

  async function waitForImageZipExportJob(jobId, totalItems) {
    const startedAt = Date.now();
    const maxWaitMs = 45 * 60 * 1000; // ندي السيرفر وقته كاملًا بدل إعادة الطلب والضغط عليه
    let pollCount = 0;

    while (Date.now() - startedAt < maxWaitMs) {
      pollCount += 1;
      await sleep(pollCount === 1 ? 1200 : 2500);
      const status = await readImageZipExportJobStatus(jobId);

      if (status.status === 'ready') return status;
      if (status.status === 'failed') {
        throw new Error(status.error || 'فشل تجهيز ملف الصور على السيرفر');
      }

      const elapsedSeconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      setZipProgress({
        current: Math.min(totalItems, elapsedSeconds),
        total: totalItems,
        message: `جاري تجهيز ZIP على السيرفر... انتظر بدون ضغط (${elapsedSeconds} ثانية)`
      });
    }

    throw new Error('انتهت مهلة الانتظار قبل اكتمال تجهيز ملف الصور');
  }

  async function triggerReadyImageZipDownload(jobId, zipName) {
    const requestKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const relativeUrl = `/api/reports/passports/export-images-zip/download/${encodeURIComponent(jobId)}?downloadKey=${encodeURIComponent(requestKey)}`;
    const absoluteUrl = `${config.REPORTS_API_URL}${relativeUrl}`;
    const safeZipName = sanitizeDownloadFileName(zipName || `صور-الجوازات-${new Date().toISOString().slice(0,10)}.zip`, 'صور-الجوازات.zip');

    if (isMasarAndroidBridgeAvailable()) {
      const resp = await fetch(absoluteUrl, { cache: 'no-store' });
      if (!resp.ok) throw new Error('تم تجهيز الملف لكن تعذر تحميله داخل التطبيق');
      const blob = await resp.blob();
      await saveBlobFile(blob, safeZipName, 'application/zip');
      return;
    }

    // ✅ تحميل بدون فتح تاب جديدة about:blank
    // نستخدم iframe مخفي لأن التحميل من سيرفر التقارير على port مختلف.
    // السيرفر يرسل Content-Disposition: attachment باسم الملف، لذلك سيبدأ التحميل بدون مغادرة الصفحة.
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src = absoluteUrl;
    document.body.appendChild(iframe);

    // نتركه وقت كافي حتى يلتقط المتصفح/برنامج التحميل الملف، ثم نحذفه من الصفحة.
    setTimeout(() => {
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch {}
    }, 2 * 60 * 1000);
  }

  async function handleConfirmImageZipExport() {
    const chosen = imageTypeOptions
      .filter(opt => selectedImageTypes[opt.key])
      .map(opt => opt.field);

    if (chosen.length === 0) {
      showToast?.('اختار نوع صورة واحد على الأقل', 'warning');
      return;
    }

    setShowImageTypeModal(false);
    try {
      setZipLoading(true);
      setZipProgress({ current: 0, total: 1, message: 'جاري إرسال طلب التصدير للسيرفر...' });

      const visiblePassports = getWorkingPassports();
      const passportIds = visiblePassports.map(p => p?.PassportID).filter(Boolean);

      if (passportIds.length === 0) {
        showToast?.('لا توجد بيانات ظاهرة للتصدير', 'warning');
        return;
      }

      showToast?.('تم إرسال طلب التصدير. انتظر حتى يجهز الملف ولا تضغط على الزر مرة أخرى.', 'info');

      const job = await startImageZipExportJob(passportIds, chosen);
      setZipProgress({
        current: 0,
        total: passportIds.length,
        message: `جاري تجهيز ZIP لعدد ${passportIds.length} جواز على السيرفر...`
      });

      const ready = await waitForImageZipExportJob(job.jobId, passportIds.length);
      setZipProgress({
        current: passportIds.length,
        total: passportIds.length,
        message: 'تم تجهيز الملف. جاري بدء التحميل...'
      });

      await triggerReadyImageZipDownload(job.jobId, ready.zipName);

      const notes = ready.missingCount > 0
        ? ` مع وجود ${ready.missingCount} ملاحظة داخل ملف المفقودات.csv`
        : '';
      showToast?.(`تم تجهيز ملف ZIP وبدأ التحميل ✅${notes}`, ready.missingCount > 0 ? 'warning' : 'success');
    } catch (e) {
      console.error(e);
      showToast?.('خطأ أثناء التصدير: ' + (e?.message || 'تعذر تجهيز ملف الصور'), 'error');
    } finally {
      setZipLoading(false);
      setZipProgress(null);
    }
  }
  





// إعدادات الترتيب
const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc' // asc or desc
});

// إعدادات عرض الملاحظات
const [notesDisplayMode, setNotesDisplayMode] = useState('combined'); // 'combined' or 'separated'
const [noteFilters, setNoteFilters] = useState({
    allNotes: '',
    note1: '',
    note2: '',
    note3: '',
    note4: ''
});
    
// إخفاء أسماء معينة مؤقتاً + ملاحظات مؤقتة (لا تُحفظ في قاعدة البيانات)
const [tempHidden, setTempHidden] = useState({}); // { [PassportID]: true }
const [tempNotes, setTempNotes] = useState({});   // { [PassportID]: 'نص الملاحظة' }
const [hideOriginalNotes, setHideOriginalNotes] = useState(false); // إخفاء الملاحظات الأصلية

// ⚙️ إعدادات تصدير الصورة
const [showReportSettingsModal, setShowReportSettingsModal] = useState(false);
const [imageExportSettings, setImageExportSettings] = useState({
    showHeader: true,
    showTotal: true,
    showPageNum: true,
    showFooter: true,
    customTitle: '',
    maxRowsPerImage: 25,
    colorTheme: 'blue',
});

// 🎨 ثيمات الألوان
const colorThemes = {
    blue:      { label: 'أزرق سماوي',   dot: '#1976d2', bg: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)',   headerBg: 'linear-gradient(135deg,#1976d2,#42a5f5)',   headerBorder: '#0d47a1', cardBg: 'linear-gradient(135deg,#e3f2fd,#bbdefb)',   cardBorder: '#1976d2', titleColor: '#0d47a1', rowOdd: '#ffffff', rowEven: '#e3f2fd', cellBorder: '#bbdefb',   footerBg: 'linear-gradient(135deg,#e3f2fd,#bbdefb)',   footerBorder: '#1976d2' },
    navyBlue:  { label: 'أزرق داكن',    dot: '#1a237e', bg: 'linear-gradient(135deg,#e8eaf6,#c5cae9)',   headerBg: 'linear-gradient(135deg,#1a237e,#3949ab)',   headerBorder: '#0d1b6e', cardBg: 'linear-gradient(135deg,#e8eaf6,#c5cae9)',   cardBorder: '#1a237e', titleColor: '#0d1b6e', rowOdd: '#ffffff', rowEven: '#e8eaf6', cellBorder: '#c5cae9',   footerBg: 'linear-gradient(135deg,#e8eaf6,#c5cae9)',   footerBorder: '#1a237e' },
    green:     { label: 'أخضر زمردي',   dot: '#2e7d32', bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',   headerBg: 'linear-gradient(135deg,#2e7d32,#66bb6a)',   headerBorder: '#1b5e20', cardBg: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)',   cardBorder: '#2e7d32', titleColor: '#1b5e20', rowOdd: '#ffffff', rowEven: '#e8f5e9', cellBorder: '#c8e6c9',   footerBg: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)',   footerBorder: '#2e7d32' },
    teal:      { label: 'تيل فيروزي',   dot: '#00695c', bg: 'linear-gradient(135deg,#f0fdfa,#ccfbf1)',   headerBg: 'linear-gradient(135deg,#00695c,#26a69a)',   headerBorder: '#004d40', cardBg: 'linear-gradient(135deg,#e0f2f1,#b2dfdb)',   cardBorder: '#00695c', titleColor: '#004d40', rowOdd: '#ffffff', rowEven: '#e0f2f1', cellBorder: '#b2dfdb',   footerBg: 'linear-gradient(135deg,#e0f2f1,#b2dfdb)',   footerBorder: '#00695c' },
    purple:    { label: 'بنفسجي',        dot: '#6a1b9a', bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)',   headerBg: 'linear-gradient(135deg,#6a1b9a,#ab47bc)',   headerBorder: '#4a148c', cardBg: 'linear-gradient(135deg,#f3e5f5,#e1bee7)',   cardBorder: '#6a1b9a', titleColor: '#4a148c', rowOdd: '#ffffff', rowEven: '#f3e5f5', cellBorder: '#e1bee7',   footerBg: 'linear-gradient(135deg,#f3e5f5,#e1bee7)',   footerBorder: '#6a1b9a' },
    indigo:    { label: 'نيلي أنيق',     dot: '#3730a3', bg: 'linear-gradient(135deg,#eef2ff,#e0e7ff)',   headerBg: 'linear-gradient(135deg,#3730a3,#6366f1)',   headerBorder: '#312e81', cardBg: 'linear-gradient(135deg,#e0e7ff,#c7d2fe)',   cardBorder: '#3730a3', titleColor: '#312e81', rowOdd: '#ffffff', rowEven: '#eef2ff', cellBorder: '#c7d2fe',   footerBg: 'linear-gradient(135deg,#e0e7ff,#c7d2fe)',   footerBorder: '#3730a3' },
    orange:    { label: 'برتقالي دافئ', dot: '#e65100', bg: 'linear-gradient(135deg,#fff7ed,#ffedd5)',   headerBg: 'linear-gradient(135deg,#e65100,#fb8c00)',   headerBorder: '#bf360c', cardBg: 'linear-gradient(135deg,#fff3e0,#ffe0b2)',   cardBorder: '#e65100', titleColor: '#bf360c', rowOdd: '#ffffff', rowEven: '#fff3e0', cellBorder: '#ffe0b2',   footerBg: 'linear-gradient(135deg,#fff3e0,#ffe0b2)',   footerBorder: '#e65100' },
    red:       { label: 'أحمر رسمي',    dot: '#c62828', bg: 'linear-gradient(135deg,#fff5f5,#fee2e2)',   headerBg: 'linear-gradient(135deg,#c62828,#ef5350)',   headerBorder: '#b71c1c', cardBg: 'linear-gradient(135deg,#ffebee,#ffcdd2)',   cardBorder: '#c62828', titleColor: '#b71c1c', rowOdd: '#ffffff', rowEven: '#ffebee', cellBorder: '#ffcdd2',   footerBg: 'linear-gradient(135deg,#ffebee,#ffcdd2)',   footerBorder: '#c62828' },
    rose:      { label: 'وردي ناعم',    dot: '#be185d', bg: 'linear-gradient(135deg,#fff1f2,#ffe4e6)',   headerBg: 'linear-gradient(135deg,#be185d,#f43f5e)',   headerBorder: '#9d174d', cardBg: 'linear-gradient(135deg,#fce7f3,#fbcfe8)',   cardBorder: '#be185d', titleColor: '#9d174d', rowOdd: '#ffffff', rowEven: '#fce7f3', cellBorder: '#fbcfe8',   footerBg: 'linear-gradient(135deg,#fce7f3,#fbcfe8)',   footerBorder: '#be185d' },
    brown:     { label: 'بني كلاسيك',   dot: '#4e342e', bg: 'linear-gradient(135deg,#fdf6f0,#fbe8d8)',   headerBg: 'linear-gradient(135deg,#4e342e,#8d6e63)',   headerBorder: '#3e2723', cardBg: 'linear-gradient(135deg,#efebe9,#d7ccc8)',   cardBorder: '#4e342e', titleColor: '#3e2723', rowOdd: '#ffffff', rowEven: '#efebe9', cellBorder: '#d7ccc8',   footerBg: 'linear-gradient(135deg,#efebe9,#d7ccc8)',   footerBorder: '#4e342e' },
    gray:      { label: 'رمادي رسمي',   dot: '#374151', bg: 'linear-gradient(135deg,#f8fafc,#f1f5f9)',   headerBg: 'linear-gradient(135deg,#374151,#6b7280)',   headerBorder: '#1f2937', cardBg: 'linear-gradient(135deg,#f3f4f6,#e5e7eb)',   cardBorder: '#374151', titleColor: '#1f2937', rowOdd: '#ffffff', rowEven: '#f3f4f6', cellBorder: '#e5e7eb',   footerBg: 'linear-gradient(135deg,#f3f4f6,#e5e7eb)',   footerBorder: '#374151' },
    darkGreen: { label: 'أخضر داكن',    dot: '#1a3c2a', bg: 'linear-gradient(135deg,#f0f7f4,#d4eae0)',   headerBg: 'linear-gradient(135deg,#1a3c2a,#2d6a4f)',   headerBorder: '#0f2e1d', cardBg: 'linear-gradient(135deg,#d8f3dc,#b7e4c7)',   cardBorder: '#1a3c2a', titleColor: '#0f2e1d', rowOdd: '#ffffff', rowEven: '#d8f3dc', cellBorder: '#b7e4c7',   footerBg: 'linear-gradient(135deg,#d8f3dc,#b7e4c7)',   footerBorder: '#1a3c2a' },
};

// متغيرات التصفح بالصفحات
const [currentPage, setCurrentPage] = useState(1);
const [itemsPerPage, setItemsPerPage] = useState(50);

// في بداية الكومبوننت، حدث visibleColumns ليشمل الأعمدة المفقودة:
const [visibleColumns, setVisibleColumns] = useState({
    serialNumber: true,
    nameArabic: true,
    passportId: true,
    company: true,
    delegate: true,
    phoneNumbers: true,
    passportNumber: true,
    passportProfession: false,
    governorate: true,
    notes: true,
    // أعمدة الملاحظات المنفصلة
    note1: false,
    note2: false,
    note3: false,
    note4: false,
    // باقي الأعمدة مخفية افتراضياً
    nameEnglish: false,
    nationalId: false,
    previousCompanies: false,
    blacklistCompanies: false,
    blacklistReasons: false,
    travelProfession: true,
    medicalExamStatus: false,
    medicalExamDate: false,
    networkDate: false,
    visaDate: false,
    travelDate: false,
    borderNumber: false,
    violationNotes: false,
    travelDuration: false,
    passportStatus: false,
    receiveStatus: false,
    address: false,
    age: false,
    birthDate: false,
    militaryStatus: false,
    passportIssueDate: false,
    passportExpiryDate: false,
    licenseType: false,
    licenseExpiryDate: false,
    abilityTest: false,
    receiveDate: false,
    receiverName: false,
    receiverPhone: false,
    // 🆕 الحقول الجديدة من المتابعة الدورية
applicationNumber: false,
    fingerprintDate: false,
    consulateDate: false,
    documentsDeliveryStatus: false,
    medicalInsuranceDate: false
});

    const travelDurations = ['3 أشهر', '4 أشهر', '6 أشهر', 'عقد', 'تعقيب'];
    const passportStatuses = ['جديد', 'قديم شركات'];
    const abilityTests = ['لم يختبر', 'لائق', 'غير لائق', 'اساسى', 'احتياطي', 'سائق ثاني'];
    const licenseTypes = ['أولى', 'ثانية', 'ثالثة', 'خاصة', 'لا يوجد'];
    const receiveStatuses = ['لم يستلم', 'استلم العميل', 'استلم المندوب', 'استلم صديق'];
    const deliveryStatuses = ['عرض الكل', 'الغير مسلمة فقط', 'المسلمة فقط'];
    const processStatuses = ['عرض الكل', 'الفعلية فقط', 'المعالجة فقط'];

    const isRealDelegate = (delegateName) => {
        if (!delegateName || delegateName.trim() === '') return false;
        const officeName = delegateName.toLowerCase().trim();
        const officeKeywords = [
            'مكتب', 'المكتب', 'مكتب', 
            'شركة', 'الشركة', 
            'office', 'company'
        ];
        return !officeKeywords.some(keyword => 
            officeName.includes(keyword.toLowerCase())
        );
    };

 const [filters, setFilters] = useState({
    listIds: [],          // ✅ بدل listId
    excludeList: false,   // ⬅️ زى ما هي
    includeArchivedHistory: false, // ✅ إظهار الأرشيف السابق لنفس أشخاص القائمة عند التفعيل فقط

    dateRange: { from: '', to: '' },
    medicalDateRange: { from: '', to: '' },
    networkDateRange: { from: '', to: '' },
fingerprintDateRange: { from: '', to: '' },
    consulateDateRange: { from: '', to: '' },
    insuranceDateRange: { from: '', to: '' },
    visaDateRange: { from: '', to: '' },
    travelDateRange: { from: '', to: '' },
    delegateId: '',
    selectedDelegates: [],
    passportProfessionId: '',
    travelProfessionId: '',
    selectedCompanies: [],
    medicalExamStatus: '',
    search: '',
    travelDuration: '',
    passportStatus: '',
    previousCompanies: [],
    abilityTest: '',
    licenseType: '',
    receiveStatus: '',
    activeStatus: 'active',
    deliveryFilter: 'الغير مسلمة فقط',
    processFilter: 'الفعلية فقط',
    selectedProfessions: [],
    selectedTravelProfessions: [],
    processingStageFilter: ''
});


    const [exportColumns, setExportColumns] = useState({
        serialNumber: true,
        nameArabic: true,
        passportId: true,
        company: true,
        delegate: true,
        phoneNumbers: true,
        passportNumber: true,
        passportProfession: false,
        governorate: true,
        notes: true,
        note1: false,
        note2: false,
        note3: false,
        note4: false,
        nameEnglish: false,
        nationalId: false,
        previousCompanies: false,
        blacklistCompanies: false,
        blacklistReasons: false,
        travelProfession: true,
        medicalExamStatus: false,
        medicalExamDate: false,
        networkDate: false,
        visaDate: false,
        travelDate: false,
        borderNumber: false,
        violationNotes: false,
        travelDuration: false,
        passportStatus: false,
        receiveStatus: false,
        address: false,
        age: false,
        birthDate: false,
        militaryStatus: false,
        passportIssueDate: false,
        passportExpiryDate: false,
        licenseType: false,
        licenseExpiryDate: false,
     applicationNumber: false,
        fingerprintDate: false,
        consulateDate: false,
        documentsDeliveryStatus: false,
        medicalInsuranceDate: false,
        abilityTest: false,
        receiveDate: false,
        receiverName: false,
        receiverPhone: false
    });

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportOptions, setExportOptions] = useState({
        format: 'excel',
        includeFilters: true,
        includeSummary: true,
        dateRange: 'all',
        customStartDate: '',
        customEndDate: '',
        selectedCategories: [],
        selectedTypes: ['all'],
        includeInactive: false,
        groupByCompany: false,
        groupByDelegate: false,
        showDetailed: true
    });




    // دوال التصفح بالصفحات
const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
};

const getTotalPages = (data) => {
    return Math.ceil(data.length / itemsPerPage);
};

const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
};

const goToFirstPage = () => {
    setCurrentPage(1);
};

const goToLastPage = (data) => {
    setCurrentPage(getTotalPages(data));
};

const goToPrevPage = () => {
    if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
    }
};

const goToNextPage = (data) => {
    if (currentPage < getTotalPages(data)) {
        setCurrentPage(currentPage + 1);
    }
};

// مكون التنقل بين الصفحات
const PaginationControls = ({ data }) => {
    const totalPages = getTotalPages(data);
    const totalItems = data.length;
    const currentStart = (currentPage - 1) * itemsPerPage + 1;
    const currentEnd = Math.min(currentPage * itemsPerPage, totalItems);

    const getVisiblePages = () => {
        const delta = 2;
        const range = [];
        
        let start = Math.max(1, currentPage - delta);
        let end = Math.min(totalPages, currentPage + delta);
        
        if (end - start < 4) {
            if (start === 1) {
                end = Math.min(totalPages, start + 4);
            } else {
                start = Math.max(1, end - 4);
            }
        }
        
        for (let i = start; i <= end; i++) {
            range.push(i);
        }
        return range;
    };

    if (totalPages <= 1) return null;

    const visiblePages = getVisiblePages();

    return (
        <div className="bg-white px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
                <div className="flex items-center text-sm text-gray-700">
                    <span>
                        عرض {currentStart} - {currentEnd} من أصل {totalItems} جواز
                    </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap justify-center">
                    <label className="text-sm text-gray-700">عرض:</label>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(parseInt(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                    <span className="text-sm text-gray-700">في الصفحة</span>
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={goToFirstPage}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        الأولى
                    </button>

                    <button
                        onClick={goToPrevPage}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        السابق
                    </button>

                    {visiblePages[0] > 1 && (
                        <>
                            <button
                                onClick={() => goToPage(1)}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700"
                            >
                                1
                            </button>
                            {visiblePages[0] > 2 && (
                                <span className="px-3 py-2 text-sm text-gray-500">...</span>
                            )}
                        </>
                    )}

                    {visiblePages.map(pageNum => (
                        <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                currentPage === pageNum
                                    ? 'text-white bg-blue-600 border border-blue-600'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                            }`}
                        >
                            {pageNum}
                        </button>
                    ))}

                    {visiblePages[visiblePages.length - 1] < totalPages && (
                        <>
                            {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                                <span className="px-3 py-2 text-sm text-gray-500">...</span>
                            )}
                            <button
                                onClick={() => goToPage(totalPages)}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700"
                            >
                                {totalPages}
                            </button>
                        </>
                    )}

                    <button
                        onClick={() => goToNextPage(data)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        التالي
                    </button>

                    <button
                        onClick={() => goToLastPage(data)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        الأخيرة
                    </button>
                </div>

                <div className="text-sm text-gray-600">
                    الصفحة {currentPage} من {totalPages}
                </div>
            </div>
        </div>
    );
};



// دالة لعرض معلومات الفلاتر النشطة
const getActiveFiltersInfo = () => {
    const activeFilters = [];
    
    if (filters.search) activeFilters.push(`البحث: ${filters.search}`);
    
    if (filters.selectedCompanies && filters.selectedCompanies.length > 0 && filters.selectedCompanies.length < companies.length) {
        activeFilters.push(`الشركات: ${filters.selectedCompanies.length} من ${companies.length}`);
    }
    
    if (filters.selectedDelegates && filters.selectedDelegates.length > 0 && filters.selectedDelegates.length < delegates.length) {
        activeFilters.push(`المناديب: ${filters.selectedDelegates.length} من ${delegates.length}`);
    }
    
    if (filters.selectedProfessions && filters.selectedProfessions.length > 0 && filters.selectedProfessions.length < professions.length) {
        activeFilters.push(`المهن: ${filters.selectedProfessions.length} من ${professions.length}`);
    }
    
    if (filters.selectedTravelProfessions && filters.selectedTravelProfessions.length > 0 && filters.selectedTravelProfessions.length < professions.length) {
        activeFilters.push(`مهن السفر: ${filters.selectedTravelProfessions.length} من ${professions.length}`);
    }
    
    if (filters.travelDuration) activeFilters.push(`مدة السفر: ${filters.travelDuration}`);
    if (filters.passportStatus) activeFilters.push(`حالة الجواز: ${filters.passportStatus}`);
    if (filters.receiveStatus) activeFilters.push(`حالة الاستلام: ${filters.receiveStatus}`);
    if (filters.processingStageFilter) activeFilters.push(`مرحلة المعالجة: ${filters.processingStageFilter}`);
    
    return activeFilters;
};

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };


    const getSavedBarcodeProfilesOnly = (settings) => {
        return Array.isArray(settings?.savedProfiles)
            ? settings.savedProfiles.filter(profile => profile?.id && profile?.name && profile?.settings)
            : [];
    };

    const openBarcodeBatchPrintModal = async () => {
        if (!canShowStrictBtn('زر طباعة باركود جماعية')) {
            showToast('ليس لديك صلاحية طباعة الباركود الجماعية', 'error');
            return;
        }

        const passportsToPrint = getWorkingPassports();
        if (!hasSearched || passportsToPrint.length === 0) {
            showToast('اعمل بحث أولًا وتأكد أن هناك أشخاص ظاهرين للطباعة', 'warning');
            return;
        }

        setShowBarcodeBatchPrintModal(true);
        setBarcodeBatchProgress(null);
        setBarcodeBatchProfilesLoading(true);
        setBarcodeBatchStopRequested(false);
        barcodeBatchCancelRef.current = false;
        barcodeBatchIdRef.current = '';

        try {
            const syncedSettings = await getSyncedBarcodeSettings();
            const savedProfiles = getSavedBarcodeProfilesOnly(syncedSettings);
            setBarcodeBatchBaseSettings(syncedSettings);
            setBarcodeBatchProfiles(savedProfiles);

            if (savedProfiles.length === 0) {
                showToast('لا توجد طرق طباعة باركود محفوظة في قاعدة البيانات', 'warning');
            }
        } catch (error) {
            console.error('خطأ في تحميل طرق طباعة الباركود المحفوظة:', error);
            setBarcodeBatchProfiles([]);
            setBarcodeBatchBaseSettings(null);
            showToast('تعذر تحميل طرق الطباعة المحفوظة: ' + (error?.message || 'خطأ غير معروف'), 'error');
        } finally {
            setBarcodeBatchProfilesLoading(false);
        }
    };

    const cancelQueuedBarcodeAgentJobs = async (batchId) => {
        const safeBatchId = String(batchId || '').trim();
        if (!safeBatchId) return { cancelledCount: 0, printingCount: 0 };

        const response = await fetch(
            `${config.API_BASE_URL}/api/passports/barcode/print-agent/batches/${encodeURIComponent(safeBatchId)}/cancel`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: 'أوقف المستخدم الطباعة الجماعية من مركز التقارير' })
            }
        );
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result?.success) {
            throw new Error(result?.error || 'تعذر إلغاء أوامر Agent المعلقة');
        }
        return {
            cancelledCount: Number(result.cancelledCount) || 0,
            printingCount: Number(result.printingCount) || 0
        };
    };

    const stopBarcodeBatchPrint = async () => {
        if (!barcodeBatchPrinting || barcodeBatchStopRequested) return;

        barcodeBatchCancelRef.current = true;
        setBarcodeBatchStopRequested(true);

        let agentMessage = '';
        try {
            const cancelled = await cancelQueuedBarcodeAgentJobs(barcodeBatchIdRef.current);
            if (cancelled.cancelledCount > 0) {
                agentMessage = ` وتم إلغاء ${cancelled.cancelledCount} أمر معلق داخل Agent.`;
            }
            if (cancelled.printingCount > 0) {
                agentMessage += ' الأمر الجاري قد يكتمل لأنه وصل بالفعل للطابعة.';
            }
        } catch (error) {
            console.warn('تعذر إلغاء أوامر Agent المعلقة، سيستمر الإيقاف من الواجهة:', error);
            agentMessage = ' تم إيقاف إرسال أسماء جديدة، لكن تعذر التأكد من حذف الأوامر المعلقة داخل Agent.';
        }

        showToast(`تم طلب الإيقاف. لن يتم إرسال أي شخص جديد بعد الأمر الجاري.${agentMessage}`, 'warning');
    };

    const closeBarcodeBatchPrintModal = () => {
        if (barcodeBatchPrinting) {
            stopBarcodeBatchPrint();
            return;
        }
        setShowBarcodeBatchPrintModal(false);
        setBarcodeBatchProgress(null);
        setBarcodeBatchPrintingProfileId('');
        setBarcodeBatchStopRequested(false);
        barcodeBatchIdRef.current = '';
    };

    const handleBatchBarcodePrint = async (profile) => {
        if (!profile?.id || barcodeBatchPrinting) return;

        const passportsToPrint = getWorkingPassports();
        if (passportsToPrint.length === 0) {
            showToast('لا توجد نتائج ظاهرة للطباعة', 'warning');
            return;
        }

        const copiesPerPerson = Math.max(1, Number(profile?.settings?.printCopies) || 1);
        const confirmed = window.confirm(
            `سيتم طباعة باركود ${passportsToPrint.length} شخص بالتتابع باستخدام طريقة: ${profile.name}` +
            (copiesPerPerson > 1 ? `\nعدد النسخ لكل شخص: ${copiesPerPerson}` : '') +
            '\n\nهل تريد بدء الطباعة الآن؟'
        );
        if (!confirmed) return;

        const batchId = `passport-report-barcode-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        barcodeBatchIdRef.current = batchId;
        barcodeBatchCancelRef.current = false;
        setBarcodeBatchStopRequested(false);
        setBarcodeBatchPrinting(true);
        setBarcodeBatchPrintingProfileId(profile.id);
        setBarcodeBatchProgress({
            current: 0,
            total: passportsToPrint.length,
            name: '',
            printed: 0
        });

        let printedCount = 0;
        let stoppedByError = null;

        try {
            const baseSettings = barcodeBatchBaseSettings || await getSyncedBarcodeSettings();
            const selectedSettings = getBarcodeProfileSettingsById(baseSettings, profile.id);

            for (let index = 0; index < passportsToPrint.length; index += 1) {
                if (barcodeBatchCancelRef.current) break;

                const passport = passportsToPrint[index];
                const personName = passport?.NameArabic || passport?.PassportNumber || `الشخص رقم ${index + 1}`;
                setBarcodeBatchProgress({
                    current: index + 1,
                    total: passportsToPrint.length,
                    name: personName,
                    printed: printedCount
                });

                try {
                    await printPassportBarcodeWithSettings({
                        passport,
                        companies,
                        delegates,
                        professions,
                        settings: selectedSettings,
                        testMode: false,
                        batchId,
                        shouldCancel: () => barcodeBatchCancelRef.current
                    });
                    printedCount += 1;
                    setBarcodeBatchProgress({
                        current: index + 1,
                        total: passportsToPrint.length,
                        name: personName,
                        printed: printedCount
                    });
                } catch (error) {
                    stoppedByError = {
                        passport,
                        name: personName,
                        message: error?.message || 'فشل إرسال أمر الطباعة'
                    };
                    console.error(`فشل طباعة باركود ${personName}:`, error);
                    break;
                }

                // مهلة قصيرة تحافظ على ترتيب أوامر الطباعة داخل الطابعة أو الـ Print Agent.
                if (index < passportsToPrint.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 150));
                }
            }

            if (stoppedByError) {
                showToast(
                    `توقفت الطباعة عند ${stoppedByError.name} بعد نجاح ${printedCount} من ${passportsToPrint.length}: ${stoppedByError.message}`,
                    'error'
                );
            } else if (barcodeBatchCancelRef.current) {
                showToast(`تم إيقاف الطباعة. تمت طباعة ${printedCount} من ${passportsToPrint.length}`, 'warning');
            } else {
                showToast(`تم إرسال باركود ${printedCount} شخص للطباعة بالترتيب ✅`, 'success');
                setTimeout(() => setShowBarcodeBatchPrintModal(false), 500);
            }
        } catch (error) {
            console.error('خطأ في الطباعة الجماعية للباركود:', error);
            showToast('تعذر بدء الطباعة الجماعية: ' + (error?.message || 'خطأ غير معروف'), 'error');
        } finally {
            setBarcodeBatchPrinting(false);
            setBarcodeBatchPrintingProfileId('');
            setBarcodeBatchStopRequested(false);
            barcodeBatchCancelRef.current = false;
            barcodeBatchIdRef.current = '';
        }
    };


    const savedListAdvancedFilterKeys = [
        'إظهار القوائم المحفوظة',
        'إظهار استثناء عناصر القوائم المختارة',
        'إظهار عرض البيانات المؤرشفة للقوائم'
    ];

    const coreAdvancedFilterKeys = [
        ...savedListAdvancedFilterKeys,
        'إظهار حالة التسليم',
        'إظهار حالة المعالجة',
        'إظهار مرحلة المعالجة'
    ];

    const dateFilterPermissionKeys = [
        'إظهار فلتر تاريخ الإضافة',
        'إظهار فلتر تاريخ الكشف الطبي',
        'إظهار فلتر تاريخ عمل النت',
        'إظهار فلتر تاريخ البصمة',
        'إظهار فلتر تاريخ التأمين الطبي',
        'إظهار فلتر تاريخ دخول القنصلية',
        'إظهار فلتر تاريخ التأشير',
        'إظهار فلتر تاريخ السفر'
    ];

    const notesFilterPermissionKeys = [
        'إظهار فلتر جميع الملاحظات',
        'إظهار فلتر ملاحظة 1',
        'إظهار فلتر ملاحظة 2',
        'إظهار فلتر ملاحظة 3',
        'إظهار فلتر ملاحظة 4'
    ];

    // ✅ تصحيح مهم للصلاحيات التفصيلية:
    // لا نجعل الصلاحية العامة شرطًا إجباريًا يخفي الصلاحيات الفرعية.
    // لو المستخدم أخذ صلاحية عنصر فرعي فقط مثل "تاريخ السفر"، لازم يظهر حتى لو لم يتم تفعيل "فلتر التواريخ" العام.
    // وفي نفس الوقت نحافظ على التوافق القديم: الصلاحية العامة القديمة تعرض المجموعة عند المستخدمين القدام.
    const canShowAdvancedFiltersBox = () =>
        canShowFilter('إظهار مربع الفلاتر المتقدمة') ||
        canShowAnyFilter(coreAdvancedFilterKeys);

    const canShowAnyDateFilters = () =>
        canShowFilter('إظهار فلتر التواريخ') ||
        canShowAnyFilter(dateFilterPermissionKeys, ['إظهار فلتر التواريخ']);

    const canShowAnyNotesFilters = () =>
        canShowFilter('إظهار فلاتر الملاحظات') ||
        canShowAnyFilter(notesFilterPermissionKeys, ['إظهار فلاتر الملاحظات']);

    const getColumnLabel = (key) => {
        const labels = {
            passportId: 'رقم الملف',
            serialNumber: 'الرقم التسلسلي',
            nameArabic: 'الاسم بالعربي',
            nameEnglish: 'الاسم بالإنجليزي',
            passportNumber: 'رقم الجواز',
            nationalId: 'الرقم القومي',
            phoneNumbers: 'أرقام الهواتف',
            delegate: 'المندوب',
            company: 'الشركة',
            passportProfession: 'المهنة بالجواز',
            travelProfession: 'مهنة السفر',
            medicalExamStatus: 'حالة الكشف الطبي',
            medicalExamDate: 'تاريخ الكشف الطبي',
            networkDate: 'تاريخ عمل النت',
            visaDate: 'تاريخ التأشير',
            travelDate: 'تاريخ السفر',
            borderNumber: 'رقم الحدود',
            violationNotes: 'المخالفة / ملاحظتها',
            receiveStatus: 'حالة الاستلام',
            address: 'العنوان',
            governorate: 'المحافظة',
            militaryStatus: 'الموقف من التجنيد',
            notes: 'ملاحظات',
            note1: 'ملاحظة 1',
            note2: 'ملاحظة 2', 
            note3: 'ملاحظة 3',
            note4: 'ملاحظة 4',
            previousCompanies: 'الشركات السابقة',
            blacklistCompanies: 'الشركات الممنوع فيها',
            blacklistReasons: 'سبب المنع',
            travelDuration: 'مدة السفر',
            passportStatus: 'حالة الجواز',
            abilityTest: 'نتيجة اختبار القدرات',
            passportIssueDate: 'تاريخ إصدار الجواز',
            passportExpiryDate: 'تاريخ انتهاء الجواز',
            birthDate: 'تاريخ الميلاد',
            age: 'العمر',
            licenseType: 'نوع الرخصة',
            licenseExpiryDate: 'تاريخ انتهاء الرخصة',
            receiveDate: 'تاريخ الاستلام',
            receiverName: 'اسم المستلم',
            receiverPhone: 'رقم هاتف المستلم',
            applicationNumber: 'رقم الطلب',
            fingerprintDate: 'تاريخ البصمة',
            consulateDate: 'تاريخ دخول القنصلية',
       documentsDeliveryStatus: 'حالة تسليم الأوراق',
            medicalInsuranceDate: 'تاريخ التأمين الطبي'
        };
        return labels[key] || key;
    };



    // ✅ Helpers خاصة بعرض كشف الجوازات على الموبايل فقط — لا تغير شكل الكمبيوتر
    const formatMobileDate = (value) => {
        if (!value) return '-';
        try {
            return new Date(value).toLocaleDateString('ar-EG');
        } catch {
            return '-';
        }
    };

    const getCompanyNameById = (companyId) => {
        const company = companies.find(c => c.CompanyID?.toString() === companyId?.toString());
        return company?.CompanyName || '-';
    };

    const getDelegateNameById = (delegateId) => {
        const delegate = delegates.find(d => d.DelegateID?.toString() === delegateId?.toString());
        return delegate?.DelegateName || '-';
    };

    const getProfessionNameById = (professionId) => {
        const profession = professions.find(prof => prof.ProfessionID?.toString() === professionId?.toString());
        return profession?.ProfessionName || '-';
    };

    const getPassportProfessionName = (passport) => {
        const manualText = String(passport?.PassportProfessionText || '').trim();
        if (manualText) return manualText;
        if (passport?.PassportProfessionName) return passport.PassportProfessionName;
        return getProfessionNameById(passport?.PassportProfessionID);
    };

    const getTravelProfessionName = (passport) => {
        if (passport?.TravelProfessionName) return passport.TravelProfessionName;
        return getProfessionNameById(passport?.TravelProfessionID);
    };

    const getMobilePhoneNumbers = (passport) => {
        try {
            const phones = JSON.parse(passport.PhoneNumbers || '[]');
            if (Array.isArray(phones)) {
                return phones.filter(phone => phone && phone.toString().trim()).join(' - ') || '-';
            }
            return passport.PhoneNumbers || '-';
        } catch {
            return passport.PhoneNumbers || '-';
        }
    };

    const getPreviousCompanyNames = (passport) => {
        try {
            const previousCompanies = JSON.parse(passport.PreviousCompanies || '[]');
            if (!Array.isArray(previousCompanies) || previousCompanies.length === 0) return '-';
            const names = previousCompanies
                .map(companyId => getCompanyNameById(companyId))
                .filter(name => name && name !== '-');
            return names.length ? names.join(' ، ') : '-';
        } catch {
            return '-';
        }
    };


    const getPassportFileNumber = (passport) => {
        const value = passport?.OfficeSerial ?? passport?.SerialNumber ?? '';
        const text = String(value ?? '').trim();
        return text || '-';
    };


    // 📱 تصدير كل النتائج المعروضة كجهات اتصال Google/WhatsApp
    const extractContactPhoneNumbers = (phoneNumbers) => {
        if (!phoneNumbers) return [];

        try {
            let phones = [];

            if (typeof phoneNumbers === 'string') {
                const raw = phoneNumbers.trim();
                if (!raw) return [];

                if (raw.startsWith('[')) {
                    try {
                        const parsed = JSON.parse(raw);
                        phones = Array.isArray(parsed) ? parsed : [raw];
                    } catch {
                        phones = raw.split(/[,،\n\r\t|;]/);
                    }
                } else {
                    phones = raw.split(/[,،\n\r\t|;]/);
                }
            } else if (Array.isArray(phoneNumbers)) {
                phones = phoneNumbers;
            } else {
                phones = [phoneNumbers];
            }

            const cleaned = phones
                .map(phone => String(phone ?? '').trim())
                .filter(Boolean)
                .map(phone => {
                    let value = phone;
                    // نفس طريقة تصدير جهات الاتصال المعتمدة في الاختيار والتجهيز
                    if (value.startsWith('+2')) value = value.substring(2);
                    return value.replace(/[^\d]/g, '');
                })
                .filter(phone => phone.length >= 10 && phone.length <= 15);

            return [...new Set(cleaned)];
        } catch (error) {
            console.error('خطأ في معالجة أرقام الهاتف لتصدير جهات الاتصال:', error);
            return [];
        }
    };

    const escapeContactsCsvCell = (value) =>
        `"${String(value ?? '').replace(/"/g, '""')}"`;

    const showContactsExportInstructions = ({
        phoneCount,
        displayedCount,
        peopleWithPhones,
        skippedWithoutPhones,
        labelName,
        fileName
    }) => {
        const oldModal = document.getElementById('reportContactsInstructionsModal');
        if (oldModal) oldModal.remove();

        const safeLabelName = escapeHtml(labelName || 'تقرير الجوازات');
        const safeFileName = escapeHtml(fileName || 'جهات-اتصال-الجوازات.csv');

        const modalHTML = `
            <div id="reportContactsInstructionsModal" class="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" dir="rtl">
                <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                    <div class="bg-green-600 text-white p-6 rounded-t-2xl">
                        <div class="flex justify-between items-center gap-4">
                            <div>
                                <h3 class="text-xl font-bold">📱 تم تصدير ${phoneCount} رقم واتساب بنجاح!</h3>
                                <p class="text-green-100 mt-1">مركز التقارير - الجوازات | إجمالي النتائج المعروضة: ${displayedCount}</p>
                            </div>
                            <button id="closeReportContactsInstructionsBtn" class="text-white hover:bg-green-700 p-2 rounded-full flex-shrink-0" type="button">
                                ✕
                            </button>
                        </div>
                    </div>

                    <div class="p-6 space-y-6">
                        <div class="bg-blue-50 rounded-lg p-6 border border-blue-200">
                            <h4 class="font-bold text-blue-900 mb-4 flex items-center gap-2">
                                🎯 الخطوات التالية لإنشاء جروب الواتساب:
                            </h4>

                            <div class="space-y-4">
                                <div class="flex gap-4">
                                    <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                                    <div>
                                        <h5 class="font-semibold text-blue-900">افتح Google Contacts</h5>
                                        <p class="text-blue-800 text-sm">روح على contacts.google.com من الكمبيوتر</p>
                                    </div>
                                </div>

                                <div class="flex gap-4">
                                    <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                                    <div>
                                        <h5 class="font-semibold text-blue-900">استورد الملف</h5>
                                        <p class="text-blue-800 text-sm">اضغط "Import" واختار الملف اللي اتحمل دلوقتي</p>
                                    </div>
                                </div>

                                <div class="flex gap-4">
                                    <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                                    <div>
                                        <h5 class="font-semibold text-blue-900">إنشاء مجموعة (Label)</h5>
                                        <p class="text-blue-800 text-sm">اختار "Create a label" وسميها "${safeLabelName}"</p>
                                    </div>
                                </div>

                                <div class="flex gap-4">
                                    <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
                                    <div>
                                        <h5 class="font-semibold text-blue-900">انتظر التزامن</h5>
                                        <p class="text-blue-800 text-sm">استنى 5-10 دقايق لحد ما الأرقام تظهر في جهات الاتصال</p>
                                    </div>
                                </div>

                                <div class="flex gap-4">
                                    <div class="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">5</div>
                                    <div>
                                        <h5 class="font-semibold text-green-900">إنشاء الجروب</h5>
                                        <p class="text-green-800 text-sm">من الواتساب، اعمل "New Group" وابحث عن "${safeLabelName}" وضيف الكل!</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                            <div class="flex items-start gap-3">
                                <span class="text-yellow-600 text-xl">💡</span>
                                <div>
                                    <h5 class="font-semibold text-yellow-900">نصائح مهمة:</h5>
                                    <ul class="text-yellow-800 text-sm mt-2 space-y-1">
                                        <li>• تأكد إنك مسجل دخول بنفس حساب Google في الجوال والكمبيوتر</li>
                                        <li>• لو الأرقام مش ظاهرة، جرب تعمل Sync للجهات من إعدادات الجوال</li>
                                        <li>• كل رقم إضافي لنفس الشخص يتصدر كجهة اتصال مستقلة: الاسم 1، الاسم 2، الاسم 3...</li>
                                        <li>• الحد الأقصى للجروب واتساب 1024 شخص</li>
                                        <li>• ممكن تقسم النتائج لأكتر من جروب لو العدد كبير</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div class="bg-green-50 rounded-lg p-4 border border-green-200">
                            <div class="flex items-center gap-3 mb-3">
                                <span class="text-green-600 text-xl">📊</span>
                                <h5 class="font-semibold text-green-900">ملف الأرقام يحتوي على:</h5>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                <div class="text-green-800"><strong>إجمالي النتائج المعروضة:</strong> ${displayedCount}</div>
                                <div class="text-green-800"><strong>إجمالي الأرقام:</strong> ${phoneCount}</div>
                                <div class="text-green-800"><strong>أشخاص لهم أرقام:</strong> ${peopleWithPhones}</div>
                                <div class="text-green-800"><strong>بدون رقم صحيح:</strong> ${skippedWithoutPhones}</div>
                                <div class="text-green-800 sm:col-span-2"><strong>اسم الملف:</strong> ${safeFileName}</div>
                                <div class="text-green-800 sm:col-span-2"><strong>صيغة الاسم:</strong> الاسم - المندوب - الشركة - المحافظة - رقم الملف</div>
                            </div>
                        </div>

                        <div class="flex flex-col sm:flex-row gap-3">
                            <button
                                id="openReportGoogleContactsBtn"
                                type="button"
                                class="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                            >
                                🌐 فتح Google Contacts
                            </button>
                            <button
                                id="closeReportContactsInstructionsBtn2"
                                type="button"
                                class="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium"
                            >
                                فهمت، شكرًا
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal = document.getElementById('reportContactsInstructionsModal');
        const closeBtn1 = document.getElementById('closeReportContactsInstructionsBtn');
        const closeBtn2 = document.getElementById('closeReportContactsInstructionsBtn2');
        const openContactsBtn = document.getElementById('openReportGoogleContactsBtn');

        const closeModal = () => {
            if (modal) modal.remove();
        };

        const openGoogleContacts = () => {
            window.open('https://contacts.google.com', '_blank');
            closeModal();
        };

        if (closeBtn1) closeBtn1.addEventListener('click', closeModal);
        if (closeBtn2) closeBtn2.addEventListener('click', closeModal);
        if (openContactsBtn) openContactsBtn.addEventListener('click', openGoogleContacts);
        if (modal) {
            modal.addEventListener('click', (event) => {
                if (event.target === modal) closeModal();
            });
        }
    };

    const exportDisplayedContacts = async () => {
        // صلاحية مستقلة وصارمة: لا يظهر/يعمل الزر إلا عند تفعيلها صراحةً من صفحة المستخدمين.
        if (!canShowStrictBtn('زر تصدير جهات الاتصال')) {
            showToast('ليس لديك صلاحية تصدير جهات الاتصال', 'error');
            return;
        }

        const displayedPassports = getWorkingPassports();
        if (!hasSearched || displayedPassports.length === 0) {
            showToast('اعمل بحث أولًا وتأكد أن هناك نتائج معروضة للتصدير', 'warning');
            return;
        }

        try {
            setIsLoading(true);
            showToast(`جاري تجهيز جهات الاتصال لـ ${displayedPassports.length} نتيجة معروضة...`, 'info');

            const rows = [
                ['Name', 'Given Name', 'Additional Name', 'Family Name', 'Phone 1 - Type', 'Phone 1 - Value']
            ];

            let peopleWithPhones = 0;
            let skippedWithoutPhones = 0;

            displayedPassports.forEach((passport) => {
                const personName = String(passport?.NameArabic || 'غير محدد').trim() || 'غير محدد';

                const delegateFallback = getDelegateNameById(passport?.DelegateID);
                const delegateName = String(
                    passport?.DelegateName ||
                    (delegateFallback && delegateFallback !== '-' ? delegateFallback : 'غير محدد')
                ).trim() || 'غير محدد';

                const companyFallback = getCompanyNameById(passport?.CompanyID);
                const companyName = String(
                    passport?.CompanyName ||
                    (companyFallback && companyFallback !== '-' ? companyFallback : 'غير محدد')
                ).trim() || 'غير محدد';

                const governorateName = String(passport?.Governorate || 'غير محدد').trim() || 'غير محدد';

                const rawFileNumber = getPassportFileNumber(passport);
                const fileNumber = rawFileNumber && rawFileNumber !== '-' ? rawFileNumber : 'غير محدد';

                const phoneNumbers = extractContactPhoneNumbers(passport?.PhoneNumbers);

                if (phoneNumbers.length === 0) {
                    skippedWithoutPhones += 1;
                    return;
                }

                peopleWithPhones += 1;

                phoneNumbers.forEach((phone, phoneIndex) => {
                    // إذا كان للشخص أكثر من رقم: نضيف 1 / 2 / 3 بعد الاسم مباشرة.
                    // مثال: أحمد محمد 1 - المندوب - الشركة - المحافظة - رقم الملف
                    const numberedPersonName = phoneNumbers.length > 1
                        ? `${personName} ${phoneIndex + 1}`
                        : personName;
                    const contactName = `${numberedPersonName} - ${delegateName} - ${companyName} - ${governorateName} - ${fileNumber}`;

                    // الاسم الكامل في Name و Given Name فقط لمنع Google Contacts من إعادة ترتيب أجزاء الاسم.
                    rows.push([
                        contactName,
                        contactName,
                        '',
                        '',
                        'Mobile',
                        phone
                    ]);
                });
            });

            if (rows.length <= 1) {
                showToast('لا توجد أرقام هواتف صحيحة ضمن النتائج المعروضة', 'warning');
                return;
            }

            const csvContent = '\uFEFF' + rows
                .map(row => row.map(escapeContactsCsvCell).join(','))
                .join('\r\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `جهات-اتصال-الجوازات-${displayedPassports.length}-نتيجة-${dateStr}.csv`;
            const labelName = `تقرير الجوازات - ${now.toLocaleDateString('ar-EG')}`;

            await saveBlobFile(
                blob,
                fileName,
                'text/csv;charset=utf-8'
            );

            const skippedText = skippedWithoutPhones > 0
                ? ` - تم تخطي ${skippedWithoutPhones} بدون رقم صحيح`
                : '';

            showToast(
                `تم تصدير ${rows.length - 1} جهة اتصال من ${peopleWithPhones} شخصًا ضمن ${displayedPassports.length} نتيجة معروضة${skippedText}`,
                skippedWithoutPhones > 0 ? 'warning' : 'success'
            );

            showContactsExportInstructions({
                phoneCount: rows.length - 1,
                displayedCount: displayedPassports.length,
                peopleWithPhones,
                skippedWithoutPhones,
                labelName,
                fileName
            });
        } catch (error) {
            console.error('خطأ في تصدير جهات الاتصال:', error);
            showToast('تعذر تصدير جهات الاتصال: ' + (error?.message || 'خطأ غير معروف'), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const getMobileReportValue = (passport, key, rowIndex) => {
        switch (key) {
            case 'serialNumber': return rowIndex + 1;
            case 'passportId': return getPassportFileNumber(passport);
            case 'nameArabic': return passport.NameArabic || '-';
            case 'nameEnglish': return passport.NameEnglish || '-';
            case 'passportNumber': return passport.PassportNumber || '-';
            case 'nationalId': return passport.NationalID || '-';
            case 'phoneNumbers': return getMobilePhoneNumbers(passport);
            case 'delegate': return getDelegateNameById(passport.DelegateID);
            case 'company': return getCompanyNameById(passport.CompanyID);
            case 'previousCompanies': return getPreviousCompanyNames(passport);
            case 'blacklistCompanies': return passport.BlacklistCompanies || '-';
            case 'blacklistReasons': return passport.BlacklistReasons || '-';
            case 'passportProfession': return getPassportProfessionName(passport);
            case 'travelProfession': return getTravelProfessionName(passport);
            case 'medicalExamStatus': return passport.MedicalExamStatus || '-';
            case 'medicalExamDate': return formatMobileDate(passport.MedicalExamDate);
            case 'networkDate': return formatMobileDate(passport.NetworkDate);
            case 'visaDate': return formatMobileDate(passport.VisaDate);
            case 'travelDate': return formatMobileDate(passport.TravelDate);
            case 'borderNumber': return passport.BorderNumber || '-';
            case 'violationNotes': return passport.ViolationNotes || '-';
            case 'travelDuration': return passport.TravelDuration || '-';
            case 'passportStatus': return passport.PassportStatus || '-';
            case 'receiveStatus': return passport.PassportReceiveStatus || 'لم يستلم';
            case 'address': return passport.Address || '-';
            case 'governorate': return passport.Governorate || '-';
            case 'age': return passport.Age ?? '-';
            case 'birthDate': return formatMobileDate(passport.DateOfBirth);
            case 'militaryStatus': return passport.MilitaryStatus || '-';
            case 'passportIssueDate': return formatMobileDate(passport.PassportIssueDate);
            case 'passportExpiryDate': return formatMobileDate(passport.PassportExpiryDate);
            case 'licenseType': return passport.LicenseType || '-';
            case 'licenseExpiryDate': return formatMobileDate(passport.LicenseExpiryDate);
            case 'abilityTest': return passport.AbilityTest || '-';
            case 'receiveDate': return (passport.ReceiveDate && passport.PassportReceiveStatus && passport.PassportReceiveStatus !== 'لم يستلم') ? formatMobileDate(passport.ReceiveDate) : '-';
            case 'receiverName': return passport.FriendName || '-';
            case 'receiverPhone': return passport.FriendPhone || '-';
            case 'applicationNumber': return passport.ApplicationNumber || '-';
            case 'fingerprintDate': return formatMobileDate(passport.FingerprintDate);
            case 'consulateDate': return formatMobileDate(passport.ConsulateEntryDate);
            case 'documentsDeliveryStatus': return passport.DocumentsDeliveryStatus || 'لم يسلم';
            case 'medicalInsuranceDate': return passport.MedicalInsuranceDate ? formatMobileDate(passport.MedicalInsuranceDate) : 'لا يوجد';
            case 'notes': {
                const baseNotes = hideOriginalNotes ? '' : (getAllNotes(passport) || '');
                const extraNote = tempNotes[passport.PassportID] || '';
                return [baseNotes, extraNote].filter(Boolean).join(' | ') || '-';
            }
            case 'note1': return getSpecificNote(passport, 1) || '-';
            case 'note2': return getSpecificNote(passport, 2) || '-';
            case 'note3': return getSpecificNote(passport, 3) || '-';
            case 'note4': return getSpecificNote(passport, 4) || '-';
            default: return '-';
        }
    };

    const mobileReportFieldOrder = [
        'serialNumber', 'company', 'delegate', 'phoneNumbers', 'passportProfession', 'travelProfession',
        'governorate', 'passportStatus', 'receiveStatus', 'medicalExamStatus', 'medicalExamDate', 'networkDate',
        'visaDate', 'travelDate', 'borderNumber', 'violationNotes', 'travelDuration', 'previousCompanies', 'blacklistCompanies', 'blacklistReasons', 'nationalId', 'nameEnglish',
        'address', 'age', 'birthDate', 'militaryStatus', 'passportIssueDate', 'passportExpiryDate', 'licenseType', 'licenseExpiryDate',
        'abilityTest', 'receiveDate', 'receiverName', 'receiverPhone', 'applicationNumber', 'fingerprintDate',
        'consulateDate', 'documentsDeliveryStatus', 'medicalInsuranceDate', 'notes', 'note1', 'note2', 'note3', 'note4'
    ];

    const getMobileVisibleFields = (passport, rowIndex) => {
        return mobileReportFieldOrder
            .filter(key => visibleColumns[key])
            .map(key => ({ key, label: getColumnLabel(key), value: getMobileReportValue(passport, key, rowIndex) }))
            .filter(field => field.value !== undefined && field.value !== null);
    };

    // خيارات ترتيب واضحة لعرض الموبايل فقط.
    // تستخدم نفس sortConfig الخاص بالجدول حتى يظل ترتيب العرض والتصدير والطباعة واحدًا.
    const mobileSortOptions = [
        { key: 'nameArabic', label: 'الاسم بالعربي' },
        { key: 'nameEnglish', label: 'الاسم بالإنجليزي' },
        { key: 'passportId', label: 'رقم الملف' },
        { key: 'passportNumber', label: 'رقم الجواز' },
        { key: 'nationalId', label: 'الرقم القومي' },
        { key: 'phoneNumbers', label: 'أرقام الهواتف' },
        { key: 'company', label: 'الشركة' },
        { key: 'delegate', label: 'المندوب' },
        { key: 'passportProfession', label: 'المهنة بالجواز' },
        { key: 'travelProfession', label: 'مهنة السفر' },
        { key: 'governorate', label: 'المحافظة' },
        { key: 'passportStatus', label: 'حالة الجواز' },
        { key: 'receiveStatus', label: 'حالة الاستلام' },
        { key: 'medicalExamStatus', label: 'حالة الكشف الطبي' },
        { key: 'medicalExamDate', label: 'تاريخ الكشف الطبي' },
        { key: 'networkDate', label: 'تاريخ عمل النت' },
        { key: 'fingerprintDate', label: 'تاريخ البصمة' },
        { key: 'consulateDate', label: 'تاريخ دخول القنصلية' },
        { key: 'visaDate', label: 'تاريخ التأشير' },
        { key: 'travelDate', label: 'تاريخ السفر' },
        { key: 'borderNumber', label: 'رقم الحدود' },
        { key: 'medicalInsuranceDate', label: 'تاريخ التأمين الطبي' },
        { key: 'documentsDeliveryStatus', label: 'حالة تسليم الأوراق' },
        { key: 'travelDuration', label: 'مدة السفر' },
        { key: 'abilityTest', label: 'نتيجة اختبار القدرات' },
        { key: 'applicationNumber', label: 'رقم الطلب' },
        { key: 'address', label: 'العنوان' },
        { key: 'age', label: 'العمر' },
        { key: 'birthDate', label: 'تاريخ الميلاد' },
        { key: 'militaryStatus', label: 'الموقف من التجنيد' },
        { key: 'passportIssueDate', label: 'تاريخ إصدار الجواز' },
        { key: 'passportExpiryDate', label: 'تاريخ انتهاء الجواز' },
        { key: 'licenseType', label: 'نوع الرخصة' },
        { key: 'licenseExpiryDate', label: 'تاريخ انتهاء الرخصة' },
        { key: 'receiveDate', label: 'تاريخ الاستلام' },
        { key: 'receiverName', label: 'اسم المستلم' },
        { key: 'receiverPhone', label: 'رقم هاتف المستلم' },
        { key: 'notes', label: 'الملاحظات' }
    ];

    const handleMobileSortFieldChange = (event) => {
        const key = event.target.value;
        setCurrentPage(1);

        if (!key) {
            setSortConfig({ key: null, direction: 'asc' });
            return;
        }

        setSortConfig(previous => ({
            key,
            direction: previous.key === key ? previous.direction : 'asc'
        }));
    };

    const handleMobileSortDirectionChange = (direction) => {
        if (!sortConfig.key) return;
        setCurrentPage(1);
        setSortConfig(previous => ({ ...previous, direction }));
    };

    const clearMobileSort = () => {
        setCurrentPage(1);
        setSortConfig({ key: null, direction: 'asc' });
    };

    const renderMobileSortControls = () => (
        <div className="md:hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4" dir="rtl">
            <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                    <h3 className="text-base font-bold text-gray-900">ترتيب النتائج</h3>
                    <p className="text-xs text-gray-500 mt-1">اختر الحقل ثم اتجاه الترتيب</p>
                </div>
                {sortConfig.key && (
                    <button
                        type="button"
                        onClick={clearMobileSort}
                        className="shrink-0 text-xs font-bold text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-xl"
                    >
                        إلغاء الترتيب
                    </button>
                )}
            </div>

            <label className="block text-xs font-bold text-gray-600 mb-1.5">ترتيب حسب</label>
            <select
                value={sortConfig.key || ''}
                onChange={handleMobileSortFieldChange}
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3 py-3 text-sm font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
                <option value="">الترتيب الافتراضي للنتائج</option>
                {mobileSortOptions.map(option => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                ))}
            </select>

            <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                    type="button"
                    onClick={() => handleMobileSortDirectionChange('asc')}
                    disabled={!sortConfig.key}
                    className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        sortConfig.key && sortConfig.direction === 'asc'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-200'
                    }`}
                >
                    <TrendingUp size={17} />
                    تصاعدي
                </button>
                <button
                    type="button"
                    onClick={() => handleMobileSortDirectionChange('desc')}
                    disabled={!sortConfig.key}
                    className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        sortConfig.key && sortConfig.direction === 'desc'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-200'
                    }`}
                >
                    <TrendingDown size={17} />
                    تنازلي
                </button>
            </div>

            {sortConfig.key && (
                <div className="mt-3 bg-blue-50 border border-blue-100 text-blue-800 rounded-xl px-3 py-2 text-xs font-bold text-center">
                    مرتب حسب {getColumnLabel(sortConfig.key)} — {sortConfig.direction === 'asc' ? 'تصاعدي' : 'تنازلي'}
                </div>
            )}
        </div>
    );

    const renderMobileReportCards = () => {
        const currentPageData = getPaginatedData(getWorkingPassports());

        if (currentPageData.length === 0) {
            return (
                <div className="md:hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                    <AlertCircle size={44} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-base font-bold text-gray-700">لا توجد نتائج للبحث المحدد</p>
                    <p className="text-sm text-gray-400 mt-1">جرب تعديل معايير البحث</p>
                </div>
            );
        }

        return (
            <div className="md:hidden space-y-3">
                {currentPageData.map((passport, index) => {
                    const rowIndex = (currentPage - 1) * itemsPerPage + index;
                    const fields = getMobileVisibleFields(passport, rowIndex);
                    const receiveStatus = passport.PassportReceiveStatus || 'لم يستلم';
                    const documentsStatus = passport.DocumentsDeliveryStatus || 'لم يسلم';

                    return (
                        <div key={passport.PassportID || index} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="bg-gradient-to-l from-blue-600 to-indigo-600 text-white p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                            <span className="bg-white/20 text-white px-2.5 py-1 rounded-full text-xs font-bold">
                                                رقم الملف: {getPassportFileNumber(passport)}
                                            </span>
                                            {passport.PassportNumber && (
                                                <span className="bg-white/15 text-white px-2.5 py-1 rounded-full text-xs font-bold ltr text-left">
                                                    {passport.PassportNumber}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="text-lg font-bold leading-relaxed break-words">{passport.NameArabic || '-'}</h3>
                                        <p className="text-xs text-blue-100 mt-1">كشف الجوازات - عرض موبايل</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setTempHidden(prev => ({ ...prev, [passport.PassportID]: true }))}
                                        className="shrink-0 bg-white/15 hover:bg-white/25 text-white rounded-xl px-3 py-2 text-xs font-bold"
                                        title="إخفاء مؤقت من الكشف"
                                    >
                                        إخفاء
                                    </button>
                                </div>
                            </div>

                            <div className="p-4 space-y-4">
                                <div className="flex flex-wrap gap-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${receiveStatus === 'لم يستلم' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                        {receiveStatus}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${documentsStatus === 'سلم' ? 'bg-green-100 text-green-800' : documentsStatus === 'سلم البعض وينقصه شيء' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-50 text-red-700'}`}>
                                        أوراق: {documentsStatus}
                                    </span>
                                    {passport.MedicalInsuranceDate ? (
                                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">تأمين طبي: نعم</span>
                                    ) : (
                                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">تأمين طبي: لا</span>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    {fields.map(field => (
                                        <div key={field.key} className={`${['notes','note1','note2','note3','note4','address','previousCompanies'].includes(field.key) ? 'col-span-2' : ''} bg-gray-50 rounded-xl p-3 border border-gray-100`}>
                                            <p className="text-[11px] text-gray-400 mb-1">{field.label}</p>
                                            <p className="text-sm font-semibold text-gray-800 leading-relaxed break-words whitespace-pre-wrap">{field.value}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-3">
                                    <label className="block text-xs font-bold text-yellow-800 mb-2">ملاحظة مؤقتة للكشف</label>
                                    <textarea
                                        value={tempNotes[passport.PassportID] || ''}
                                        onChange={(e) => setTempNotes(prev => ({ ...prev, [passport.PassportID]: e.target.value }))}
                                        placeholder="اكتب ملاحظة تظهر في الكشف والتصدير فقط..."
                                        className="w-full min-h-[76px] border border-yellow-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white resize-none"
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}

                {getWorkingPassports().length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3">
                        <PaginationControls data={getWorkingPassports()} />
                    </div>
                )}
            </div>
        );
    };
    // تحميل البيانات الأولية فقط
    
useEffect(() => {
    // تحميل القوائم المحفوظة للاستخدام كفلتر
    fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists?includeArchived=false`)
        .then(r => r.json())
        .then(data => setSavedLists(Array.isArray(data) ? data : []))
        .catch(() => setSavedLists([]));
}, []);
useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true);
            try {
                const [companiesRes, professionsRes, delegatesRes] = await Promise.all([
                    fetch(`${config.REPORTS_API_URL}/api/companies`),
                    fetch(`${config.REPORTS_API_URL}/api/professions`),
                    fetch(`${config.REPORTS_API_URL}/api/delegates`)
                ]);

                const [companiesData, professionsData, delegatesData] = await Promise.all([
                    companiesRes.json(),
                    professionsRes.json(),
                    delegatesRes.json()
                ]);

                setCompanies(companiesData);
                setProfessions(professionsData);
                setDelegates(delegatesData);
                
                showToast('تم تحميل البيانات الأساسية بنجاح', 'success');
            } catch (error) {
                console.error('خطأ في جلب البيانات:', error);
                showToast('خطأ في جلب البيانات الأساسية', 'error');
            }
            setIsLoading(false);
        };

        loadInitialData();
    }, []);


    // useEffect لإعادة تعيين الصفحة عند البحث الجديد
useEffect(() => {
    setCurrentPage(1);
}, [selectedPassports.length]);

// ✅ تجهيز الفلاتر التي سيتم إرسالها للسيرفر حسب الصلاحيات
// مهم: لو فلتر مخفي عن المستخدم لا نترك قيمته الافتراضية تؤثر على النتيجة بدون ما يراها.
const getEffectiveFiltersByPermissions = () => {
    const emptyRange = { from: '', to: '' };
    const next = { ...filters };

    if (!canShowFilter('إظهار حالة النشاط')) {
        next.activeStatus = 'active';
    }

    if (!canShowAdvancedFiltersBox() || !canShowFilter('إظهار القوائم المحفوظة')) {
        next.listIds = [];
        next.excludeList = false;
        next.includeArchivedHistory = false;
    } else {
        if (!canShowFilter('إظهار استثناء عناصر القوائم المختارة', ['إظهار القوائم المحفوظة'])) {
            next.excludeList = false;
        }
        if (!canShowFilter('إظهار عرض البيانات المؤرشفة للقوائم')) {
            next.includeArchivedHistory = false;
        }
        if (next.excludeList) {
            next.includeArchivedHistory = false;
        }
    }

    if (!canShowFilter('إظهار حالة التسليم')) {
        next.deliveryFilter = 'عرض الكل';
    }
    if (!canShowFilter('إظهار حالة المعالجة')) {
        next.processFilter = 'عرض الكل';
    }
    if (!canShowFilter('إظهار مرحلة المعالجة')) {
        next.processingStageFilter = '';
    }

    if (!canShowFilter('إظهار فلتر المناديب')) {
        next.delegateId = '';
        next.selectedDelegates = [];
    }
    if (!canShowFilter('إظهار فلتر الشركات')) {
        next.selectedCompanies = [];
    }
    if (!canShowFilter('إظهار فلتر المهن بالجواز')) {
        next.passportProfessionId = '';
        next.selectedProfessions = [];
    }
    if (!canShowFilter('إظهار فلتر مهن السفر')) {
        next.travelProfessionId = '';
        next.selectedTravelProfessions = [];
    }
    if (!canShowFilter('إظهار فلتر مدة السفر')) {
        next.travelDuration = '';
    }
    if (!canShowFilter('إظهار فلتر حالة الجواز')) {
        next.passportStatus = '';
    }
    if (!canShowFilter('إظهار فلتر الشركات السابقة')) {
        next.previousCompanies = [];
    }

    if (!canShowAnyDateFilters()) {
        next.dateRange = emptyRange;
        next.medicalDateRange = emptyRange;
        next.networkDateRange = emptyRange;
        next.fingerprintDateRange = emptyRange;
        next.insuranceDateRange = emptyRange;
        next.consulateDateRange = emptyRange;
        next.visaDateRange = emptyRange;
        next.travelDateRange = emptyRange;
    } else {
        if (!canShowFilter('إظهار فلتر تاريخ الإضافة', ['إظهار فلتر التواريخ'])) next.dateRange = emptyRange;
        if (!canShowFilter('إظهار فلتر تاريخ الكشف الطبي', ['إظهار فلتر التواريخ'])) next.medicalDateRange = emptyRange;
        if (!canShowFilter('إظهار فلتر تاريخ عمل النت', ['إظهار فلتر التواريخ'])) next.networkDateRange = emptyRange;
        if (!canShowFilter('إظهار فلتر تاريخ البصمة', ['إظهار فلتر التواريخ'])) next.fingerprintDateRange = emptyRange;
        if (!canShowFilter('إظهار فلتر تاريخ التأمين الطبي', ['إظهار فلتر التواريخ'])) next.insuranceDateRange = emptyRange;
        if (!canShowFilter('إظهار فلتر تاريخ دخول القنصلية', ['إظهار فلتر التواريخ'])) next.consulateDateRange = emptyRange;
        if (!canShowFilter('إظهار فلتر تاريخ التأشير', ['إظهار فلتر التواريخ'])) next.visaDateRange = emptyRange;
        if (!canShowFilter('إظهار فلتر تاريخ السفر', ['إظهار فلتر التواريخ'])) next.travelDateRange = emptyRange;
    }

    return next;
};

// دالة فلترة الملاحظات المنفصلة
const applyNotesFilter = (passports) => {
    if (!canShowAnyNotesFilters()) {
        return passports;
    }

    const activeNoteFilters = {
        allNotes: canShowFilter('إظهار فلتر جميع الملاحظات', ['إظهار فلاتر الملاحظات']) ? noteFilters.allNotes : '',
        note1: canShowFilter('إظهار فلتر ملاحظة 1', ['إظهار فلاتر الملاحظات']) ? noteFilters.note1 : '',
        note2: canShowFilter('إظهار فلتر ملاحظة 2', ['إظهار فلاتر الملاحظات']) ? noteFilters.note2 : '',
        note3: canShowFilter('إظهار فلتر ملاحظة 3', ['إظهار فلاتر الملاحظات']) ? noteFilters.note3 : '',
        note4: canShowFilter('إظهار فلتر ملاحظة 4', ['إظهار فلاتر الملاحظات']) ? noteFilters.note4 : ''
    };

    if (!activeNoteFilters.allNotes && !activeNoteFilters.note1 && !activeNoteFilters.note2 && !activeNoteFilters.note3 && !activeNoteFilters.note4) {
        return passports;
    }

    return passports.filter(passport => {
        let matchesFilter = true;

        // فلتر البحث في جميع الملاحظات
        if (activeNoteFilters.allNotes) {
            const allNotes = getAllNotes(passport).toLowerCase();
            if (!allNotes.includes(activeNoteFilters.allNotes.toLowerCase())) {
                matchesFilter = false;
            }
        }

        // فلتر ملاحظة 1
        if (activeNoteFilters.note1) {
            const note1 = getSpecificNote(passport, 1).toLowerCase();
            if (!note1.includes(activeNoteFilters.note1.toLowerCase())) {
                matchesFilter = false;
            }
        }

        // فلتر ملاحظة 2
        if (activeNoteFilters.note2) {
            const note2 = getSpecificNote(passport, 2).toLowerCase();
            if (!note2.includes(activeNoteFilters.note2.toLowerCase())) {
                matchesFilter = false;
            }
        }

        // فلتر ملاحظة 3
        if (activeNoteFilters.note3) {
            const note3 = getSpecificNote(passport, 3).toLowerCase();
            if (!note3.includes(activeNoteFilters.note3.toLowerCase())) {
                matchesFilter = false;
            }
        }

        // فلتر ملاحظة 4
        if (activeNoteFilters.note4) {
            const note4 = getSpecificNote(passport, 4).toLowerCase();
            if (!note4.includes(activeNoteFilters.note4.toLowerCase())) {
                matchesFilter = false;
            }
        }

        return matchesFilter;
    });
};

// في دالة performSearch، قم بإزالة التحقق من معايير البحث المعقدة واستبدلها بهذا:
const performSearch = async () => {
    if (isLoading) return;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 130000) : null;

    try {
        setIsLoading(true);
        setCurrentPage(1);

        const filtersForRequest = getEffectiveFiltersByPermissions();
        console.log('📄 بدء البحث المتقدم');
        console.log('📋 معايير البحث:', filtersForRequest);
        showToast('جاري البحث... انتظر حتى تظهر النتيجة', 'info');
        
        const url = `${config.REPORTS_API_URL}/api/reports/passports/filter`;
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            cache: 'no-store',
            signal: controller?.signal,
            body: JSON.stringify({ 
                filters: filtersForRequest,
                timestamp: Date.now()
            })
        });

        const result = await response.json().catch(() => null);
        if (!response.ok || result?.success === false) {
            throw new Error(result?.details || result?.error || `فشل في جلب البيانات المفلترة (${response.status})`);
        }

        const rows = Array.isArray(result?.data) ? result.data : [];
        console.log('📊 نتائج البحث:', {
            filtered: rows.length,
            raw: result?.rawTotal,
            timestamp: result?.timestamp,
            isLimited: result?.isLimited
        });
        
        // تطبيق فلتر الملاحظات المنفصلة محليًا لأنه يعتمد على طريقة عرض الملاحظات داخل الصفحة
        const filteredWithNotes = applyNotesFilter(rows);
        setSelectedPassports(filteredWithNotes);
        setHasSearched(true);
        setSearchStats({ total: filteredWithNotes.length });

        const limitText = result?.limitMessage ? ` - ${result.limitMessage}` : '';
        showToast(`تم العثور على ${filteredWithNotes.length} جواز${limitText}`, result?.isLimited ? 'warning' : 'success');
        
    } catch (error) {
        console.error('❌ خطأ في البحث:', error);
        const message = error?.name === 'AbortError'
            ? 'انتهت مهلة انتظار البحث من المتصفح. ضيق الفلاتر أو راجع السيرفر.'
            : (error?.message || 'تعذر تنفيذ البحث');
        showToast('خطأ في البحث: ' + message, 'error');
        setSelectedPassports([]);
        setHasSearched(false);
    } finally {
        if (timeoutId) clearTimeout(timeoutId);
        setIsLoading(false);
    }
};


  const resetSearch = () => {
    setFilters({
        listIds: [],            // ✅ إرجاع القوائم المحفوظة
        excludeList: false,     // ✅ رجعها false
        includeArchivedHistory: false, // ✅ إرجاع زر الأرشيف السابق للوضع الافتراضي

        dateRange: { from: '', to: '' },

        medicalDateRange: { from: '', to: '' },
        networkDateRange: { from: '', to: '' },
  fingerprintDateRange: { from: '', to: '' },
        consulateDateRange: { from: '', to: '' },
        insuranceDateRange: { from: '', to: '' },
        visaDateRange: { from: '' , to: '' },
        travelDateRange: { from: '' , to: '' },
        delegateId: '',
        passportProfessionId: '',
        travelProfessionId: '',
        selectedCompanies: [],
        medicalExamStatus: '',
        search: '',
        travelDuration: '',
        passportStatus: '',
        previousCompanies: [],
        abilityTest: '',
        selectedDelegates: [],
        licenseType: '',
        receiveStatus: '',
        activeStatus: 'active',
        deliveryFilter: 'الغير مسلمة فقط',
        processFilter: 'الفعلية فقط',
        selectedProfessions: [],
        selectedTravelProfessions: [],
        processingStageFilter: ''
    });

    setSelectedPassports([]);
    setHasSearched(false);
    setSearchStats(null);
    setNoteFilters({ allNotes: '', note1: '', note2: '', note3: '', note4: '' });
    showToast('تم مسح معايير البحث', 'info');
};


// دالة مساعدة: تبنى صف واحد حسب الأعمدة المختارة فى exportColumns
const buildPassportExportRowForImage = (passport, index) => {
    const row = {};

    // إضافة البيانات حسب الأعمدة المختارة (اعتماداً على exportColumns فقط)
    if (exportColumns.serialNumber)   row['م'] = index + 1;
    // لا نضيف رقم الملف أو الـ ID في الصور المحملة بناءً على طلبك.
    if (exportColumns.nameArabic)     row['الاسم بالعربي'] = passport.NameArabic || '-';
    if (exportColumns.nameEnglish)    row['الاسم بالإنجليزي'] = passport.NameEnglish || '-';
    if (exportColumns.passportNumber) row['رقم الجواز'] = passport.PassportNumber || '-';
    if (exportColumns.nationalId)     row['الرقم القومي'] = passport.NationalID || '-';

    if (exportColumns.company) {
        const company = companies.find(c => c.CompanyID === passport.CompanyID);
        row['الشركة'] = company?.CompanyName || '-';
    }

    if (exportColumns.delegate) {
        const delegate = delegates.find(d => d.DelegateID === passport.DelegateID);
        row['المندوب'] = delegate?.DelegateName || '-';
    }

        if (exportColumns.previousCompanies) {
        try {
            const prevCompanies = JSON.parse(passport.PreviousCompanies || '[]');

            if (Array.isArray(prevCompanies) && prevCompanies.length > 0) {
                const companyNames = prevCompanies
                    .map(companyId => {
                        const company = companies.find(
                            c => c.CompanyID?.toString() === companyId?.toString()
                        );
                        return company?.CompanyName;
                    })
                    .filter(Boolean);

                row['الشركات السابقة'] =
                    companyNames.length > 0 ? companyNames.join(' ، ') : '-';
            } else {
                row['الشركات السابقة'] = '-';
            }
        } catch {
            row['الشركات السابقة'] = '-';
        }
    }

    if (exportColumns.blacklistCompanies) {
        row['الشركات الممنوع فيها'] = passport.BlacklistCompanies || '-';
    }

    if (exportColumns.blacklistReasons) {
        row['سبب المنع'] = passport.BlacklistReasons || '-';
    }

    if (exportColumns.phoneNumbers) {
        try {
            const phones = JSON.parse(passport.PhoneNumbers || '[]');
            if (Array.isArray(phones)) {
                row['أرقام الهواتف'] = phones
                    .filter(phone => phone && phone.trim())
                    .join(' - ') || '-';
            } else {
                row['أرقام الهواتف'] = passport.PhoneNumbers || '-';
            }
        } catch {
            row['أرقام الهواتف'] = passport.PhoneNumbers || '-';
        }
    }

    if (exportColumns.passportProfession) {
        row['المهنة بالجواز'] = getPassportProfessionName(passport);
    }

    if (exportColumns.travelProfession) {
        row['مهنة السفر'] = getTravelProfessionName(passport);
    }

      // حالة الكشف الطبي
    if (exportColumns.medicalExamStatus) {
        row['حالة الكشف الطبي'] = passport.MedicalExamStatus || '-';
    }
    
    if (exportColumns.medicalExamDate) {
        row['تاريخ الكشف الطبي'] = passport.MedicalExamDate
            ? new Date(passport.MedicalExamDate).toLocaleDateString('ar-EG')
            : '-';
    }

    if (exportColumns.networkDate) {
        row['تاريخ عمل النت'] = passport.NetworkDate
            ? new Date(passport.NetworkDate).toLocaleDateString('ar-EG')
            : '-';
    }

    if (exportColumns.visaDate) {
        row['تاريخ التأشير'] = passport.VisaDate
            ? new Date(passport.VisaDate).toLocaleDateString('ar-EG')
            : '-';
    }

    if (exportColumns.travelDate) {
        row['تاريخ السفر'] = passport.TravelDate
            ? new Date(passport.TravelDate).toLocaleDateString('ar-EG')
            : '-';
    }

    if (exportColumns.borderNumber) row['رقم الحدود'] = passport.BorderNumber || '-';
    if (exportColumns.violationNotes) row['المخالفة / ملاحظتها'] = passport.ViolationNotes || '-';

    if (exportColumns.travelDuration) row['مدة السفر'] = passport.TravelDuration || '-';
    if (exportColumns.passportStatus) row['حالة الجواز'] = passport.PassportStatus || '-';
    if (exportColumns.receiveStatus)  row['حالة الاستلام'] = passport.PassportReceiveStatus || 'لم يستلم';
    if (exportColumns.notes) {
    const baseNotes = hideOriginalNotes ? '' : (getAllNotes(passport) || '');
    const extra = tempNotes[passport.PassportID] || '';
    const combined = [baseNotes, extra].filter(Boolean).join(' | ');
    row['الملاحظات'] = combined || '-';
}

    if (exportColumns.note1)          row['ملاحظة 1'] = getSpecificNote(passport, 1) || '-';
    if (exportColumns.note2)          row['ملاحظة 2'] = getSpecificNote(passport, 2) || '-';
    if (exportColumns.note3)          row['ملاحظة 3'] = getSpecificNote(passport, 3) || '-';
    if (exportColumns.note4)          row['ملاحظة 4'] = getSpecificNote(passport, 4) || '-';

    // 🔹 بيانات إضافية للجواز
    if (exportColumns.address) {
        row['العنوان'] = passport.Address || '-';
    }

    if (exportColumns.governorate) {
        row['المحافظة'] = passport.Governorate || '-';
    }

    // العمر
    if (exportColumns.age) {
        row['العمر'] = passport.Age ?? '-';
    }

    // تاريخ الميلاد
    if (exportColumns.birthDate) {
        row['تاريخ الميلاد'] = passport.DateOfBirth
            ? new Date(passport.DateOfBirth).toLocaleDateString('ar-EG')
            : '-';
    }

    // الموقف من التجنيد
    if (exportColumns.militaryStatus) {
        row['الموقف من التجنيد'] = passport.MilitaryStatus || '-';
    }

    if (exportColumns.passportIssueDate) {
        row['تاريخ إصدار الجواز'] = passport.PassportIssueDate
            ? new Date(passport.PassportIssueDate).toLocaleDateString('ar-EG')
            : '-';
    }

    // تاريخ انتهاء الجواز
    if (exportColumns.passportExpiryDate) {
        row['تاريخ انتهاء الجواز'] = passport.PassportExpiryDate
            ? new Date(passport.PassportExpiryDate).toLocaleDateString('ar-EG')
            : '-';
    }

    // نوع الرخصة
    if (exportColumns.licenseType) {
        row['نوع الرخصة'] = passport.LicenseType || '-';
    }

    // تاريخ انتهاء الرخصة
    if (exportColumns.licenseExpiryDate) {
        row['تاريخ انتهاء الرخصة'] = passport.LicenseExpiryDate
            ? new Date(passport.LicenseExpiryDate).toLocaleDateString('ar-EG')
            : '-';
    }

    // اختبار الكفاءة
    if (exportColumns.abilityTest) {
        row['اختبار الكفاءة'] = passport.AbilityTest || '-';
    }

    // ✅ تاريخ الاستلام – يعتمد على حالة الاستلام
    if (exportColumns.receiveDate) {
        const deliveredStatuses = ['استلم العميل', 'استلم المندوب', 'استلم صديق'];
        const currentStatus = (passport.PassportReceiveStatus || 'لم يستلم').trim();
        const isDelivered = deliveredStatuses.includes(currentStatus);

        row['تاريخ الاستلام'] =
            (isDelivered && passport.ReceiveDate)
                ? new Date(passport.ReceiveDate).toLocaleDateString('ar-EG')
                : '-';
    }

    // اسم المستلم

    // 🆕 الحقول الجديدة من المتابعة الدورية
    // رقم الطلب
    if (exportColumns.applicationNumber) {
        row['رقم الطلب'] = passport.ApplicationNumber || '-';
    }

    // تاريخ البصمة
    if (exportColumns.fingerprintDate) {
        row['تاريخ البصمة'] = passport.FingerprintDate
            ? new Date(passport.FingerprintDate).toLocaleDateString('ar-EG')
            : '-';
    }

    if (exportColumns.consulateDate) {
        row['تاريخ دخول القنصلية'] = passport.ConsulateEntryDate
            ? new Date(passport.ConsulateEntryDate).toLocaleDateString('ar-EG')
            : '-';
    }

    // حالة تسليم الأوراق
if (exportColumns.documentsDeliveryStatus) {
        row['حالة تسليم الأوراق'] = passport.DocumentsDeliveryStatus || 'لم يسلم';
    }
    if (exportColumns.medicalInsuranceDate) {
        row['تاريخ التأمين الطبي'] = passport.MedicalInsuranceDate
            ? new Date(passport.MedicalInsuranceDate).toLocaleDateString('ar-EG')
            : '-';
    }
    if (exportColumns.receiverName) {
        row['اسم المستلم'] = passport.FriendName || '-';
    }

    // هاتف المستلم
    if (exportColumns.receiverPhone) {
        row['هاتف المستلم'] = passport.FriendPhone || '-';
    }

    return row;
};



 const handleAdvancedExport = async () => {
  try {
   const sortedPassports = getWorkingPassports();


    // إنشاء workbook جديد
    const wb = XLSX.utils.book_new();

    // ======= ورقة البيانات الرئيسية =======
    const mainData = sortedPassports.map((passport, index) => {
      const row = {};

      // إضافة البيانات حسب الأعمدة المختارة (اعتماداً على exportColumns فقط)
      if (exportColumns.serialNumber)   row['م'] = index + 1;
      if (exportColumns.passportId)     row['رقم الملف'] = getPassportFileNumber(passport);
      if (exportColumns.nameArabic)     row['الاسم بالعربي'] = passport.NameArabic || '-';
      if (exportColumns.nameEnglish)    row['الاسم بالإنجليزي'] = passport.NameEnglish || '-';
      if (exportColumns.passportNumber) row['رقم الجواز'] = passport.PassportNumber || '-';
      if (exportColumns.nationalId)     row['الرقم القومي'] = passport.NationalID || '-';

      if (exportColumns.company) {
        const company = companies.find(c => c.CompanyID === passport.CompanyID);
        row['الشركة'] = company?.CompanyName || '-';
      }

      if (exportColumns.delegate) {
        const delegate = delegates.find(d => d.DelegateID === passport.DelegateID);
        row['المندوب'] = delegate?.DelegateName || '-';
      }

      // الشركات السابقة
      if (exportColumns.previousCompanies) {
        try {
          const prevCompanies = JSON.parse(passport.PreviousCompanies || '[]');
          if (Array.isArray(prevCompanies) && prevCompanies.length > 0) {
            const companyNames = prevCompanies
              .map(companyId => {
                const company = companies.find(c => c.CompanyID?.toString() === companyId?.toString());
                return company?.CompanyName;
              })
              .filter(Boolean);
            row['الشركات السابقة'] = companyNames.length > 0 ? companyNames.join(' ، ') : '-';
          } else {
            row['الشركات السابقة'] = '-';
          }
        } catch {
          row['الشركات السابقة'] = '-';
        }
      }

      if (exportColumns.blacklistCompanies) {
        row['الشركات الممنوع فيها'] = passport.BlacklistCompanies || '-';
      }

      if (exportColumns.blacklistReasons) {
        row['سبب المنع'] = passport.BlacklistReasons || '-';
      }

      if (exportColumns.phoneNumbers) {
        try {
          const phones = JSON.parse(passport.PhoneNumbers || '[]');
          if (Array.isArray(phones)) {
            row['أرقام الهواتف'] = phones
              .filter(phone => phone && phone.trim())
              .join(' - ') || '-';
          } else {
            row['أرقام الهواتف'] = passport.PhoneNumbers || '-';
          }
        } catch {
          row['أرقام الهواتف'] = passport.PhoneNumbers || '-';
        }
      }

      if (exportColumns.passportProfession) {
        row['المهنة بالجواز'] = getPassportProfessionName(passport);
      }

      if (exportColumns.travelProfession) {
        row['مهنة السفر'] = getTravelProfessionName(passport);
      }

      if (exportColumns.medicalExamDate) {
        row['تاريخ الكشف الطبي'] = passport.MedicalExamDate
          ? new Date(passport.MedicalExamDate).toLocaleDateString('ar-EG')
          : '-';
      }

      if (exportColumns.networkDate) {
        row['تاريخ عمل النت'] = passport.NetworkDate
          ? new Date(passport.NetworkDate).toLocaleDateString('ar-EG')
          : '-';
      }

      if (exportColumns.visaDate) {
        row['تاريخ التأشير'] = passport.VisaDate
          ? new Date(passport.VisaDate).toLocaleDateString('ar-EG')
          : '-';
      }

      if (exportColumns.travelDate) {
        row['تاريخ السفر'] = passport.TravelDate
          ? new Date(passport.TravelDate).toLocaleDateString('ar-EG')
          : '-';
      }

      if (exportColumns.borderNumber) row['رقم الحدود'] = passport.BorderNumber || '-';
      if (exportColumns.violationNotes) row['المخالفة / ملاحظتها'] = passport.ViolationNotes || '-';

      if (exportColumns.travelDuration) row['مدة السفر'] = passport.TravelDuration || '-';
      if (exportColumns.passportStatus) row['حالة الجواز'] = passport.PassportStatus || '-';
      if (exportColumns.receiveStatus)  row['حالة الاستلام'] = passport.PassportReceiveStatus || 'لم يستلم';
    if (exportColumns.notes) {
    const baseNotes = hideOriginalNotes ? '' : (getAllNotes(passport) || '');
    const extra = tempNotes[passport.PassportID] || '';
    const combined = [baseNotes, extra].filter(Boolean).join(' | ');
    row['الملاحظات'] = combined || '-';
}

      if (exportColumns.note1)          row['ملاحظة 1'] = getSpecificNote(passport, 1) || '-';
      if (exportColumns.note2)          row['ملاحظة 2'] = getSpecificNote(passport, 2) || '-';
      if (exportColumns.note3)          row['ملاحظة 3'] = getSpecificNote(passport, 3) || '-';
      if (exportColumns.note4)          row['ملاحظة 4'] = getSpecificNote(passport, 4) || '-';

        // 🔹 بيانات إضافية للجواز
      if (exportColumns.address) {
        row['العنوان'] = passport.Address || '-';
      }

      if (exportColumns.governorate) {
        row['المحافظة'] = passport.Governorate || '-';
      }

      // العمر
      if (exportColumns.age) {
        row['العمر'] = passport.Age ?? '-';
      }

      // تاريخ الميلاد
      if (exportColumns.birthDate) {
        row['تاريخ الميلاد'] = passport.DateOfBirth
          ? new Date(passport.DateOfBirth).toLocaleDateString('ar-EG')
          : '-';
      }

      // الموقف من التجنيد
      if (exportColumns.militaryStatus) {
        row['الموقف من التجنيد'] = passport.MilitaryStatus || '-';
      }

      if (exportColumns.passportIssueDate) {
        row['تاريخ إصدار الجواز'] = passport.PassportIssueDate
          ? new Date(passport.PassportIssueDate).toLocaleDateString('ar-EG')
          : '-';
      }

      // تاريخ انتهاء الجواز
      if (exportColumns.passportExpiryDate) {
        row['تاريخ انتهاء الجواز'] = passport.PassportExpiryDate
          ? new Date(passport.PassportExpiryDate).toLocaleDateString('ar-EG')
          : '-';
      }

      // نوع الرخصة
      if (exportColumns.licenseType) {
        row['نوع الرخصة'] = passport.LicenseType || '-';
      }

      // تاريخ انتهاء الرخصة
      if (exportColumns.licenseExpiryDate) {
        row['تاريخ انتهاء الرخصة'] = passport.LicenseExpiryDate
          ? new Date(passport.LicenseExpiryDate).toLocaleDateString('ar-EG')
          : '-';
      }

      // اختبار الكفاءة

      // 🆕 الحقول الجديدة من المتابعة الدورية
      // رقم الطلب
      if (exportColumns.applicationNumber) {
        row['رقم الطلب'] = passport.ApplicationNumber || '-';
      }

      // تاريخ البصمة
      if (exportColumns.fingerprintDate) {
        row['تاريخ البصمة'] = passport.FingerprintDate
          ? new Date(passport.FingerprintDate).toLocaleDateString('ar-EG')
          : '-';
      }

      // تاريخ دخول القنصلية
      if (exportColumns.consulateDate) {
        row['تاريخ دخول القنصلية'] = passport.ConsulateEntryDate
          ? new Date(passport.ConsulateEntryDate).toLocaleDateString('ar-EG')
          : '-';
      }

      // حالة تسليم الأوراق
   if (exportColumns.documentsDeliveryStatus) {
        row['حالة تسليم الأوراق'] = passport.DocumentsDeliveryStatus || 'لم يسلم';
      }
      if (exportColumns.medicalInsuranceDate) {
        row['تاريخ التأمين الطبي'] = passport.MedicalInsuranceDate
          ? new Date(passport.MedicalInsuranceDate).toLocaleDateString('ar-EG')
          : '-';
      }
      if (exportColumns.abilityTest) {
        row['اختبار الكفاءة'] = passport.AbilityTest || '-';
      }

      // تاريخ الاستلام
    // ✅ تاريخ الاستلام – يعتمد على حالة الاستلام
if (exportColumns.receiveDate) {
  const deliveredStatuses = ['استلم العميل', 'استلم المندوب', 'استلم صديق'];
  const currentStatus = (passport.PassportReceiveStatus || 'لم يستلم').trim();
  const isDelivered = deliveredStatuses.includes(currentStatus);

  row['تاريخ الاستلام'] =
    (isDelivered && passport.ReceiveDate)
      ? new Date(passport.ReceiveDate).toLocaleDateString('ar-EG')
      : '-';
}


      // اسم المستلم
      if (exportColumns.receiverName) {
        row['اسم المستلم'] = passport.FriendName || '-';
      }

      // هاتف المستلم
      if (exportColumns.receiverPhone) {
        row['هاتف المستلم'] = passport.FriendPhone || '-';
      }

      return row;
    });


    const wsMain = XLSX.utils.json_to_sheet(mainData);

    // تحسين عرض الأعمدة
    const headers = Object.keys(mainData[0] || {});
    const colWidths = headers.map(header => {
      let maxWidth = header.length + 2;
      mainData.forEach(row => {
        const cellValue = String(row[header] || '');
        maxWidth = Math.max(maxWidth, cellValue.length + 2);
      });
      return { wch: Math.min(Math.max(maxWidth, 12), 40) };
    });
    wsMain['!cols'] = colWidths;

    // تنسيق الجدول الرئيسي
    const range = XLSX.utils.decode_range(wsMain['!ref'] || 'A1:A1');

    // تنسيق العناوين
    for (let col = range.s.c; col <= range.e.c; col++) {
      const headerCell = XLSX.utils.encode_cell({ r: 0, c: col });
      if (wsMain[headerCell]) {
        wsMain[headerCell].s = {
          fill: { fgColor: { rgb: "2F75B5" } },
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "medium", color: { rgb: "000000" } },
            bottom: { style: "medium", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }
    }

    // تنسيق البيانات
    for (let row = 1; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (wsMain[cellAddress]) {
          wsMain[cellAddress].s = {
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "DDDDDD" } },
              bottom: { style: "thin", color: { rgb: "DDDDDD" } },
              left: { style: "thin", color: { rgb: "DDDDDD" } },
              right: { style: "thin", color: { rgb: "DDDDDD" } }
            },
            fill: { fgColor: { rgb: row % 2 === 0 ? "F8F9FA" : "FFFFFF" } }
          };
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, wsMain, 'كشف الجوازات');

    // ======= ورقة الإحصائيات =======
    const statsData = [
      ['📊 إحصائيات شاملة للجوازات', '', ''],
      ['', '', ''],
      ['المؤشر', 'العدد', 'النسبة'],
      ['إجمالي الجوازات', selectedPassports.length, '100%'],
    ];

    // إحصائيات الشركات
    const companyStats = {};
    selectedPassports.forEach(passport => {
      const company = companies.find(c => c.CompanyID === passport.CompanyID);
      const companyName = company?.CompanyName || 'غير محدد';
      companyStats[companyName] = (companyStats[companyName] || 0) + 1;
    });

    statsData.push(['', '', '']);
    statsData.push(['📈 توزيع الشركات', '', '']);
    Object.entries(companyStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .forEach(([company, count]) => {
        const percentage = ((count / selectedPassports.length) * 100).toFixed(1);
        statsData.push([company, count, `${percentage}%`]);
      });

    // إحصائيات حالة الاستلام
    const receiveStats = {};
    selectedPassports.forEach(passport => {
      const status = passport.PassportReceiveStatus || 'لم يستلم';
      receiveStats[status] = (receiveStats[status] || 0) + 1;
    });

    statsData.push(['', '', '']);
    statsData.push(['📋 حالة الاستلام', '', '']);
    Object.entries(receiveStats).forEach(([status, count]) => {
      const percentage = ((count / selectedPassports.length) * 100).toFixed(1);
      statsData.push([status, count, `${percentage}%`]);
    });

    const wsStats = XLSX.utils.aoa_to_sheet(statsData);
    wsStats['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }];

    // تنسيق ورقة الإحصائيات
    if (wsStats['A1']) {
      wsStats['A1'].s = {
        font: { bold: true, sz: 16, color: { rgb: "2F75B5" } },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: "E8F4FD" } }
      };
    }
    wsStats['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];

    XLSX.utils.book_append_sheet(wb, wsStats, 'الإحصائيات');

    // ======= ورقة معلومات التقرير =======
    const infoData = [
      ['📄 معلومات التقرير', ''],
      ['', ''],
      ['تاريخ الإنشاء', new Date().toLocaleDateString('ar-EG')],
      ['وقت الإنشاء', new Date().toLocaleTimeString('ar-EG')],
      ['إجمالي السجلات', selectedPassports.length],
      // ✅ عدّ الأعمدة من exportColumns بدل visibleColumns
      ['الأعمدة المصدرة', Object.values(exportColumns).filter(Boolean).length],
    ];

    if (sortConfig.key) {
      const sortLabel =
        sortConfig.key === 'nameArabic'  ? 'الاسم بالعربي'   :
        sortConfig.key === 'passportId'  ? 'رقم الملف'   :
        sortConfig.key === 'governorate' ? 'المحافظة'        :
        sortConfig.key === 'company'     ? 'الشركة'          :
        sortConfig.key === 'notes'       ? 'الملاحظات'       : sortConfig.key;

      infoData.push([
        'ترتيب البيانات',
        `${sortLabel} (${sortConfig.direction === 'asc' ? 'تصاعدي' : 'تنازلي'})`
      ]);
    }

    infoData.push(['', '']);
    infoData.push(['🔍 فلاتر البحث المطبقة', '']);

    if (filters.search) infoData.push(['البحث النصي', filters.search]);

    if (filters.delegateId) {
      const delegate = delegates.find(d => d.DelegateID.toString() === filters.delegateId);
      infoData.push(['المندوب', delegate?.DelegateName || filters.delegateId]);
    }

    if (filters.selectedCompanies.length > 0) {
      const selectedCompanyNames = filters.selectedCompanies
        .map(id => companies.find(c => c.CompanyID.toString() === id)?.CompanyName)
        .filter(Boolean)
        .join(', ');
      infoData.push(['الشركات المحددة', selectedCompanyNames]);
    }

    const wsInfo = XLSX.utils.aoa_to_sheet(infoData);
    wsInfo['!cols'] = [{ wch: 25 }, { wch: 40 }];

    if (wsInfo['A1']) {
      wsInfo['A1'].s = {
        font: { bold: true, sz: 16, color: { rgb: "2F75B5" } },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: "E8F4FD" } }
      };
    }
    wsInfo['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];

    XLSX.utils.book_append_sheet(wb, wsInfo, 'معلومات التقرير');

    // تصدير الملف
    const fileName = `تقرير_جوازات_متقدم_${new Date().toISOString().split('T')[0]}.xlsx`;
    if (isMasarAndroidBridgeAvailable()) {
      const base64 = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'base64',
        compression: true
      });
      saveBase64FileToAndroid(fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', base64);
      showToast('تم إرسال ملف Excel للحفظ داخل التنزيلات / Masar ✅', 'success');
    } else {
      XLSX.writeFile(wb, fileName, {
        bookType: 'xlsx',
        type: 'binary',
        compression: true
      });
      showToast('تم تصدير التقرير المتقدم بنجاح! 🎉', 'success');
    }
  } catch (error) {
    console.error('خطأ في التصدير المتقدم:', error);
    showToast('حدث خطأ أثناء التصدير المتقدم: ' + error.message, 'error');
  }
};

    


  


  const selectAllColumns = () => {
      const newExportColumns = {};
      const newVisibleColumns = {};
      Object.keys(exportColumns).forEach(key => {
          newExportColumns[key] = true;
          newVisibleColumns[key] = true;
      });
      setExportColumns(newExportColumns);
      setVisibleColumns(newVisibleColumns);
  };

  const unselectAllColumns = () => {
      const newExportColumns = {};
      const newVisibleColumns = {};
      Object.keys(exportColumns).forEach(key => {
          newExportColumns[key] = false;
          newVisibleColumns[key] = false;
      });
      setExportColumns(newExportColumns);
      setVisibleColumns(newVisibleColumns);
  };

// في دالة handlePreview، استبدل جزء CSS الخاص بالـ table:

const handlePreview = () => {
    if (selectedPassports.length === 0) {
        showToast('لا توجد بيانات للمعاينة', 'warning');
        return;
    }

    const sortedPassports = getWorkingPassports();
    const previewSummary = {
        totalPassports: sortedPassports.length
    };

    // حساب عدد الأعمدة المرئية
    const visibleColumnsCount = Object.values(visibleColumns).filter(Boolean).length;
    
    // تحديد التخطيط المثالي
    const getOptimalLayout = (count) => {
        if (count <= 2) {
            return {
                tableWidth: '70%',
                cellPadding: '20px 15px',
                fontSize: { header: '16px', cell: '14px', title: '28px' }
            };
        }
        if (count === 3) {
            return {
                tableWidth: '90%',
                cellPadding: '18px 12px',
                fontSize: { header: '15px', cell: '13px', title: '26px' }
            };
        }
        if (count <= 5) {
            return {
                tableWidth: '95%',
                cellPadding: '15px 10px',
                fontSize: { header: '14px', cell: '12px', title: '24px' }
            };
        }
        if (count <= 8) {
            return {
                tableWidth: '100%',
                cellPadding: '12px 8px',
                fontSize: { header: '12px', cell: '11px', title: '22px' }
            };
        }
        return {
            tableWidth: '100%',
            cellPadding: '10px 6px',
            fontSize: { header: '11px', cell: '10px', title: '20px' }
        };
    };

    const layout = getOptimalLayout(visibleColumnsCount);

    const previewWindow = window.open('', '_blank');
    
    previewWindow.document.write(`
        <!DOCTYPE html>
        <html dir="rtl">
            <head>
                <title>معاينة كشف الجوازات</title>
                <meta charset="UTF-8">
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
                    
                    body { 
                        font-family: 'Cairo', sans-serif;
                        padding: 20px;
                        background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                        margin: 0;
                        min-height: 100vh;
                    }
                    
                    .container {
                        background-color: white;
                        padding: 30px;
                        border-radius: 15px;
                        box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                        margin: 0 auto;
                        max-width: 100%;
                        overflow-x: auto;
                    }
                    
                    h1 {
                        text-align: center;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        font-size: ${layout.fontSize.title};
                        margin-bottom: 15px;
                        font-weight: 700;
                        text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
                    }

                    .report-info {
                        margin-bottom: 30px;
                        padding: 25px;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        border-radius: 15px;
                        color: white;
                        text-align: center;
                        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
                    }

                    .report-info h2 {
                        margin: 0 0 20px 0;
                        font-size: ${visibleColumnsCount <= 3 ? '22px' : '18px'};
                        font-weight: 600;
                    }

                    .stat-container {
                        text-align: center;
                        padding: 15px;
                    }

                    .stat-item {
                        background: rgba(255,255,255,0.2);
                        backdrop-filter: blur(10px);
                        padding: 20px 30px;
                        border-radius: 12px;
                        display: inline-block;
                        margin: 0 auto;
                        border: 1px solid rgba(255,255,255,0.3);
                    }

                    .stat-number {
                        font-size: ${visibleColumnsCount <= 3 ? '32px' : '24px'};
                        font-weight: bold;
                        margin-bottom: 8px;
                        text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
                    }

                    .stat-label {
                        font-size: ${visibleColumnsCount <= 3 ? '16px' : '12px'};
                        opacity: 0.9;
                        font-weight: 500;
                    }
                    
                    .controls {
                        margin-bottom: 25px;
                        padding: 20px;
                        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                        border-radius: 12px;
                        border: 1px solid #dee2e6;
                        text-align: center;
                    }
                    
                    .btn {
                        background: linear-gradient(135deg, #2F75B5, #4A90E2);
                        color: white;
                        border: none;
                        padding: 12px 20px;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 14px;
                        font-weight: 600;
                        margin: 0 8px;
                        transition: all 0.3s ease;
                        box-shadow: 0 3px 10px rgba(47, 117, 181, 0.3);
                    }
                    
                    .btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 5px 15px rgba(47, 117, 181, 0.4);
                    }
                    
                    table {
                        width: ${layout.tableWidth};
                        margin: 25px auto;
                        border-collapse: collapse;
                        background-color: white;
                        box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                        border-radius: 12px;
                        overflow: hidden;
                        border: 2px solid #2F75B5;
                    }
                    
                    th {
                        background: linear-gradient(135deg, #2F75B5, #4A90E2);
                        color: white;
                        font-size: ${layout.fontSize.header};
                        font-weight: 700;
                        padding: ${layout.cellPadding};
                        text-align: center;
                        border-bottom: 3px solid #1e5a96;
                        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
                        position: relative;
                        overflow: hidden;
                    }

                    th::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                        transition: left 0.5s;
                    }

                    th:hover::before {
                        left: 100%;
                    }
                    
                    td {
                        font-size: ${layout.fontSize.cell};
                        padding: ${layout.cellPadding};
                        border-bottom: 1px solid #eee;
                        vertical-align: middle;
                        text-align: center;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        transition: all 0.2s ease;
                    }

                    tbody tr:nth-child(even) {
                        background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
                    }
                    
                    tbody tr:hover {
                        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                        transform: scale(1.02);
                        box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                    }

                    .status-badge {
                        display: inline-block;
                        padding: 6px 12px;
                        border-radius: 20px;
                        font-size: ${visibleColumnsCount <= 3 ? '11px' : '9px'};
                        font-weight: 600;
                        text-transform: uppercase;
                        white-space: nowrap;
                        border: 1px solid;
                        transition: all 0.2s ease;
                    }

                    .status-badge:hover {
                        transform: scale(1.1);
                    }

                    .status-new { 
                        background: linear-gradient(135deg, #d4edda, #c3e6cb); 
                        color: #155724; 
                        border-color: #c3e6cb; 
                        box-shadow: 0 2px 8px rgba(21, 87, 36, 0.2);
                    }
                    .status-old { 
                        background: linear-gradient(135deg, #fff3cd, #ffeaa7); 
                        color: #856404; 
                        border-color: #ffeaa7; 
                        box-shadow: 0 2px 8px rgba(133, 100, 4, 0.2);
                    }
                    .status-received { 
                        background: linear-gradient(135deg, #d4edda, #c3e6cb); 
                        color: #155724; 
                        border-color: #c3e6cb; 
                        box-shadow: 0 2px 8px rgba(21, 87, 36, 0.2);
                    }
                    .status-not-received { 
                        background: linear-gradient(135deg, #f8d7da, #f5c6cb); 
                        color: #721c24; 
                        border-color: #f5c6cb; 
                        box-shadow: 0 2px 8px rgba(114, 28, 36, 0.2);
                    }

                    .date-badge {
                        background: linear-gradient(135deg, #e3f2fd, #bbdefb);
                        color: #1976d2;
                        padding: 5px 10px;
                        border-radius: 15px;
                        font-size: ${visibleColumnsCount <= 3 ? '11px' : '9px'};
                        font-weight: 500;
                        border: 1px solid #bbdefb;
                        white-space: nowrap;
                        box-shadow: 0 2px 6px rgba(25, 118, 210, 0.2);
                        transition: all 0.2s ease;
                    }

                    .date-badge:hover {
                        transform: scale(1.05);
                    }

                    /* تحسينات للأعمدة القليلة */
                    ${visibleColumnsCount <= 3 ? `
                        .serial-number {
                            font-weight: bold;
                            color: #2F75B5;
                            font-size: 18px;
                            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
                        }

                        .name-cell {
                            font-weight: 700;
                            color: #333;
                            font-size: 16px;
                            text-shadow: 0 1px 2px rgba(0,0,0,0.1);
                        }

                        .passport-cell {
                            font-family: 'Courier New', monospace;
                            font-weight: 600;
                            color: #2F75B5;
                            letter-spacing: 1px;
                            background: linear-gradient(135deg, #f8f9fa, #e9ecef);
                            border-radius: 8px;
                            padding: 8px 12px;
                            margin: 2px;
                            display: inline-block;
                        }

                        .company-cell {
                            font-weight: 600;
                            color: #2F75B5;
                            background: linear-gradient(135deg, #e8f4fd, #d1ecf1);
                            border-radius: 8px;
                            padding: 8px 12px;
                            margin: 2px;
                            display: inline-block;
                        }

                        .phone-cell {
                            font-family: monospace;
                            background: linear-gradient(135deg, #f0f8f0, #e8f5e8);
                            border-radius: 8px;
                            padding: 8px 12px;
                            margin: 2px;
                            display: inline-block;
                        }
                    ` : ''}

                    @media print {
                        .controls { display: none; }
                        .container { box-shadow: none; }
                        body { background: white; }
                        
                        table {
                            box-shadow: none;
                            break-inside: auto;
                        }
                        
                        tr {
                            break-inside: avoid;
                            break-after: auto;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <div class="controls">
                        <button class="btn" onclick="window.print()">🖨️ طباعة تفاعلية</button>
                        <button class="btn" onclick="window.close()">❌ إغلاق</button>
                        <span style="color: #666; font-size: 14px; margin-right: 20px; font-weight: 500;">
                            📊 عدد الأعمدة المعروضة: ${visibleColumnsCount} | ✨ معاينة محسّنة
                        </span>
                    </div>
                
                    <h1>🌟 كشف الجوازات - معاينة تفاعلية شاملة</h1>
                    
                    ${sortConfig.key ? `
                        <div style="text-align: center; margin: 20px 0; padding: 20px; background: linear-gradient(135deg, #f0f8ff, #e6f3ff); border: 2px solid #4472C4; border-radius: 15px; box-shadow: 0 4px 15px rgba(68, 114, 196, 0.2);">
                            <strong style="color: #2F75B5; font-size: ${visibleColumnsCount <= 3 ? '18px' : '14px'};">
                                🔄 مرتب حسب: ${
                                    sortConfig.key === 'nameArabic' ? 'الاسم بالعربي' :
                                    sortConfig.key === 'passportId' ? 'رقم الملف' :
                                    sortConfig.key === 'governorate' ? 'المحافظة' :
                                    sortConfig.key === 'company' ? 'الشركة' :
                                    sortConfig.key === 'notes' ? 'الملاحظات' :
                                    sortConfig.key === 'note1' ? 'ملاحظة 1' :
                                    sortConfig.key === 'note2' ? 'ملاحظة 2' :
                                    sortConfig.key === 'note3' ? 'ملاحظة 3' :
                                    sortConfig.key === 'note4' ? 'ملاحظة 4' : sortConfig.key
                                } (${sortConfig.direction === 'asc' ? 'تصاعدي ⬆️' : 'تنازلي ⬇️'})
                            </strong>
                        </div>
                    ` : ''}

                    <div class="report-info">
                        <h2>📊 الملخص الإحصائي التفاعلي</h2>
                        <div class="stat-container">
                            <div class="stat-item">
                                <div class="stat-number">${previewSummary.totalPassports}</div>
                                <div class="stat-label">إجمالي الجوازات</div>
                            </div>
                        </div>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                ${visibleColumns.serialNumber ? '<th>🔢 م</th>' : ''}
                                ${visibleColumns.passportId ? '<th>🆔 رقم الملف</th>' : ''}
                                ${visibleColumns.nameArabic ? '<th>👤 الاسم بالعربي</th>' : ''}
                                ${visibleColumns.nameEnglish ? '<th>👤 الاسم بالإنجليزي</th>' : ''}
                                ${visibleColumns.passportNumber ? '<th>📘 رقم الجواز</th>' : ''}
                                ${visibleColumns.nationalId ? '<th>🆔 الرقم القومي</th>' : ''}
                                ${visibleColumns.phoneNumbers ? '<th>📱 أرقام الهواتف</th>' : ''}
                                ${visibleColumns.delegate ? '<th>👨‍💼 المندوب</th>' : ''}
                                ${visibleColumns.company ? '<th>🏢 الشركة</th>' : ''}
                                ${visibleColumns.previousCompanies ? '<th>🏢 الشركات السابقة</th>' : ''}
                                ${visibleColumns.blacklistCompanies ? '<th>🚫 الشركات الممنوع فيها</th>' : ''}
                                ${visibleColumns.blacklistReasons ? '<th>⚠️ سبب المنع</th>' : ''}
                                ${visibleColumns.passportProfession ? '<th>💼 المهنة بالجواز</th>' : ''}
                                ${visibleColumns.travelProfession ? '<th>✈️ مهنة السفر</th>' : ''}
                                ${visibleColumns.medicalExamDate ? '<th>🏥 تاريخ الكشف</th>' : ''}
                                ${visibleColumns.networkDate ? '<th>🌐 تاريخ النت</th>' : ''}
                                ${visibleColumns.visaDate ? '<th>📋 تاريخ التأشير</th>' : ''}
                                ${visibleColumns.travelDate ? '<th>✈️ تاريخ السفر</th>' : ''}
                                ${visibleColumns.borderNumber ? '<th>🧾 رقم الحدود</th>' : ''}
                                ${visibleColumns.violationNotes ? '<th>⚠️ المخالفة / ملاحظتها</th>' : ''}
                                ${visibleColumns.travelDuration ? '<th>⏰ مدة السفر</th>' : ''}
                                ${visibleColumns.passportStatus ? '<th>📊 حالة الجواز</th>' : ''}
                                ${visibleColumns.receiveStatus ? '<th>📦 حالة الاستلام</th>' : ''}
                                ${visibleColumns.notes ? '<th>📝 الملاحظات</th>' : ''}
                                ${visibleColumns.note1 ? '<th>📝 ملاحظة 1</th>' : ''}
                                ${visibleColumns.note2 ? '<th>📝 ملاحظة 2</th>' : ''}
                                ${visibleColumns.note3 ? '<th>📝 ملاحظة 3</th>' : ''}
                                ${visibleColumns.note4 ? '<th>📝 ملاحظة 4</th>' : ''}
                                ${visibleColumns.medicalExamStatus ? '<th>🏥 حالة الكشف الطبي</th>' : ''}
                                ${visibleColumns.address ? '<th>🏠 العنوان</th>' : ''}
                                ${visibleColumns.governorate ? '<th>🗺️ المحافظة</th>' : ''}
                                ${visibleColumns.age ? '<th>🎂 العمر</th>' : ''}
                                ${visibleColumns.birthDate ? '<th>📅 تاريخ الميلاد</th>' : ''}
                                ${visibleColumns.militaryStatus ? '<th>🎖️ الموقف من التجنيد</th>' : ''}
                                ${visibleColumns.passportIssueDate ? '<th>📅 تاريخ إصدار الجواز</th>' : ''}
                                ${visibleColumns.passportExpiryDate ? '<th>⏰ تاريخ انتهاء الجواز</th>' : ''}
                                ${visibleColumns.licenseType ? '<th>🚗 نوع الرخصة</th>' : ''}
                                ${visibleColumns.licenseExpiryDate ? '<th>⏰ تاريخ انتهاء الرخصة</th>' : ''}
                                ${visibleColumns.abilityTest ? '<th>📊 نتيجة اختبار القدرات</th>' : ''}
                                ${visibleColumns.receiveDate ? '<th>📅 تاريخ الاستلام</th>' : ''}
                                ${visibleColumns.receiverName ? '<th>👤 اسم المستلم</th>' : ''}
                                ${visibleColumns.receiverPhone ? '<th>📱 رقم هاتف المستلم</th>' : ''}
                                ${visibleColumns.applicationNumber ? '<th>📋 رقم الطلب</th>' : ''}
                                ${visibleColumns.fingerprintDate ? '<th>👆 تاريخ البصمة</th>' : ''}
                                ${visibleColumns.consulateDate ? '<th>🏛️ تاريخ دخول القنصلية</th>' : ''}
                        ${visibleColumns.documentsDeliveryStatus ? '<th>📦 حالة تسليم الأوراق</th>' : ''}
                                ${visibleColumns.medicalInsuranceDate ? '<th>🏥 تاريخ التأمين الطبي</th>' : ''}
                            </tr>
                        </thead>
                        <tbody>
                            ${sortedPassports.map((passport, index) => `
                                <tr>
                                    ${visibleColumns.serialNumber ? `<td class="serial-number">${index + 1}</td>` : ''}
                                    ${visibleColumns.passportId ? `<td class="passport-cell">${getPassportFileNumber(passport)}</td>` : ''}
                                    ${visibleColumns.nameArabic ? `<td class="name-cell">${passport.NameArabic}</td>` : ''}
                                    ${visibleColumns.nameEnglish ? `<td class="name-cell">${passport.NameEnglish || '-'}</td>` : ''}
                                    ${visibleColumns.passportNumber ? `<td class="passport-cell">${passport.PassportNumber}</td>` : ''}
                                    ${visibleColumns.nationalId ? `<td style="font-family: monospace; font-weight: 500;">${passport.NationalID || '-'}</td>` : ''}
                                    ${visibleColumns.phoneNumbers ? `<td class="phone-cell">${(() => {
                                        try {
                                            const phones = JSON.parse(passport.PhoneNumbers || '[]');
                                            if (Array.isArray(phones)) {
                                                return phones.filter(phone => phone && phone.trim()).join('<br>') || '-';
                                            } else {
                                                return passport.PhoneNumbers || '-';
                                            }
                                        } catch {
                                            return passport.PhoneNumbers || '-';
                                        }
                                    })()}</td>` : ''}
                                    ${visibleColumns.delegate ? `<td style="font-weight: 500;">${delegates.find(d => d.DelegateID === passport.DelegateID)?.DelegateName || '-'}</td>` : ''}
                                    ${visibleColumns.company ? `<td class="company-cell">${companies.find(c => c.CompanyID === passport.CompanyID)?.CompanyName || '-'}</td>` : ''}
                                    ${visibleColumns.previousCompanies ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'}; max-width: 150px;">${(() => {
                                        try {
                                            const prevCompanies = JSON.parse(passport.PreviousCompanies || '[]');
                                            if (Array.isArray(prevCompanies) && prevCompanies.length > 0) {
                                                const companyNames = prevCompanies
                                                    .map(companyId => {
                                                        const company = companies.find(c => c.CompanyID.toString() === companyId?.toString());
                                                        return company?.CompanyName;
                                                    })
                                                    .filter(Boolean);
                                                
                                                if (companyNames.length > 0) {
                                                    return companyNames.join('<br>');
                                                }
                                            }
                                            return '-';
                                        } catch {
                                            return '-';
                                        }
                                    })()}</td>` : ''}
                                    ${visibleColumns.blacklistCompanies ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'}; max-width: 180px;">${passport.BlacklistCompanies || '-'}</td>` : ''}
                                    ${visibleColumns.blacklistReasons ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'}; max-width: 220px;">${passport.BlacklistReasons || '-'}</td>` : ''}
                                    ${visibleColumns.passportProfession ? `<td>${escapeHtml(getPassportProfessionName(passport))}</td>` : ''}
                                    ${visibleColumns.travelProfession ? `<td>${escapeHtml(getTravelProfessionName(passport))}</td>` : ''}
                                    ${visibleColumns.medicalExamDate ? `<td>${passport.MedicalExamDate ? `<span class="date-badge">${new Date(passport.MedicalExamDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                    ${visibleColumns.networkDate ? `<td>${passport.NetworkDate ? `<span class="date-badge">${new Date(passport.NetworkDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                    ${visibleColumns.visaDate ? `<td>${passport.VisaDate ? `<span class="date-badge">${new Date(passport.VisaDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                    ${visibleColumns.travelDate ? `<td>${passport.TravelDate ? `<span class="date-badge">${new Date(passport.TravelDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                    ${visibleColumns.borderNumber ? `<td style="font-family: monospace;">${passport.BorderNumber || '-'}</td>` : ''}
                                    ${visibleColumns.violationNotes ? `<td>${passport.ViolationNotes || '-'}</td>` : ''}
                                    ${visibleColumns.travelDuration ? `<td><span class="status-badge">${passport.TravelDuration || '-'}</span></td>` : ''}
                                    ${visibleColumns.passportStatus ? `<td><span class="status-badge ${passport.PassportStatus === 'جديد' ? 'status-new' : 'status-old'}">${passport.PassportStatus || '-'}</span></td>` : ''}
                                    ${visibleColumns.receiveStatus ? `<td><span class="status-badge ${passport.PassportReceiveStatus === 'استلم العميل' ? 'status-received' : 'status-not-received'}">${passport.PassportReceiveStatus || 'لم يستلم'}</span></td>` : ''}
                                    ${visibleColumns.notes ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'}; max-width: 150px;">${hideOriginalNotes ? (tempNotes[passport.PassportID] || '-') : (getAllNotes(passport) || '-')}</td>` : ''}
                                    ${visibleColumns.note1 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'};">${getSpecificNote(passport, 1) || '-'}</td>` : ''}
                                    ${visibleColumns.note2 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'};">${getSpecificNote(passport, 2) || '-'}</td>` : ''}
                                    ${visibleColumns.note3 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'};">${getSpecificNote(passport, 3) || '-'}</td>` : ''}
                                    ${visibleColumns.note4 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '12px' : '10px'};">${getSpecificNote(passport, 4) || '-'}</td>` : ''}
                                    ${visibleColumns.medicalExamStatus ? `<td>${passport.MedicalExamStatus || '-'}</td>` : ''}
                                    ${visibleColumns.address ? `<td>${passport.Address || '-'}</td>` : ''}
                                    ${visibleColumns.governorate ? `<td>${passport.Governorate || '-'}</td>` : ''}
                                    ${visibleColumns.age ? `<td>${passport.Age || '-'}</td>` : ''}
                                    ${visibleColumns.birthDate ? `<td>${passport.DateOfBirth ? new Date(passport.DateOfBirth).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                    ${visibleColumns.militaryStatus ? `<td>${passport.MilitaryStatus || '-'}</td>` : ''}
                                    ${visibleColumns.passportIssueDate ? `<td>${passport.PassportIssueDate ? new Date(passport.PassportIssueDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                    ${visibleColumns.passportExpiryDate ? `<td>${passport.PassportExpiryDate ? new Date(passport.PassportExpiryDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                    ${visibleColumns.licenseType ? `<td>${passport.LicenseType || '-'}</td>` : ''}
                                    ${visibleColumns.licenseExpiryDate ? `<td>${passport.LicenseExpiryDate ? new Date(passport.LicenseExpiryDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                    ${visibleColumns.abilityTest ? `<td>${passport.AbilityTest || '-'}</td>` : ''}
                                    ${visibleColumns.receiveDate ? `<td>${passport.ReceiveDate ? new Date(passport.ReceiveDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                    ${visibleColumns.receiverName ? `<td>${passport.FriendName || '-'}</td>` : ''}
                                    ${visibleColumns.receiverPhone ? `<td>${passport.FriendPhone || '-'}</td>` : ''}
                                    ${visibleColumns.applicationNumber ? `<td style="font-family: monospace;">${passport.ApplicationNumber || '-'}</td>` : ''}
                                    ${visibleColumns.fingerprintDate ? `<td>${passport.FingerprintDate ? new Date(passport.FingerprintDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                    ${visibleColumns.consulateDate ? `<td>${passport.ConsulateEntryDate ? new Date(passport.ConsulateEntryDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                      ${visibleColumns.documentsDeliveryStatus ? `<td><span class="status-badge ${passport.DocumentsDeliveryStatus === 'سلم' ? 'status-received' : 'status-not-received'}">${passport.DocumentsDeliveryStatus || 'لم يسلم'}</span></td>` : ''}
                                    ${visibleColumns.medicalInsuranceDate ? `<td>${passport.MedicalInsuranceDate ? new Date(passport.MedicalInsuranceDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                               </tr>
                           `).join('')}
                       </tbody>
                   </table>

                   <div style="margin-top: 40px; text-align: center; font-size: 14px; color: #666; padding-top: 25px; border-top: 3px solid #eee; background: linear-gradient(135deg, #f8f9fa, #e9ecef); padding: 25px; border-radius: 12px;">
                       <p><strong>✨ تم إنشاء هذا التقرير بواسطة نظام إدارة الجوازات المتطور</strong></p>
                       <p>📅 تاريخ الإنشاء: ${new Date().toLocaleString('ar-EG')} | 🔧 معاينة تفاعلية محسّنة</p>
                       <p>💼 نظام متكامل لإدارة الجوازات والسفر | 📊 ${visibleColumnsCount} عمود معروض</p>
                   </div>
               </div>
           </body>
       </html>
   `);
   previewWindow.document.close();
   
   showToast('تم فتح المعاينة التفاعلية المحسّنة! 🌟', 'success');
};


const AGENT_REPORT_COLUMN_ORDER = [
    'serialNumber', 'passportId', 'nameArabic', 'nameEnglish', 'passportNumber', 'nationalId',
    'phoneNumbers', 'delegate', 'company', 'previousCompanies', 'blacklistCompanies', 'blacklistReasons',
    'passportProfession', 'travelProfession', 'medicalExamDate', 'networkDate', 'visaDate', 'travelDate',
    'borderNumber', 'violationNotes', 'travelDuration', 'passportStatus', 'receiveStatus', 'notes',
    'note1', 'note2', 'note3', 'note4', 'medicalExamStatus', 'address', 'governorate', 'age', 'birthDate',
    'militaryStatus', 'passportIssueDate', 'passportExpiryDate', 'licenseType', 'licenseExpiryDate',
    'abilityTest', 'applicationNumber', 'fingerprintDate', 'consulateDate', 'documentsDeliveryStatus',
    'medicalInsuranceDate', 'receiveDate', 'receiverName', 'receiverPhone'
];

const AGENT_REPORT_COLUMN_WEIGHTS = {
    serialNumber: 0.55,
    passportId: 0.85,
    nameArabic: 1.65,
    nameEnglish: 1.65,
    passportNumber: 1.05,
    nationalId: 1.2,
    phoneNumbers: 1.35,
    delegate: 1.15,
    company: 1.25,
    previousCompanies: 1.6,
    blacklistCompanies: 1.55,
    blacklistReasons: 2.0,
    passportProfession: 1.25,
    travelProfession: 1.25,
    notes: 2.2,
    note1: 1.6,
    note2: 1.6,
    note3: 1.6,
    note4: 1.6,
    address: 1.8,
    violationNotes: 1.8,
    receiverName: 1.2,
    receiverPhone: 1.2
};

const getAgentPrintSelectedAgent = () =>
    agentPrintAgents.find(agent => String(agent.agentId) === String(agentPrintForm.agentId)) || null;

const getAgentPrinters = (agent) => {
    const printers = Array.isArray(agent?.printers) ? agent.printers : [];
    return printers
        .map(printer => typeof printer === 'string' ? { name: printer } : printer)
        .filter(printer => String(printer?.name || '').trim());
};

const getAgentReportSortLabel = () => {
    if (!sortConfig?.key) return '';
    const direction = sortConfig.direction === 'desc' ? 'تنازلي' : 'تصاعدي';
    return `${getColumnLabel(sortConfig.key)} (${direction})`;
};

const buildAgentProfessionalReportPayload = () => {
    const passports = getWorkingPassports();
    const columns = AGENT_REPORT_COLUMN_ORDER
        .filter(key => visibleColumns[key])
        .map(key => ({
            key,
            label: getColumnLabel(key),
            weight: AGENT_REPORT_COLUMN_WEIGHTS[key] || 1
        }));

    if (!columns.length) throw new Error('اختار عمودًا واحدًا على الأقل قبل الطباعة');
    if (!passports.length) throw new Error('لا توجد نتائج ظاهرة للطباعة');

    const rows = passports.map((passport, rowIndex) =>
        columns.map(column => {
            const raw = getMobileReportValue(passport, column.key, rowIndex);
            const value = raw === null || raw === undefined ? '-' : String(raw);
            return value.length > 1200 ? `${value.slice(0, 1197)}...` : value;
        })
    );

    return {
        type: 'professional_report',
        version: 2,
        renderEngine: 'edge_chromium_exact',
        htmlContent: buildProfessionalPrintHtml(),
        title: imageExportSettings.customTitle || 'نظام إدارة الجوازات',
        reportTitle: 'كشف الجوازات',
        generatedAt: new Date().toLocaleString('ar-EG'),
        sortLabel: getAgentReportSortLabel(),
        totalPassports: passports.length,
        showHeader: imageExportSettings.showHeader !== false,
        showTotal: imageExportSettings.showTotal !== false,
        showFooter: imageExportSettings.showFooter !== false,
        page: {
            size: 'A4',
            landscape: true,
            marginTopMm: 8,
            marginBottomMm: 8,
            marginLeftMm: 5,
            marginRightMm: 5
        },
        columns,
        rows
    };
};

const loadAgentPrintDevices = async () => {
    try {
        setAgentPrintLoading(true);
        setAgentPrintStatus(null);
        const response = await fetch(`${config.API_BASE_URL}/api/passports/barcode/agents`, {
            cache: 'no-store'
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result?.success) {
            throw new Error(result?.error || 'تعذر تحميل أجهزة Masar Print Agent');
        }

        const agents = Array.isArray(result.agents) ? result.agents : [];
        setAgentPrintAgents(agents);

        const available = agents.filter(agent =>
            agent?.isOnline &&
            Array.isArray(agent?.capabilities) &&
            agent.capabilities.includes('professional_report_html_exact')
        );

        setAgentPrintForm(prev => {
            const currentStillAvailable = available.some(agent => String(agent.agentId) === String(prev.agentId));
            const selectedAgent = currentStillAvailable ? available.find(agent => String(agent.agentId) === String(prev.agentId)) : available[0];
            const printers = getAgentPrinters(selectedAgent);
            const printerName = currentStillAvailable && printers.some(p => p.name === prev.printerName)
                ? prev.printerName
                : (selectedAgent?.defaultPrinter || printers[0]?.name || '');
            return {
                ...prev,
                agentId: selectedAgent?.agentId || '',
                printerName
            };
        });
    } catch (error) {
        console.error('خطأ في تحميل أجهزة Agent:', error);
        setAgentPrintAgents([]);
        setAgentPrintStatus({ type: 'error', message: error.message });
    } finally {
        setAgentPrintLoading(false);
    }
};

const openAgentPrintModal = async () => {
    const passports = getWorkingPassports();
    if (!hasSearched || passports.length === 0) {
        showToast('اعمل بحث أولًا وتأكد من وجود نتائج ظاهرة للطباعة', 'warning');
        return;
    }
    setShowAgentPrintModal(true);
    setAgentPrintStatus(null);
    await loadAgentPrintDevices();
};

const closeAgentPrintModal = () => {
    if (agentPrintSending) return;
    setShowAgentPrintModal(false);
    setAgentPrintStatus(null);
};

const handleAgentSelectionChange = (agentId) => {
    const agent = agentPrintAgents.find(item => String(item.agentId) === String(agentId));
    const printers = getAgentPrinters(agent);
    setAgentPrintForm(prev => ({
        ...prev,
        agentId,
        printerName: agent?.defaultPrinter || printers[0]?.name || ''
    }));
    setAgentPrintStatus(null);
};

const waitForAgentReportJob = async (jobId) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 120000) {
        await new Promise(resolve => setTimeout(resolve, 1400));
        const response = await fetch(`${config.API_BASE_URL}/api/passports/barcode/print-agent/jobs/${encodeURIComponent(jobId)}`, {
            cache: 'no-store'
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result?.success) {
            throw new Error(result?.error || 'تعذر متابعة حالة أمر الطباعة');
        }

        const status = result?.job?.status;
        if (status === 'printed') return result.job;
        if (status === 'failed') {
            throw new Error(result?.job?.errorMessage || 'فشل Agent في طباعة التقرير');
        }

        setAgentPrintStatus({
            type: 'info',
            message: status === 'printing'
                ? 'استلم Agent التقرير ويقوم بالطباعة الآن...'
                : 'تم إرسال التقرير وينتظر Agent لاستلامه...'
        });
    }
    throw new Error('تم إرسال التقرير، لكن لم يصل تأكيد الطباعة خلال دقيقتين. راجع Agent والطابعة.');
};

const handleAgentProfessionalPrint = async () => {
    const selectedAgent = getAgentPrintSelectedAgent();
    if (!selectedAgent) {
        setAgentPrintStatus({ type: 'error', message: 'اختار جهاز Agent متصلًا أولًا' });
        return;
    }
    if (!agentPrintForm.printerName) {
        setAgentPrintStatus({ type: 'error', message: 'اختار الطابعة المتصلة بالجهاز' });
        return;
    }

    try {
        setAgentPrintSending(true);
        setAgentPrintStatus({ type: 'info', message: 'جاري إرسال نفس HTML وCSS الخاص بالطباعة الاحترافية إلى Agent بدون إعادة رسم...' });

        const payload = buildAgentProfessionalReportPayload();
        const response = await fetch(`${config.API_BASE_URL}/api/passports/barcode/print-agent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                agentId: selectedAgent.agentId,
                printerName: agentPrintForm.printerName,
                copies: Math.min(Math.max(parseInt(agentPrintForm.copies, 10) || 1, 1), 20),
                payload
            })
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result?.success) {
            throw new Error(result?.error || 'تعذر إرسال التقرير إلى Agent');
        }

        setAgentPrintStatus({ type: 'info', message: 'تم إرسال التقرير. في انتظار تأكيد الطباعة من Agent...' });
        await waitForAgentReportJob(result.jobId);

        setAgentPrintStatus({ type: 'success', message: 'تمت طباعة التقرير عن طريق Agent بنجاح ✅' });
        showToast('تمت طباعة التقرير الاحترافي عن طريق Agent بنجاح ✅', 'success');
    } catch (error) {
        console.error('خطأ في طباعة التقرير عبر Agent:', error);
        setAgentPrintStatus({ type: 'error', message: error.message });
        showToast(error.message, 'error');
    } finally {
        setAgentPrintSending(false);
    }
};


const buildProfessionalPrintHtml = () => {
    const sortedPassports = getWorkingPassports();
    
    const printSummary = {
        totalPassports: sortedPassports.length
    };
    
    // استخدام visibleColumns الحالية فقط للأعمدة المحددة
    const currentVisibleColumns = { ...visibleColumns };
    
    // حساب عدد الأعمدة المرئية
    const visibleColumnsCount = Object.values(currentVisibleColumns).filter(Boolean).length;
    
    // تحديد حجم الخط بناءً على عدد الأعمدة
    let baseFontSize, cellFontSize, headerFontSize;
    
    if (visibleColumnsCount <= 5) {
        baseFontSize = '18px';
        cellFontSize = '16px';
        headerFontSize = '18px';
    } else if (visibleColumnsCount <= 10) {
        baseFontSize = '16px';
        cellFontSize = '14px';
        headerFontSize = '16px';
    } else if (visibleColumnsCount <= 15) {
        baseFontSize = '14px';
        cellFontSize = '12px';
        headerFontSize = '14px';
    } else if (visibleColumnsCount <= 20) {
        baseFontSize = '12px';
        cellFontSize = '11px';
        headerFontSize = '12px';
    } else {
        baseFontSize = '11px';
        cellFontSize = '10px';
        headerFontSize = '11px';
    }
    
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>كشف الجوازات</title>
            <style>
                @page {
                    size: A4 landscape;
                    margin: 8mm 5mm;
                }
                
                * { 
                    margin: 0; 
                    padding: 0; 
                    box-sizing: border-box; 
                }
                
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    direction: rtl;
                    background: white;
                    color: black;
                    font-size: ${baseFontSize};
                    line-height: 1.4;
                }
                
                .print-container {
                    max-width: 100%;
                    margin: 0;
                    background: white;
                    padding: 8px;
                }
                
                .report-header {
                    text-align: center;
                    margin-bottom: 20px;
                    border-bottom: 3px solid black;
                    padding-bottom: 15px;
                }
                
                .company-name {
                    font-size: 32px;
                    font-weight: bold;
                    color: black;
                    margin-bottom: 8px;
                }
                
                .report-title {
                    font-size: 24px;
                    color: black;
                    margin-bottom: 8px;
                }
                
                .report-date {
                    font-size: 18px;
                    color: black;
                }
                
                .summary-section {
                    background: white;
                    border: 2px solid black;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 20px;
                }
                
                .summary-title {
                    font-size: 24px;
                    font-weight: bold;
                    color: black;
                    margin-bottom: 15px;
                    text-align: center;
                    border-bottom: 1px solid black;
                    padding-bottom: 8px;
                }
                
                .summary-item {
                    background: white;
                    padding: 12px;
                    border: 1px solid black;
                    border-radius: 6px;
                }
                
                .summary-label {
                    font-weight: bold;
                    color: black;
                    margin-bottom: 6px;
                    font-size: 16px;
                }
                
                .summary-value {
                    font-size: 22px;
                    font-weight: bold;
                    color: black;
                }
                
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    border: 2px solid black;
                }
                
                th, td { 
                    padding: 10px 6px;
                    text-align: center;
                    border: 1px solid black;
                    font-size: ${cellFontSize};
                    color: black;
                    font-weight: bold;
                }
                
                th { 
                    background: #f0f0f0;
                    font-weight: bold;
                    color: black;
                    font-size: ${headerFontSize};
                }
                
                tr:nth-child(even) { 
                    background: #f8f8f8; 
                }
                
                tr:nth-child(odd) { 
                    background: white; 
                }
                
                @media print {
                    body { 
                        font-size: ${baseFontSize};
                    }
                    
                    .print-container {
                        padding: 5px;
                    }
                    
                    th, td { 
                        padding: 8px 4px; 
                        font-size: ${cellFontSize};
                        font-weight: bold;
                    }
                    
                    th {
                        font-size: ${headerFontSize};
                    }
                    
                    @page {
                        margin: 8mm 3mm;
                    }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <div class="report-header">
                    <div class="company-name">${imageExportSettings.customTitle || 'نظام إدارة الجوازات'}</div>
                    <div class="report-title">كشف الجوازات</div>

                    ${sortConfig.key ? `
                        <div style="text-align: center; margin: 15px 0; padding: 10px; background: #f0f8ff; border: 1px solid #4472C4; border-radius: 5px;">
                            <strong style="color: #2F75B5;">
                            مُرتب حسب: ${
                                sortConfig.key === 'nameArabic' ? 'الاسم بالعربي' :
                                sortConfig.key === 'passportId' ? 'رقم الملف' :
                                sortConfig.key === 'governorate' ? 'المحافظة' :
                                sortConfig.key === 'company' ? 'الشركة' :
                                sortConfig.key === 'blacklistCompanies' ? 'الشركات الممنوع فيها' :
                                sortConfig.key === 'blacklistReasons' ? 'سبب المنع' :
                                sortConfig.key === 'notes' ? 'الملاحظات' :
                                sortConfig.key === 'note1' ? 'ملاحظة 1' :
                                sortConfig.key === 'note2' ? 'ملاحظة 2' :
                                sortConfig.key === 'note3' ? 'ملاحظة 3' :
                                sortConfig.key === 'note4' ? 'ملاحظة 4' : sortConfig.key
                            } (${sortConfig.direction === 'asc' ? 'تصاعدي' : 'تنازلي'})
                            </strong>
                        </div>
                    ` : ''}

                    <div class="report-date">
                        تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            weekday: 'long'
                        })} | عدد الجوازات: ${sortedPassports.length} | عدد الأعمدة: ${visibleColumnsCount}
                    </div>
                </div>

                ${imageExportSettings.showHeader ? `
                <div class="summary-section">
                    <div class="summary-title">الملخص الإحصائي</div>
                    <div style="text-align: center; padding: 10px; display: flex; justify-content: center; gap: 30px; flex-wrap: wrap;">
                        ${imageExportSettings.showTotal ? `
                        <div class="summary-item" style="display: inline-block; max-width: 180px; padding: 10px 20px;">
                            <div class="summary-label" style="font-size: 14px; margin-bottom: 5px;">إجمالي الجوازات</div>
                            <div class="summary-value" style="font-size: 20px;">${printSummary.totalPassports}</div>
                        </div>` : ''}
                    </div>
                </div>` : ''}

                <table>
                    <thead>
                        <tr>
                            ${currentVisibleColumns.serialNumber ? '<th>م</th>' : ''}
                            ${currentVisibleColumns.passportId ? '<th>رقم الملف</th>' : ''}
                            ${currentVisibleColumns.nameArabic ? '<th>الاسم بالعربي</th>' : ''}
                            ${currentVisibleColumns.nameEnglish ? '<th>الاسم بالإنجليزي</th>' : ''}
                            ${currentVisibleColumns.passportNumber ? '<th>رقم الجواز</th>' : ''}
                            ${currentVisibleColumns.nationalId ? '<th>الرقم القومي</th>' : ''}
                            ${currentVisibleColumns.phoneNumbers ? '<th>أرقام الهواتف</th>' : ''}
                            ${currentVisibleColumns.delegate ? '<th>المندوب</th>' : ''}
                            ${currentVisibleColumns.company ? '<th>الشركة</th>' : ''}
                            ${currentVisibleColumns.previousCompanies ? '<th>الشركات السابقة</th>' : ''}
                            ${currentVisibleColumns.blacklistCompanies ? '<th>الشركات الممنوع فيها</th>' : ''}
                            ${currentVisibleColumns.blacklistReasons ? '<th>سبب المنع</th>' : ''}
                            ${currentVisibleColumns.passportProfession ? '<th>المهنة بالجواز</th>' : ''}
                            ${currentVisibleColumns.travelProfession ? '<th>مهنة السفر</th>' : ''}
                            ${currentVisibleColumns.medicalExamDate ? '<th>تاريخ الكشف</th>' : ''}
                            ${currentVisibleColumns.networkDate ? '<th>تاريخ النت</th>' : ''}
                            ${currentVisibleColumns.visaDate ? '<th>تاريخ التأشير</th>' : ''}
                            ${currentVisibleColumns.travelDate ? '<th>تاريخ السفر</th>' : ''}
                            ${currentVisibleColumns.borderNumber ? '<th>رقم الحدود</th>' : ''}
                            ${currentVisibleColumns.violationNotes ? '<th>المخالفة / ملاحظتها</th>' : ''}
                            ${currentVisibleColumns.travelDuration ? '<th>مدة السفر</th>' : ''}
                            ${currentVisibleColumns.passportStatus ? '<th>حالة الجواز</th>' : ''}
                            ${currentVisibleColumns.receiveStatus ? '<th>حالة الاستلام</th>' : ''}
                            ${currentVisibleColumns.notes ? '<th>الملاحظات</th>' : ''}
                            ${currentVisibleColumns.note1 ? '<th>ملاحظة 1</th>' : ''}
                            ${currentVisibleColumns.note2 ? '<th>ملاحظة 2</th>' : ''}
                            ${currentVisibleColumns.note3 ? '<th>ملاحظة 3</th>' : ''}
                            ${currentVisibleColumns.note4 ? '<th>ملاحظة 4</th>' : ''}
                            ${currentVisibleColumns.medicalExamStatus ? '<th>حالة الكشف الطبي</th>' : ''}
                            ${currentVisibleColumns.address ? '<th>العنوان</th>' : ''}
                            ${currentVisibleColumns.governorate ? '<th>المحافظة</th>' : ''}
                            ${currentVisibleColumns.age ? '<th>العمر</th>' : ''}
                            ${currentVisibleColumns.birthDate ? '<th>تاريخ الميلاد</th>' : ''}
                            ${currentVisibleColumns.militaryStatus ? '<th>الموقف من التجنيد</th>' : ''}
                            ${currentVisibleColumns.passportIssueDate ? '<th>تاريخ إصدار الجواز</th>' : ''}
                            ${currentVisibleColumns.passportExpiryDate ? '<th>تاريخ انتهاء الجواز</th>' : ''}
                            ${currentVisibleColumns.licenseType ? '<th>نوع الرخصة</th>' : ''}
                            ${currentVisibleColumns.licenseExpiryDate ? '<th>تاريخ انتهاء الرخصة</th>' : ''}
                    ${currentVisibleColumns.abilityTest ? '<th>نتيجة اختبار القدرات</th>' : ''}
                            ${currentVisibleColumns.applicationNumber ? '<th>رقم الطلب</th>' : ''}
                            ${currentVisibleColumns.fingerprintDate ? '<th>تاريخ البصمة</th>' : ''}
                            ${currentVisibleColumns.consulateDate ? '<th>تاريخ دخول القنصلية</th>' : ''}
                            ${currentVisibleColumns.documentsDeliveryStatus ? '<th>حالة تسليم الأوراق</th>' : ''}
                            ${currentVisibleColumns.medicalInsuranceDate ? '<th>تاريخ التأمين الطبي</th>' : ''}
                            ${currentVisibleColumns.receiveDate ? '<th>تاريخ الاستلام</th>' : ''}
                            ${currentVisibleColumns.receiverName ? '<th>اسم المستلم</th>' : ''}
                            ${currentVisibleColumns.receiverPhone ? '<th>رقم هاتف المستلم</th>' : ''}
                        </tr>
                    </thead>
                    <tbody>
                        ${sortedPassports.map((passport, index) => `
                            <tr>
                                ${currentVisibleColumns.serialNumber ? `<td>${index + 1}</td>` : ''}
                                ${currentVisibleColumns.passportId ? `<td>${getPassportFileNumber(passport)}</td>` : ''}
                                ${currentVisibleColumns.nameArabic ? `<td>${passport.NameArabic}</td>` : ''}
                                ${currentVisibleColumns.nameEnglish ? `<td>${passport.NameEnglish || '-'}</td>` : ''}
                                ${currentVisibleColumns.passportNumber ? `<td>${passport.PassportNumber}</td>` : ''}
                                ${currentVisibleColumns.nationalId ? `<td>${passport.NationalID || '-'}</td>` : ''}
                                ${currentVisibleColumns.phoneNumbers ? `<td>${(() => {
                                    try {
                                        const phones = JSON.parse(passport.PhoneNumbers || '[]');
                                        if (Array.isArray(phones)) {
                                            return phones.filter(phone => phone && phone.trim()).join(' - ') || '-';
                                        } else {
                                            return passport.PhoneNumbers || '-';
                                        }
                                    } catch {
                                        return passport.PhoneNumbers || '-';
                                    }
                                })()}</td>` : ''}
                                ${currentVisibleColumns.delegate ? `<td>${delegates.find(d => d.DelegateID === passport.DelegateID)?.DelegateName || '-'}</td>` : ''}
                                ${currentVisibleColumns.company ? `<td>${companies.find(c => c.CompanyID === passport.CompanyID)?.CompanyName || '-'}</td>` : ''}
                                ${currentVisibleColumns.previousCompanies ? `<td>${(() => {
                                    try {
                                        const prevCompanies = JSON.parse(passport.PreviousCompanies || '[]');
                                        if (Array.isArray(prevCompanies) && prevCompanies.length > 0) {
                                            const companyNames = prevCompanies
                                                .map(companyId => {
                                                    const company = companies.find(c => c.CompanyID.toString() === companyId?.toString());
                                                    return company?.CompanyName;
                                                })
                                                .filter(Boolean);
                                            
                                            if (companyNames.length > 0) {
                                                return companyNames.join(' ، ');
                                            }
                                        }
                                        return '-';
                                    } catch {
                                        return '-';
                                    }
                                })()}</td>` : ''}
                                ${currentVisibleColumns.blacklistCompanies ? `<td>${passport.BlacklistCompanies || '-'}</td>` : ''}
                                ${currentVisibleColumns.blacklistReasons ? `<td>${passport.BlacklistReasons || '-'}</td>` : ''}
                                ${currentVisibleColumns.passportProfession ? `<td>${escapeHtml(getPassportProfessionName(passport))}</td>` : ''}
                                ${currentVisibleColumns.travelProfession ? `<td>${escapeHtml(getTravelProfessionName(passport))}</td>` : ''}
                                ${currentVisibleColumns.medicalExamDate ? `<td>${passport.MedicalExamDate ? new Date(passport.MedicalExamDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.networkDate ? `<td>${passport.NetworkDate ? new Date(passport.NetworkDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.visaDate ? `<td>${passport.VisaDate ? new Date(passport.VisaDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.travelDate ? `<td>${passport.TravelDate ? new Date(passport.TravelDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.borderNumber ? `<td>${passport.BorderNumber || '-'}</td>` : ''}
                                ${currentVisibleColumns.violationNotes ? `<td>${passport.ViolationNotes || '-'}</td>` : ''}
                                ${currentVisibleColumns.travelDuration ? `<td>${passport.TravelDuration || '-'}</td>` : ''}
                                ${currentVisibleColumns.passportStatus ? `<td>${passport.PassportStatus || '-'}</td>` : ''}
                                ${currentVisibleColumns.receiveStatus ? `<td>${passport.PassportReceiveStatus || 'لم يستلم'}</td>` : ''}
                                ${currentVisibleColumns.notes ? `<td>${hideOriginalNotes ? (tempNotes[passport.PassportID] || '-') : (getAllNotes(passport) || '-')}</td>` : ''}
                                ${currentVisibleColumns.note1 ? `<td>${getSpecificNote(passport, 1) || '-'}</td>` : ''}
                                ${currentVisibleColumns.note2 ? `<td>${getSpecificNote(passport, 2) || '-'}</td>` : ''}
                                ${currentVisibleColumns.note3 ? `<td>${getSpecificNote(passport, 3) || '-'}</td>` : ''}
                                ${currentVisibleColumns.note4 ? `<td>${getSpecificNote(passport, 4) || '-'}</td>` : ''}
                                ${currentVisibleColumns.medicalExamStatus ? `<td>${passport.MedicalExamStatus || '-'}</td>` : ''}
                                ${currentVisibleColumns.address ? `<td>${passport.Address || '-'}</td>` : ''}
                                ${currentVisibleColumns.governorate ? `<td>${passport.Governorate || '-'}</td>` : ''}
                                ${currentVisibleColumns.age ? `<td>${passport.Age || '-'}</td>` : ''}
                                ${currentVisibleColumns.birthDate ? `<td>${passport.DateOfBirth ? new Date(passport.DateOfBirth).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.militaryStatus ? `<td>${passport.MilitaryStatus || '-'}</td>` : ''}
                                ${currentVisibleColumns.passportIssueDate ? `<td>${passport.PassportIssueDate ? new Date(passport.PassportIssueDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.passportExpiryDate ? `<td>${passport.PassportExpiryDate ? new Date(passport.PassportExpiryDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.licenseType ? `<td>${passport.LicenseType || '-'}</td>` : ''}
                                ${currentVisibleColumns.licenseExpiryDate ? `<td>${passport.LicenseExpiryDate ? new Date(passport.LicenseExpiryDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                         ${currentVisibleColumns.abilityTest ? `<td>${passport.AbilityTest || '-'}</td>` : ''}
                                ${currentVisibleColumns.applicationNumber ? `<td>${passport.ApplicationNumber || '-'}</td>` : ''}
                                ${currentVisibleColumns.fingerprintDate ? `<td>${passport.FingerprintDate ? new Date(passport.FingerprintDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.consulateDate ? `<td>${passport.ConsulateEntryDate ? new Date(passport.ConsulateEntryDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.documentsDeliveryStatus ? `<td>${passport.DocumentsDeliveryStatus || 'لم يسلم'}</td>` : ''}
                                ${currentVisibleColumns.medicalInsuranceDate ? `<td>${passport.MedicalInsuranceDate ? new Date(passport.MedicalInsuranceDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.receiveDate ? `<td>${passport.ReceiveDate ? new Date(passport.ReceiveDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                ${currentVisibleColumns.receiverName ? `<td>${passport.FriendName || '-'}</td>` : ''}
                                ${currentVisibleColumns.receiverPhone ? `<td>${passport.FriendPhone || '-'}</td>` : ''}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                <div style="margin-top: 20px; text-align: center; color: black; font-size: 14px; border-top: 1px solid black; padding-top: 10px; ${!imageExportSettings.showFooter ? 'display:none;' : ''}">
                    تم إنشاء هذا التقرير بواسطة نظام إدارة الجوازات | 
                    ${new Date().toLocaleString('ar-EG')}
                </div>
            </div>
        </body>
        </html>
    `;

    return htmlContent;
};


const handleAdvancedPrint = () => {
    if (selectedPassports.length === 0) {
        showToast('لا توجد بيانات للطباعة', 'warning');
        return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        showToast('المتصفح منع نافذة الطباعة. اسمح بالنوافذ المنبثقة ثم أعد المحاولة.', 'warning');
        return;
    }

    const htmlContent = buildProfessionalPrintHtml();

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    showToast('تم فتح نافذة الطباعة الاحترافية!', 'success');
};


const getImageExportLayoutSettings = (visibleColumnsCount, runningInsideAndroidApp) => {
    let headerFontSize, cellFontSize, titleFontSize, containerWidth;
    if (visibleColumnsCount <= 5) {
        headerFontSize = '16px'; cellFontSize = '15px'; titleFontSize = '26px'; containerWidth = '900px';
    } else if (visibleColumnsCount <= 10) {
        headerFontSize = '14px'; cellFontSize = '13px'; titleFontSize = '24px'; containerWidth = '1100px';
    } else if (visibleColumnsCount <= 15) {
        headerFontSize = '12px'; cellFontSize = '11px'; titleFontSize = '22px'; containerWidth = '1300px';
    } else if (visibleColumnsCount <= 20) {
        headerFontSize = '11px'; cellFontSize = '10px'; titleFontSize = '20px'; containerWidth = runningInsideAndroidApp ? '1350px' : '1500px';
    } else {
        headerFontSize = '10px'; cellFontSize = '9px'; titleFontSize = '18px'; containerWidth = runningInsideAndroidApp ? '1450px' : '1700px';
    }

    return { headerFontSize, cellFontSize, titleFontSize, containerWidth };
};

const renderPassportReportImageBlob = async ({
    headers,
    pageRows,
    theme,
    settings,
    title,
    totalRowsCount,
    pageNumber,
    totalPages,
    runningInsideAndroidApp
}) => {
    const { headerFontSize, cellFontSize, titleFontSize, containerWidth } = getImageExportLayoutSettings(headers.length, runningInsideAndroidApp);

    const container = document.createElement('div');
    container.style.cssText = `
        width: ${containerWidth};
        padding: 30px;
        background: ${theme.bg};
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        direction: rtl;
        color: #1a1a1a;
        font-size: ${cellFontSize};
        line-height: 1.6;
        position: fixed;
        left: 0;
        top: 0;
        transform: translateX(-110%);
        z-index: 2147483000;
        pointer-events: none;
        box-sizing: border-box;
    `;

    const headerCells = headers.map(h => `
        <th style="
            padding: 14px 10px;
            background: ${theme.headerBg};
            color: white;
            font-size: ${headerFontSize};
            font-weight: bold;
            text-align: center;
            white-space: nowrap;
            border: 1px solid ${theme.headerBorder};
            text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
        ">${escapeHtml(h)}</th>
    `).join('');

    const rowsHtml = pageRows.map((row, rowIndex) => {
        const cells = headers.map(h => `
            <td style="
                padding: 10px 8px;
                border: 1px solid ${theme.cellBorder};
                font-size: ${cellFontSize};
                font-weight: bold;
                text-align: center;
                color: #1a1a1a;
                background: ${rowIndex % 2 === 0 ? theme.rowOdd : theme.rowEven};
            ">${escapeHtml(row[h] ?? '-')}</td>
        `).join('');
        return `<tr>${cells}</tr>`;
    }).join('');

    const headerSection = settings.showHeader ? `
        <div style="
            margin-bottom: 25px;
            padding: 30px;
            background: ${theme.cardBg};
            border-radius: 15px;
            border: 3px solid ${theme.cardBorder};
            box-shadow: 0 4px 15px rgba(0,0,0,0.15);
        ">
            <div style="font-size:${titleFontSize};font-weight:bold;text-align:center;color:${theme.titleColor};margin-bottom:20px;text-shadow:1px 1px 2px rgba(0,0,0,0.1);">
                📋 ${escapeHtml(title)}
            </div>
            ${(settings.showTotal || settings.showPageNum) ? `
            <div style="display:flex;justify-content:space-around;align-items:center;gap:20px;">
                ${settings.showTotal ? `
                <div style="text-align:center;padding:20px 35px;background:white;border-radius:12px;border:3px solid ${theme.cardBorder};min-width:160px;box-shadow:0 3px 10px rgba(0,0,0,0.1);">
                    <div style="font-size:14px;color:${theme.titleColor};font-weight:700;margin-bottom:10px;">إجمالي الجوازات</div>
                    <div style="font-size:32px;color:${theme.titleColor};font-weight:bold;">${totalRowsCount}</div>
                </div>` : ''}
                ${settings.showPageNum ? `
                <div style="text-align:center;padding:20px 35px;background:white;border-radius:12px;border:3px solid ${theme.cardBorder};min-width:160px;box-shadow:0 3px 10px rgba(0,0,0,0.1);">
                    <div style="font-size:14px;color:${theme.titleColor};font-weight:700;margin-bottom:10px;">الصفحة</div>
                    <div style="font-size:32px;color:${theme.titleColor};font-weight:bold;">${pageNumber} / ${totalPages}</div>
                </div>` : ''}
            </div>` : ''}
            <div style="font-size:13px;color:${theme.titleColor};text-align:center;margin-top:18px;font-weight:600;">
                📅 تاريخ التصدير: ${new Date().toLocaleString('ar-EG')}
            </div>
        </div>
    ` : settings.customTitle ? `
        <div style="text-align:center;margin-bottom:20px;font-size:${titleFontSize};font-weight:bold;color:${theme.titleColor};">
            📋 ${escapeHtml(title)}
        </div>
    ` : '';

    const footerSection = settings.showFooter ? `
        <div style="margin-top:25px;padding:20px;background:${theme.footerBg};border-radius:12px;text-align:center;border:3px solid ${theme.footerBorder};box-shadow:0 3px 12px rgba(0,0,0,0.1);">
            <div style="font-size:15px;color:${theme.titleColor};font-weight:bold;margin-bottom:6px;">💼 نظام إدارة الجوازات</div>
            <div style="font-size:13px;color:${theme.titleColor};font-weight:600;">صفحة ${pageNumber} من ${totalPages}</div>
        </div>
    ` : '';

    container.innerHTML = `
        ${headerSection}
        <div style="background:white;border-radius:15px;overflow:hidden;border:3px solid ${theme.cardBorder};box-shadow:0 5px 20px rgba(0,0,0,0.15);">
            <table style="width:100%;border-collapse:collapse;background:white;">
                <thead><tr>${headerCells}</tr></thead>
                <tbody>${rowsHtml}</tbody>
            </table>
        </div>
        ${footerSection}
    `;

    document.body.appendChild(container);
    await waitForExportRender();

    try {
        const canvas = await html2canvas(container, {
            backgroundColor: null,
            scale: runningInsideAndroidApp ? 1.25 : 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: container.scrollWidth,
            windowHeight: container.scrollHeight,
            width: container.scrollWidth,
            height: container.scrollHeight
        });

        const blob = await new Promise((resolve, reject) => {
            if (!canvas?.toBlob) {
                try {
                    const dataUrl = canvas.toDataURL('image/png', 0.95);
                    fetch(dataUrl).then(r => r.blob()).then(resolve).catch(reject);
                } catch (err) {
                    reject(err);
                }
                return;
            }
            canvas.toBlob((result) => {
                if (result) resolve(result);
                else reject(new Error('تعذر إنشاء ملف الصورة'));
            }, 'image/png', 0.95);
        });

        return blob;
    } finally {
        if (container.parentNode) {
            container.parentNode.removeChild(container);
        }
    }
};

const buildDelegateImageExportTitle = (delegateName) => {
    const baseTitle = String(imageExportSettings.customTitle || '').trim();
    const safeDelegateName = String(delegateName || 'بدون مندوب').trim() || 'بدون مندوب';
    return baseTitle ? `${baseTitle} - المندوب: ${safeDelegateName}` : `كشف الجوازات - المندوب: ${safeDelegateName}`;
};

const getDelegateDisplayNameForPassport = (passport) => {
    const delegate = delegates.find(d => String(d.DelegateID) === String(passport?.DelegateID ?? ''));
    const delegateName = String(delegate?.DelegateName || passport?.DelegateName || '').trim();
    return delegateName || 'بدون مندوب';
};

const buildDelegateGroupsFromWorkingPassports = (passports) => {
    const groupsMap = new Map();

    passports.forEach((passport) => {
        const delegateName = getDelegateDisplayNameForPassport(passport);
        if (!groupsMap.has(delegateName)) {
            groupsMap.set(delegateName, []);
        }
        groupsMap.get(delegateName).push(passport);
    });

    return Array.from(groupsMap.entries())
        .map(([delegateName, passports]) => ({ delegateName, passports }))
        .sort((a, b) => a.delegateName.localeCompare(b.delegateName, 'ar'));
};

const exportDelegateReportsAsZip = async () => {
    if (!Array.isArray(selectedPassports) || selectedPassports.length === 0) {
        showToast('لا توجد بيانات لتصديرها', 'warning');
        return;
    }

    try {
        const workingPassports = getWorkingPassports();
        if (!Array.isArray(workingPassports) || workingPassports.length === 0) {
            showToast('لا توجد بيانات ظاهرة حالياً للتصدير', 'warning');
            return;
        }

        const delegateGroups = buildDelegateGroupsFromWorkingPassports(workingPassports)
            .filter(group => Array.isArray(group.passports) && group.passports.length > 0);

        if (delegateGroups.length === 0) {
            showToast('لم يتم العثور على مناديب داخل البيانات المعروضة', 'warning');
            return;
        }

        const adv = imageExportSettings;
        const thm = colorThemes[adv.colorTheme] || colorThemes.blue;
        const runningInsideAndroidApp = isAndroidWebViewExport();
        const zip = new JSZip();
        const datePart = new Date().toISOString().split('T')[0];

        setDelegateZipLoading(true);
        setDelegateZipProgress({ current: 0, total: delegateGroups.length, delegateName: '' });
        showToast('جاري تجهيز ملفات صور المناديب...', 'info');

        for (let groupIndex = 0; groupIndex < delegateGroups.length; groupIndex += 1) {
            const group = delegateGroups[groupIndex];
            const groupRows = group.passports
                .map((passport, index) => buildPassportExportRowForImage(passport, index))
                .filter(row => Object.keys(row).length > 0);

            if (groupRows.length === 0) continue;

            const headers = Object.keys(groupRows[0]);
            const requestedMaxRows = Number(adv.maxRowsPerImage) || 25;
            const maxRowsPerImage = runningInsideAndroidApp ? Math.min(requestedMaxRows, 12) : requestedMaxRows;
            const totalRows = groupRows.length;
            const pages = (!runningInsideAndroidApp && totalRows <= maxRowsPerImage + 4)
                ? [groupRows]
                : chunk(groupRows, maxRowsPerImage);

            const delegateTitle = buildDelegateImageExportTitle(group.delegateName);
            const safeDelegateName = sanitizeDownloadFileName(group.delegateName || 'بدون مندوب', 'بدون مندوب');

            setDelegateZipProgress({ current: groupIndex + 1, total: delegateGroups.length, delegateName: group.delegateName });

            for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
                const blob = await renderPassportReportImageBlob({
                    headers,
                    pageRows: pages[pageIndex],
                    theme: thm,
                    settings: adv,
                    title: delegateTitle,
                    totalRowsCount: totalRows,
                    pageNumber: pageIndex + 1,
                    totalPages: pages.length,
                    runningInsideAndroidApp
                });

                const fileName = pages.length > 1
                    ? `${safeDelegateName} - جزء ${pageIndex + 1} من ${pages.length}.png`
                    : `${safeDelegateName}.png`;

                zip.file(fileName, blob);
            }
        }

        if (Object.keys(zip.files).length === 0) {
            showToast('لم يتم إنشاء أي صور للتصدير', 'warning');
            return;
        }

        const zipBlob = await zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: { level: 5 }
        });

        await saveBlobFile(zipBlob, `كشف-المناديب-${datePart}.zip`, 'application/zip');
        showToast(`تم إنشاء ملف ZIP لصور ${delegateGroups.length} مندوب بنجاح ✅`, 'success');
    } catch (error) {
        console.error('خطأ أثناء تصدير صور المناديب ZIP:', error);
        showToast(`حدث خطأ أثناء إنشاء ZIP المناديب${error?.message ? `: ${error.message}` : ''}`, 'error');
    } finally {
        setDelegateZipLoading(false);
        setDelegateZipProgress(null);
    }
};


const savePassportReportAsImages = async () => {
    if (!Array.isArray(selectedPassports) || selectedPassports.length === 0) {
        showToast('لا توجد بيانات لتصديرها كصورة', 'warning');
        return;
    }

    try {
        showToast('جاري تجهيز صور الكشف...', 'info');

        const sortedPassports = getWorkingPassports();
        const adv = imageExportSettings;
        const thm = colorThemes[adv.colorTheme] || colorThemes.blue;
        const runningInsideAndroidApp = isAndroidWebViewExport();

        // بناء بيانات ديناميكية
        const mainData = sortedPassports.map((passport, index) =>
            buildPassportExportRowForImage(passport, index)
        ).filter(row => Object.keys(row).length > 0);

        if (mainData.length === 0) {
            showToast('لم يتم اختيار أى أعمدة للتصدير', 'warning');
            return;
        }

        const headers = Object.keys(mainData[0]);
        const visibleColumnsCount = headers.length;

        // تحديد حجم الخط بناءً على عدد الأعمدة
        let headerFontSize, cellFontSize, titleFontSize, containerWidth;
        if (visibleColumnsCount <= 5) {
            headerFontSize = '16px'; cellFontSize = '15px'; titleFontSize = '26px'; containerWidth = '900px';
        } else if (visibleColumnsCount <= 10) {
            headerFontSize = '14px'; cellFontSize = '13px'; titleFontSize = '24px'; containerWidth = '1100px';
        } else if (visibleColumnsCount <= 15) {
            headerFontSize = '12px'; cellFontSize = '11px'; titleFontSize = '22px'; containerWidth = '1300px';
        } else if (visibleColumnsCount <= 20) {
            headerFontSize = '11px'; cellFontSize = '10px'; titleFontSize = '20px'; containerWidth = runningInsideAndroidApp ? '1350px' : '1500px';
        } else {
            headerFontSize = '10px'; cellFontSize = '9px'; titleFontSize = '18px'; containerWidth = runningInsideAndroidApp ? '1450px' : '1700px';
        }

        // عدد الصفوف لكل صورة من الإعدادات
        // في تطبيق الموبايل نقلل حجم كل صورة لتجنب فشل WebView مع Canvas كبير.
        const requestedMaxRows = Number(adv.maxRowsPerImage) || 25;
        const MAX_ROWS = runningInsideAndroidApp ? Math.min(requestedMaxRows, 12) : requestedMaxRows;
        const totalRows = mainData.length;
        let pages;
        if (runningInsideAndroidApp) {
            pages = chunk(mainData, MAX_ROWS);
        } else if (totalRows <= MAX_ROWS + 4) {
            pages = [mainData];
        } else {
            pages = chunk(mainData, MAX_ROWS);
        }
        const totalPages = pages.length;

        let pageNumber = 1;

        for (const pageRows of pages) {
            const container = document.createElement('div');
            container.style.cssText = `
                width: ${containerWidth};
                padding: 30px;
                background: ${thm.bg};
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                direction: rtl;
                color: #1a1a1a;
                font-size: ${cellFontSize};
                line-height: 1.6;
                position: fixed;
                left: 0;
                top: 0;
                transform: translateX(-110%);
                z-index: 2147483000;
                pointer-events: none;
                box-sizing: border-box;
            `;

            const headerCells = headers.map(h => `
                <th style="
                    padding: 14px 10px;
                    background: ${thm.headerBg};
                    color: white;
                    font-size: ${headerFontSize};
                    font-weight: bold;
                    text-align: center;
                    white-space: nowrap;
                    border: 1px solid ${thm.headerBorder};
                    text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
                ">${escapeHtml(h)}</th>
            `).join('');

            const rowsHtml = pageRows.map((row, rowIndex) => {
                const cells = headers.map(h => `
                    <td style="
                        padding: 10px 8px;
                        border: 1px solid ${thm.cellBorder};
                        font-size: ${cellFontSize};
                        font-weight: bold;
                        text-align: center;
                        color: #1a1a1a;
                        background: ${rowIndex % 2 === 0 ? thm.rowOdd : thm.rowEven};
                    ">${escapeHtml(row[h] ?? '-')}</td>
                `).join('');
                return `<tr>${cells}</tr>`;
            }).join('');

            // الجزء العلوي (اختياري)
            const headerSection = adv.showHeader ? `
                <div style="
                    margin-bottom: 25px;
                    padding: 30px;
                    background: ${thm.cardBg};
                    border-radius: 15px;
                    border: 3px solid ${thm.cardBorder};
                    box-shadow: 0 4px 15px rgba(0,0,0,0.15);
                ">
                    <div style="font-size:${titleFontSize};font-weight:bold;text-align:center;color:${thm.titleColor};margin-bottom:20px;text-shadow:1px 1px 2px rgba(0,0,0,0.1);">
                        📋 ${escapeHtml(adv.customTitle || 'كشف الجوازات')}
                    </div>
                    ${(adv.showTotal || adv.showPageNum) ? `
                    <div style="display:flex;justify-content:space-around;align-items:center;gap:20px;">
                        ${adv.showTotal ? `
                        <div style="text-align:center;padding:20px 35px;background:white;border-radius:12px;border:3px solid ${thm.cardBorder};min-width:160px;box-shadow:0 3px 10px rgba(0,0,0,0.1);">
                            <div style="font-size:14px;color:${thm.titleColor};font-weight:700;margin-bottom:10px;">إجمالي الجوازات</div>
                            <div style="font-size:32px;color:${thm.titleColor};font-weight:bold;">${sortedPassports.length}</div>
                        </div>` : ''}
                        ${adv.showPageNum ? `
                        <div style="text-align:center;padding:20px 35px;background:white;border-radius:12px;border:3px solid ${thm.cardBorder};min-width:160px;box-shadow:0 3px 10px rgba(0,0,0,0.1);">
                            <div style="font-size:14px;color:${thm.titleColor};font-weight:700;margin-bottom:10px;">الصفحة</div>
                            <div style="font-size:32px;color:${thm.titleColor};font-weight:bold;">${pageNumber} / ${totalPages}</div>
                        </div>` : ''}
                    </div>` : ''}
                    <div style="font-size:13px;color:${thm.titleColor};text-align:center;margin-top:18px;font-weight:600;">
                        📅 تاريخ التصدير: ${new Date().toLocaleString('ar-EG')}
                    </div>
                </div>
            ` : adv.customTitle ? `
                <div style="text-align:center;margin-bottom:20px;font-size:${titleFontSize};font-weight:bold;color:${thm.titleColor};">
                    📋 ${escapeHtml(adv.customTitle)}
                </div>
            ` : '';

            // الجزء السفلي (اختياري)
            const footerSection = adv.showFooter ? `
                <div style="margin-top:25px;padding:20px;background:${thm.footerBg};border-radius:12px;text-align:center;border:3px solid ${thm.footerBorder};box-shadow:0 3px 12px rgba(0,0,0,0.1);">
                    <div style="font-size:15px;color:${thm.titleColor};font-weight:bold;margin-bottom:6px;">💼 نظام إدارة الجوازات</div>
                    <div style="font-size:13px;color:${thm.titleColor};font-weight:600;">صفحة ${pageNumber} من ${totalPages}</div>
                </div>
            ` : '';

            container.innerHTML = `
                ${headerSection}
                <div style="background:white;border-radius:15px;overflow:hidden;border:3px solid ${thm.cardBorder};box-shadow:0 5px 20px rgba(0,0,0,0.15);">
                    <table style="width:100%;border-collapse:collapse;background:white;">
                        <thead><tr>${headerCells}</tr></thead>
                        <tbody>${rowsHtml}</tbody>
                    </table>
                </div>
                ${footerSection}
            `;

            document.body.appendChild(container);
            await waitForExportRender();

            const canvas = await html2canvas(container, {
                backgroundColor: null,
                scale: runningInsideAndroidApp ? 1.25 : 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                scrollX: 0,
                scrollY: 0,
                windowWidth: container.scrollWidth,
                windowHeight: container.scrollHeight,
                width: container.scrollWidth,
                height: container.scrollHeight
            });

            document.body.removeChild(container);

            const datePart = new Date().toISOString().split('T')[0];
            const fileName = `كشف-الجوازات-صفحة-${pageNumber}-${datePart}.png`;

            if (isMasarAndroidBridgeAvailable()) {
                const dataUrl = canvas.toDataURL('image/png', 0.92);
                const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
                if (!base64) throw new Error('تعذر تجهيز الصورة للحفظ');
                saveBase64FileToAndroid(fileName, 'image/png', base64);
            } else {
                const blob = await new Promise((resolve) => {
                    if (canvas.toBlob) {
                        canvas.toBlob(resolve, 'image/png', 0.95);
                    } else {
                        resolve(null);
                    }
                });

                if (blob) {
                    await saveBlobFile(blob, fileName, 'image/png');
                } else {
                    const link = document.createElement('a');
                    link.href = canvas.toDataURL('image/png', 0.95);
                    link.download = sanitizeDownloadFileName(fileName);
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            }

            pageNumber += 1;
        }

        showToast(runningInsideAndroidApp ? 'تم إرسال صور الكشف للحفظ داخل التنزيلات / Masar ✅' : 'تم إنشاء صور الكشف بنجاح ✅', 'success');
    } catch (error) {
        console.error('خطأ أثناء تصدير الكشف كصورة:', error);
        showToast('حدث خطأ أثناء إنشاء الصورة', 'error');
    }
};




const exportToPDFPreviewStyle = () => {
    if (selectedPassports.length === 0) {
        showToast('لا توجد بيانات للتصدير', 'warning');
        return;
    }

    const printWindow = window.open('', '_blank');
    const sortedPassports = getWorkingPassports();
    
    const exportSummary = {
        totalPassports: sortedPassports.length
    };

    // حساب عدد الأعمدة المرئية
    const visibleColumnsCount = Object.values(visibleColumns).filter(Boolean).length;
    
    // تحديد العرض المثالي لكل عمود بناءً على عدد الأعمدة
    const getOptimalLayout = (count) => {
        if (count <= 2) {
            return {
                tableWidth: '60%',
                cellPadding: '25px 20px',
                fontSize: { header: '18px', cell: '16px', title: '28px' },
                columnWidth: '50%'
            };
        }
        if (count === 3) {
            return {
                tableWidth: '85%',
                cellPadding: '20px 15px',
                fontSize: { header: '16px', cell: '14px', title: '26px' },
                columnWidth: '33.33%'
            };
        }
        if (count <= 5) {
            return {
                tableWidth: '95%',
                cellPadding: '15px 12px',
                fontSize: { header: '14px', cell: '12px', title: '24px' },
                columnWidth: (100/count) + '%'
            };
        }
        if (count <= 8) {
            return {
                tableWidth: '100%',
                cellPadding: '12px 8px',
                fontSize: { header: '12px', cell: '10px', title: '22px' },
                columnWidth: (100/count) + '%'
            };
        }
        return {
            tableWidth: '100%',
            cellPadding: '8px 4px',
            fontSize: { header: '10px', cell: '8px', title: '20px' },
            columnWidth: (100/count) + '%'
        };
    };

    const layout = getOptimalLayout(visibleColumnsCount);

    printWindow.document.write(`
        <!DOCTYPE html>
        <html dir="rtl">
            <head>
                <title>تصدير PDF - كشف الجوازات</title>
                <meta charset="UTF-8">
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
                    
                    @page {
                        size: A4 ${visibleColumnsCount > 6 ? 'landscape' : 'portrait'};
                        margin: ${visibleColumnsCount > 8 ? '8mm' : '15mm'};
                    }
                    
                    body { 
                        font-family: 'Cairo', sans-serif;
                        padding: 0;
                        margin: 0;
                        background: white;
                        color: black;
                        font-size: ${layout.fontSize.cell};
                        line-height: 1.4;
                    }
                    
                    .container {
                        background-color: white;
                        padding: 20px;
                        margin: 0 auto;
                        max-width: 100%;
                    }
                    
                    h1 {
                        text-align: center;
                        color: #2F75B5;
                        font-size: ${layout.fontSize.title};
                        margin-bottom: 15px;
                        font-weight: 700;
                        border-bottom: 3px solid #2F75B5;
                        padding-bottom: 10px;
                    }

                    .report-info {
                        margin-bottom: 25px;
                        padding: 20px;
                        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                        border: 2px solid #2F75B5;
                        border-radius: 12px;
                        text-align: center;
                    }

                    .report-info h2 {
                        margin: 0 0 15px 0;
                        font-size: ${visibleColumnsCount <= 3 ? '20px' : '16px'};
                        font-weight: 600;
                        color: #2F75B5;
                    }

                    .stat-item {
                        background: white;
                        padding: 15px 25px;
                        border: 2px solid #dee2e6;
                        border-radius: 10px;
                        text-align: center;
                        display: inline-block;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    }

                    .stat-number {
                        font-size: ${visibleColumnsCount <= 3 ? '24px' : '20px'};
                        font-weight: bold;
                        margin-bottom: 5px;
                        color: #2F75B5;
                    }

                    .stat-label {
                        font-size: ${visibleColumnsCount <= 3 ? '14px' : '12px'};
                        color: #666;
                        font-weight: 500;
                    }
                    
                    .controls {
                        margin-bottom: 20px;
                        text-align: center;
                        padding: 15px;
                        background: #f8f9fa;
                        border-radius: 8px;
                        border: 1px solid #dee2e6;
                    }
                    
                    .btn {
                        background: #2F75B5;
                        color: white;
                        border: none;
                        padding: 12px 20px;
                        border-radius: 6px;
                        cursor: pointer;
                        font-size: 14px;
                        font-weight: 600;
                        margin: 0 8px;
                        transition: all 0.2s;
                    }
                    
                    .btn:hover {
                        background: #1e5a96;
                        transform: translateY(-1px);
                    }
                    
                    .btn-secondary {
                        background: #6c757d;
                    }
                    
                    .btn-secondary:hover {
                        background: #545b62;
                    }
                    
                    table {
                        width: ${layout.tableWidth};
                        margin: 20px auto;
                        border-collapse: collapse;
                        background-color: white;
                        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
                        border-radius: 10px;
                        overflow: hidden;
                        border: 2px solid #2F75B5;
                    }
                    
                    th {
                        background: linear-gradient(135deg, #2F75B5, #4A90E2);
                        color: white;
                        font-size: ${layout.fontSize.header};
                        font-weight: 700;
                        padding: ${layout.cellPadding};
                        text-align: center;
                        border-bottom: 3px solid #1e5a96;
                        width: ${layout.columnWidth};
                        word-wrap: break-word;
                        vertical-align: middle;
                        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
                    }
                    
                    td {
                        font-size: ${layout.fontSize.cell};
                        padding: ${layout.cellPadding};
                        border-bottom: 1px solid #dee2e6;
                        vertical-align: middle;
                        text-align: center;
                        color: black;
                        width: ${layout.columnWidth};
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        line-height: 1.5;
                    }

                    tbody tr:nth-child(even) {
                        background-color: #f8f9fa;
                    }
                    
                    tbody tr:nth-child(odd) {
                        background-color: white;
                    }

                    tbody tr:hover {
                        background-color: #e3f2fd !important;
                        transition: background-color 0.2s;
                    }

                    .status-badge {
                        display: inline-block;
                        padding: 4px 10px;
                        border-radius: 15px;
                        font-size: ${visibleColumnsCount <= 3 ? '11px' : '9px'};
                        font-weight: 600;
                        text-transform: uppercase;
                        white-space: nowrap;
                        border: 1px solid;
                    }

                    .status-new { 
                        background: linear-gradient(135deg, #d4edda, #c3e6cb); 
                        color: #155724; 
                        border-color: #c3e6cb; 
                    }
                    
                    .status-old { 
                        background: linear-gradient(135deg, #fff3cd, #ffeaa7); 
                        color: #856404; 
                        border-color: #ffeaa7; 
                    }
                    
                    .status-received { 
                        background: linear-gradient(135deg, #d4edda, #c3e6cb); 
                        color: #155724; 
                        border-color: #c3e6cb; 
                    }
                    
                    .status-not-received { 
                        background: linear-gradient(135deg, #f8d7da, #f5c6cb); 
                        color: #721c24; 
                        border-color: #f5c6cb; 
                    }

                    .date-badge {
                        background: linear-gradient(135deg, #e3f2fd, #bbdefb);
                        color: #1976d2;
                        padding: 4px 8px;
                        border-radius: 12px;
                        font-size: ${visibleColumnsCount <= 3 ? '11px' : '9px'};
                        font-weight: 500;
                        border: 1px solid #bbdefb;
                        white-space: nowrap;
                    }

                    .footer {
                        margin-top: 30px;
                        text-align: center;
                        font-size: ${visibleColumnsCount > 8 ? '10px' : '12px'};
                        color: #666;
                        padding-top: 20px;
                        border-top: 2px solid #dee2e6;
                        background: #f8f9fa;
                        padding: 20px;
                        border-radius: 8px;
                    }

                    /* تحسينات خاصة للأعمدة القليلة */
                    ${visibleColumnsCount <= 3 ? `
                        .container {
                            max-width: 90%;
                            margin: 0 auto;
                        }
                        
                        th {
                            font-size: 18px;
                            padding: 25px 20px;
                            letter-spacing: 0.5px;
                        }
                        
                        td {
                            font-size: 16px;
                            padding: 20px 15px;
                            font-weight: 500;
                        }

                        .serial-number {
                            font-weight: bold;
                            color: #2F75B5;
                            font-size: 18px;
                        }

                        .name-cell {
                            font-weight: 700;
                            color: #333;
                            font-size: 17px;
                        }

                        .passport-cell {
                            font-family: 'Courier New', monospace;
                            font-weight: 600;
                            color: #2F75B5;
                            letter-spacing: 1px;
                        }
                    ` : ''}

                    @media print {
                        .controls { display: none; }
                        body { 
                            font-size: ${layout.fontSize.cell}; 
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        
                        table {
                            page-break-inside: auto;
                        }
                        
                        tr {
                            page-break-inside: avoid;
                            page-break-after: auto;
                        }
                        
                        th, td { 
                            padding: ${visibleColumnsCount <= 3 ? '15px 12px' : '8px 5px'}; 
                        }
                        
                        .container { 
                            padding: 10px; 
                            max-width: 100%;
                        }
                        
                        @page { 
                            margin: ${visibleColumnsCount <= 3 ? '15mm' : '10mm'}; 
                        }
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <div class="controls">
                        <button class="btn" onclick="window.print()">🖨️ طباعة / حفظ PDF</button>
                        <button class="btn btn-secondary" onclick="window.close()">❌ إغلاق</button>
                        <span style="color: #666; font-size: 14px; margin-right: 15px; font-weight: 500;">
                            📊 عدد الأعمدة: ${visibleColumnsCount} | 📄 نموذج محسّن
                        </span>
                    </div>
                
                   <h1>📋 كشف الجوازات - تقرير PDF محسّن</h1>
                    
                    ${sortConfig.key ? `
                        <div style="text-align: center; margin: 20px 0; padding: 15px; background: linear-gradient(135deg, #f0f8ff, #e6f3ff); border: 2px solid #4472C4; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                            <strong style="color: #2F75B5; font-size: ${visibleColumnsCount <= 3 ? '16px' : '14px'};">
                             🔄 مرتب حسب: ${
                                sortConfig.key === 'nameArabic' ? 'الاسم بالعربي' :
                                sortConfig.key === 'passportId' ? 'رقم الملف' :
                                sortConfig.key === 'governorate' ? 'المحافظة' :
                                sortConfig.key === 'company' ? 'الشركة' :
                                sortConfig.key === 'blacklistCompanies' ? 'الشركات الممنوع فيها' :
                                sortConfig.key === 'blacklistReasons' ? 'سبب المنع' :
                                sortConfig.key === 'notes' ? 'الملاحظات' :
                                sortConfig.key === 'note1' ? 'ملاحظة 1' :
                                sortConfig.key === 'note2' ? 'ملاحظة 2' :
                                sortConfig.key === 'note3' ? 'ملاحظة 3' :
                                sortConfig.key === 'note4' ? 'ملاحظة 4' : sortConfig.key
                            } (${sortConfig.direction === 'asc' ? 'تصاعدي ⬆️' : 'تنازلي ⬇️'})
                            </strong>
                        </div>
                    ` : ''}

                    <div class="report-info">
                        <h2>📊 الملخص الإحصائي الشامل</h2>
                        <div style="text-align: center; padding: 15px;">
                            <div class="stat-item">
                                <div class="stat-number">${exportSummary.totalPassports}</div>
                                <div class="stat-label">إجمالي الجوازات المعروضة</div>
                            </div>
                        </div>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                ${visibleColumns.serialNumber ? '<th>🔢 الرقم التسلسلي</th>' : ''}
                                ${visibleColumns.passportId ? '<th>🆔 رقم الملف</th>' : ''}
                                ${visibleColumns.nameArabic ? '<th>👤 الاسم بالعربي</th>' : ''}
                                ${visibleColumns.nameEnglish ? '<th>👤 الاسم بالإنجليزي</th>' : ''}
                                ${visibleColumns.passportNumber ? '<th>📘 رقم الجواز</th>' : ''}
                                ${visibleColumns.nationalId ? '<th>🆔 الرقم القومي</th>' : ''}
                                ${visibleColumns.phoneNumbers ? '<th>📱 أرقام الهواتف</th>' : ''}
                                ${visibleColumns.delegate ? '<th>👨‍💼 المندوب</th>' : ''}
                                ${visibleColumns.company ? '<th>🏢 الشركة</th>' : ''}
                                ${visibleColumns.previousCompanies ? '<th>🏢 الشركات السابقة</th>' : ''}
                                ${visibleColumns.blacklistCompanies ? '<th>🚫 الشركات الممنوع فيها</th>' : ''}
                                ${visibleColumns.blacklistReasons ? '<th>⚠️ سبب المنع</th>' : ''}
                                ${visibleColumns.passportProfession ? '<th>💼 المهنة بالجواز</th>' : ''}
                                ${visibleColumns.travelProfession ? '<th>✈️ مهنة السفر</th>' : ''}
                                ${visibleColumns.medicalExamDate ? '<th>🏥 تاريخ الكشف</th>' : ''}
                                ${visibleColumns.networkDate ? '<th>🌐 تاريخ النت</th>' : ''}
                                ${visibleColumns.visaDate ? '<th>📋 تاريخ التأشير</th>' : ''}
                                ${visibleColumns.travelDate ? '<th>✈️ تاريخ السفر</th>' : ''}
                                ${visibleColumns.borderNumber ? '<th>🧾 رقم الحدود</th>' : ''}
                                ${visibleColumns.violationNotes ? '<th>⚠️ المخالفة / ملاحظتها</th>' : ''}
                                ${visibleColumns.travelDuration ? '<th>⏰ مدة السفر</th>' : ''}
                                ${visibleColumns.passportStatus ? '<th>📊 حالة الجواز</th>' : ''}
                                ${visibleColumns.receiveStatus ? '<th>📦 حالة الاستلام</th>' : ''}
                                ${visibleColumns.notes ? '<th>📝 الملاحظات</th>' : ''}
                                ${visibleColumns.note1 ? '<th>📝 ملاحظة 1</th>' : ''}
                                ${visibleColumns.note2 ? '<th>📝 ملاحظة 2</th>' : ''}
                                ${visibleColumns.note3 ? '<th>📝 ملاحظة 3</th>' : ''}
                                ${visibleColumns.note4 ? '<th>📝 ملاحظة 4</th>' : ''}
                                ${visibleColumns.medicalExamStatus ? '<th>🏥 حالة الكشف الطبي</th>' : ''}
                                ${visibleColumns.address ? '<th>🏠 العنوان</th>' : ''}
                                ${visibleColumns.governorate ? '<th>🗺️ المحافظة</th>' : ''}
                                ${visibleColumns.age ? '<th>🎂 العمر</th>' : ''}
                                ${visibleColumns.birthDate ? '<th>📅 تاريخ الميلاد</th>' : ''}
                                ${visibleColumns.militaryStatus ? '<th>🎖️ الموقف من التجنيد</th>' : ''}
                                ${visibleColumns.passportIssueDate ? '<th>📅 تاريخ إصدار الجواز</th>' : ''}
                                ${visibleColumns.passportExpiryDate ? '<th>⏰ تاريخ انتهاء الجواز</th>' : ''}
                                ${visibleColumns.licenseType ? '<th>🚗 نوع الرخصة</th>' : ''}
                                ${visibleColumns.licenseExpiryDate ? '<th>⏰ تاريخ انتهاء الرخصة</th>' : ''}
                       ${visibleColumns.abilityTest ? '<th>📊 نتيجة اختبار القدرات</th>' : ''}
                                ${visibleColumns.applicationNumber ? '<th>📋 رقم الطلب</th>' : ''}
                                ${visibleColumns.fingerprintDate ? '<th>👆 تاريخ البصمة</th>' : ''}
                                ${visibleColumns.consulateDate ? '<th>🏛️ تاريخ دخول القنصلية</th>' : ''}
                                ${visibleColumns.documentsDeliveryStatus ? '<th>📦 حالة تسليم الأوراق</th>' : ''}
                                ${visibleColumns.medicalInsuranceDate ? '<th>🏥 تاريخ التأمين الطبي</th>' : ''}
                                ${visibleColumns.receiveDate ? '<th>📅 تاريخ الاستلام</th>' : ''}
                                ${visibleColumns.receiverName ? '<th>👤 اسم المستلم</th>' : ''}
                                ${visibleColumns.receiverPhone ? '<th>📱 رقم هاتف المستلم</th>' : ''}
                            </tr>
                       </thead>
                       <tbody>
                           ${sortedPassports.map((passport, index) => `
                               <tr>
                                   ${visibleColumns.serialNumber ? `<td class="serial-number">${index + 1}</td>` : ''}
                                   ${visibleColumns.passportId ? `<td class="passport-cell">${getPassportFileNumber(passport)}</td>` : ''}
                                   ${visibleColumns.nameArabic ? `<td class="name-cell">${passport.NameArabic}</td>` : ''}
                                   ${visibleColumns.nameEnglish ? `<td class="name-cell">${passport.NameEnglish || '-'}</td>` : ''}
                                   ${visibleColumns.passportNumber ? `<td class="passport-cell">${passport.PassportNumber}</td>` : ''}
                                   ${visibleColumns.nationalId ? `<td style="font-family: monospace; font-weight: 500;">${passport.NationalID || '-'}</td>` : ''}
                                   ${visibleColumns.phoneNumbers ? `<td style="font-family: monospace; font-size: ${visibleColumnsCount <= 3 ? '14px' : '10px'};">${(() => {
                                       try {
                                           const phones = JSON.parse(passport.PhoneNumbers || '[]');
                                           if (Array.isArray(phones)) {
                                               return phones.filter(phone => phone && phone.trim()).join('<br>') || '-';
                                           } else {
                                               return passport.PhoneNumbers || '-';
                                           }
                                       } catch {
                                           return passport.PhoneNumbers || '-';
                                       }
                                   })()}</td>` : ''}
                                   ${visibleColumns.delegate ? `<td style="font-weight: 500;">${delegates.find(d => d.DelegateID === passport.DelegateID)?.DelegateName || '-'}</td>` : ''}
                                   ${visibleColumns.company ? `<td style="font-weight: 600; color: #2F75B5;">${companies.find(c => c.CompanyID === passport.CompanyID)?.CompanyName || '-'}</td>` : ''}
                                   ${visibleColumns.previousCompanies ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'};">${(() => {
                                       try {
                                           const prevCompanies = JSON.parse(passport.PreviousCompanies || '[]');
                                           if (Array.isArray(prevCompanies) && prevCompanies.length > 0) {
                                               const companyNames = prevCompanies
                                                   .map(companyId => {
                                                       const company = companies.find(c => c.CompanyID.toString() === companyId?.toString());
                                                       return company?.CompanyName;
                                                   })
                                                   .filter(Boolean);
                                               
                                               if (companyNames.length > 0) {
                                                   return companyNames.join('<br>');
                                               }
                                           }
                                           return '-';
                                       } catch {
                                           return '-';
                                       }
                                   })()}</td>` : ''}
                                   ${visibleColumns.blacklistCompanies ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'}; color:#b91c1c; font-weight:600;">${passport.BlacklistCompanies || '-'}</td>` : ''}
                                   ${visibleColumns.blacklistReasons ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'}; line-height:1.4;">${passport.BlacklistReasons || '-'}</td>` : ''}
                                   ${visibleColumns.passportProfession ? `<td style="font-weight: 500;">${escapeHtml(getPassportProfessionName(passport))}</td>` : ''}
                                   ${visibleColumns.travelProfession ? `<td style="font-weight: 500;">${escapeHtml(getTravelProfessionName(passport))}</td>` : ''}
                                   ${visibleColumns.medicalExamDate ? `<td>${passport.MedicalExamDate ? `<span class="date-badge">${new Date(passport.MedicalExamDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                   ${visibleColumns.networkDate ? `<td>${passport.NetworkDate ? `<span class="date-badge">${new Date(passport.NetworkDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                   ${visibleColumns.visaDate ? `<td>${passport.VisaDate ? `<span class="date-badge">${new Date(passport.VisaDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                   ${visibleColumns.travelDate ? `<td>${passport.TravelDate ? `<span class="date-badge">${new Date(passport.TravelDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                    ${visibleColumns.borderNumber ? `<td style="font-family: monospace;">${passport.BorderNumber || '-'}</td>` : ''}
                                    ${visibleColumns.violationNotes ? `<td>${passport.ViolationNotes || '-'}</td>` : ''}
                                   ${visibleColumns.travelDuration ? `<td><span class="status-badge">${passport.TravelDuration || '-'}</span></td>` : ''}
                                   ${visibleColumns.passportStatus ? `<td><span class="status-badge ${passport.PassportStatus === 'جديد' ? 'status-new' : 'status-old'}">${passport.PassportStatus || '-'}</span></td>` : ''}
                                 ${visibleColumns.receiveStatus ? `<td><span class="status-badge ${passport.PassportReceiveStatus === 'استلم العميل' ? 'status-received' : 'status-not-received'}">${passport.PassportReceiveStatus || 'لم يستلم'}</span></td>` : ''}
                                 ${visibleColumns.notes ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'}; line-height: 1.4;">${hideOriginalNotes ? (tempNotes[passport.PassportID] || '-') : (getAllNotes(passport) || '-')}</td>` : ''}
                                 ${visibleColumns.note1 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'};">${getSpecificNote(passport, 1) || '-'}</td>` : ''}
                                 ${visibleColumns.note2 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'};">${getSpecificNote(passport, 2) || '-'}</td>` : ''}
                                 ${visibleColumns.note3 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'};">${getSpecificNote(passport, 3) || '-'}</td>` : ''}
                                 ${visibleColumns.note4 ? `<td style="font-size: ${visibleColumnsCount <= 3 ? '13px' : '9px'};">${getSpecificNote(passport, 4) || '-'}</td>` : ''}
                                 ${visibleColumns.medicalExamStatus ? `<td><span class="status-badge">${passport.MedicalExamStatus || '-'}</span></td>` : ''}
                                 ${visibleColumns.address ? `<td>${passport.Address || '-'}</td>` : ''}
                                 ${visibleColumns.governorate ? `<td style="font-weight: 500;">${passport.Governorate || '-'}</td>` : ''}
                                 ${visibleColumns.age ? `<td style="font-weight: 600; color: #2F75B5;">${passport.Age || '-'}</td>` : ''}
                                 ${visibleColumns.birthDate ? `<td>${passport.DateOfBirth ? `<span class="date-badge">${new Date(passport.DateOfBirth).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                 ${visibleColumns.militaryStatus ? `<td>${passport.MilitaryStatus || '-'}</td>` : ''}
                                 ${visibleColumns.passportIssueDate ? `<td>${passport.PassportIssueDate ? `<span class="date-badge">${new Date(passport.PassportIssueDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                 ${visibleColumns.passportExpiryDate ? `<td>${passport.PassportExpiryDate ? `<span class="date-badge">${new Date(passport.PassportExpiryDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                 ${visibleColumns.licenseType ? `<td>${passport.LicenseType || '-'}</td>` : ''}
                                 ${visibleColumns.licenseExpiryDate ? `<td>${passport.LicenseExpiryDate ? `<span class="date-badge">${new Date(passport.LicenseExpiryDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                      ${visibleColumns.abilityTest ? `<td><span class="status-badge">${passport.AbilityTest || '-'}</span></td>` : ''}
                                 ${visibleColumns.applicationNumber ? `<td style="font-family: monospace;">${passport.ApplicationNumber || '-'}</td>` : ''}
                                 ${visibleColumns.fingerprintDate ? `<td>${passport.FingerprintDate ? new Date(passport.FingerprintDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                 ${visibleColumns.consulateDate ? `<td>${passport.ConsulateEntryDate ? new Date(passport.ConsulateEntryDate).toLocaleDateString('ar-EG') : '-'}</td>` : ''}
                                 ${visibleColumns.documentsDeliveryStatus ? `<td><span class="status-badge ${passport.DocumentsDeliveryStatus === 'سلم' ? 'status-received' : 'status-not-received'}">${passport.DocumentsDeliveryStatus || 'لم يسلم'}</span></td>` : ''}
                                 ${visibleColumns.medicalInsuranceDate ? `<td>${passport.MedicalInsuranceDate ? '<span class="date-badge">' + new Date(passport.MedicalInsuranceDate).toLocaleDateString('ar-EG') + '</span>' : '-'}</td>` : ''}
                                 ${visibleColumns.receiveDate ? `<td>${passport.ReceiveDate ? `<span class="date-badge">${new Date(passport.ReceiveDate).toLocaleDateString('ar-EG')}</span>` : '-'}</td>` : ''}
                                 ${visibleColumns.receiverName ? `<td style="font-weight: 500;">${passport.FriendName || '-'}</td>` : ''}
                                 ${visibleColumns.receiverPhone ? `<td style="font-family: monospace;">${passport.FriendPhone || '-'}</td>` : ''}
                               </tr>
                           `).join('')}
                       </tbody>
                   </table>

                   <div class="footer">
                       <p><strong>📄 تم إنشاء هذا التقرير بواسطة نظام إدارة الجوازات المتطور</strong></p>
                       <p>📅 تاريخ الإنشاء: ${new Date().toLocaleString('ar-EG')} | 🆔 رقم النسخة: PDF-${new Date().getTime()}</p>
                       <p>💼 نظام متكامل لإدارة الجوازات والسفر | 📊 عدد الأعمدة المعروضة: ${visibleColumnsCount}</p>
                      <p>🎯 تقرير محسّن للطباعة والأرشفة الرقمية</p>
                  </div>
              </div>
          </body>
      </html>
  `);
  printWindow.document.close();
  
  showToast('تم فتح نافذة تصدير PDF المحسّنة! اضغط على زر الطباعة لحفظ الملف كـ PDF', 'success');
};


// دوال للتحكم في تحديد كل الشركات
const selectAllCompanies = () => {
    const allCompanyIds = companies.map(company => company.CompanyID.toString());
    setFilters({ ...filters, selectedCompanies: allCompanyIds });
};

const unselectAllCompanies = () => {
    setFilters({ ...filters, selectedCompanies: [] });
};

// دوال للتحكم في تحديد كل المهن
const selectAllProfessions = () => {
    const allProfessionIds = professions.map(profession => profession.ProfessionID.toString());
    setFilters({ ...filters, selectedProfessions: allProfessionIds });
};

const unselectAllProfessions = () => {
    setFilters({ ...filters, selectedProfessions: [] });
};

// دوال للتحكم في تحديد كل مهن السفر
const selectAllTravelProfessions = () => {
    const allTravelProfessionIds = professions.map(profession => profession.ProfessionID.toString());
    setFilters({ ...filters, selectedTravelProfessions: allTravelProfessionIds });
};

const unselectAllTravelProfessions = () => {
    setFilters({ ...filters, selectedTravelProfessions: [] });
};




// دالة لاستخراج ملاحظة محددة
const getSpecificNote = (passport, noteIndex) => {
    try {
        if (passport.Notes && typeof passport.Notes === 'string') {
            const notesArray = JSON.parse(passport.Notes);
            if (Array.isArray(notesArray) && notesArray[noteIndex - 1]) {
                return notesArray[noteIndex - 1].text?.trim() || '';
            }
        }
        return '';
    } catch {
        return '';
    }
};

// دالة لاستخراج جميع الملاحظات
const getAllNotes = (passport) => {
    try {
        if (passport.Notes && typeof passport.Notes === 'string') {
            const notesArray = JSON.parse(passport.Notes);
            if (Array.isArray(notesArray)) {
                return notesArray
                    .filter(note => note.text && note.text.trim())
                    .map(note => note.text.trim())
                    .join(' | ');
            }
        }
        return passport.Notes || '';
    } catch {
        return passport.Notes || '';
    }
};

const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
    }
    setSortConfig({ key, direction });
};

// نفس الجوازات ولكن بعد الترتيب + حذف الأسماء المخفية مؤقتاً
const getWorkingPassports = () => {
    let sorted = getSortedPassports(selectedPassports || []);
    // تصفية الأسماء المخفية مؤقتاً
    sorted = sorted.filter(p => !tempHidden[p.PassportID]);
    
    // تطبيق فلتر مرحلة المعالجة
    if (filters.processingStageFilter) {
        sorted = sorted.filter(p => {
            switch (filters.processingStageFilter) {
                case 'لم يبصم':
                    return !p.FingerprintDate;
                case 'مبصم ولم يؤشر':
                    return p.FingerprintDate && !p.VisaDate;
                case 'مبصم ولم يسافر':
                    return p.FingerprintDate && !p.TravelDate;
            case 'مأشر ولم يسافر':
                    return p.VisaDate && !p.TravelDate;
                case 'لم يسلم أوراق':
                    return !p.DocumentsDeliveryStatus || p.DocumentsDeliveryStatus === 'لم يسلم';
                case 'تم التأمين الطبي':
                    return !!p.MedicalInsuranceDate;
                case 'لم يتم التأمين الطبي':
                    return !p.MedicalInsuranceDate;
                default:
                    return true;
            }
        });
    }
    
    return sorted;
};


const getSortedPassports = (passports) => {
    if (!sortConfig.key) return passports;

    return [...passports].sort((a, b) => {
        let aVal, bVal;
        
        switch (sortConfig.key) {
            case 'nameArabic':
                aVal = a.NameArabic || '';
                bVal = b.NameArabic || '';
                break;
            case 'nameEnglish':
                aVal = a.NameEnglish || '';
                bVal = b.NameEnglish || '';
                break;
            case 'passportNumber':
                aVal = a.PassportNumber || '';
                bVal = b.PassportNumber || '';
                break;
            case 'nationalId':
                aVal = a.NationalID || '';
                bVal = b.NationalID || '';
                break;
            case 'phoneNumbers':
                aVal = getMobilePhoneNumbers(a) || '';
                bVal = getMobilePhoneNumbers(b) || '';
                break;
            case 'passportId':
                aVal = parseInt(a.OfficeSerial ?? a.SerialNumber) || 0;
                bVal = parseInt(b.OfficeSerial ?? b.SerialNumber) || 0;
                break;
            case 'delegate':
                const delegateA = delegates.find(d => d.DelegateID?.toString() === a.DelegateID?.toString());
                const delegateB = delegates.find(d => d.DelegateID?.toString() === b.DelegateID?.toString());
                aVal = delegateA?.DelegateName || '';
                bVal = delegateB?.DelegateName || '';
                break;
            case 'company':
                const companyA = companies.find(c => c.CompanyID?.toString() === a.CompanyID?.toString());
                const companyB = companies.find(c => c.CompanyID?.toString() === b.CompanyID?.toString());
                aVal = companyA?.CompanyName || '';
                bVal = companyB?.CompanyName || '';
                break;
            case 'blacklistCompanies':
                aVal = a.BlacklistCompanies || '';
                bVal = b.BlacklistCompanies || '';
                break;
            case 'blacklistReasons':
                aVal = a.BlacklistReasons || '';
                bVal = b.BlacklistReasons || '';
                break;
            case 'passportProfession':
                aVal = getPassportProfessionName(a) || '';
                bVal = getPassportProfessionName(b) || '';
                break;
            case 'travelProfession':
                aVal = getTravelProfessionName(a) || '';
                bVal = getTravelProfessionName(b) || '';
                break;
            case 'medicalExamDate':
                aVal = a.MedicalExamDate ? new Date(a.MedicalExamDate).getTime() : 0;
                bVal = b.MedicalExamDate ? new Date(b.MedicalExamDate).getTime() : 0;
                break;
            case 'networkDate':
                aVal = a.NetworkDate ? new Date(a.NetworkDate).getTime() : 0;
                bVal = b.NetworkDate ? new Date(b.NetworkDate).getTime() : 0;
                break;
            case 'visaDate':
                aVal = a.VisaDate ? new Date(a.VisaDate).getTime() : 0;
                bVal = b.VisaDate ? new Date(b.VisaDate).getTime() : 0;
                break;
            case 'travelDate':
                aVal = a.TravelDate ? new Date(a.TravelDate).getTime() : 0;
                bVal = b.TravelDate ? new Date(b.TravelDate).getTime() : 0;
                break;
            case 'borderNumber':
                aVal = a.BorderNumber || '';
                bVal = b.BorderNumber || '';
                break;
            case 'violationNotes':
                aVal = a.ViolationNotes || '';
                bVal = b.ViolationNotes || '';
                break;
            case 'travelDuration':
                aVal = a.TravelDuration || '';
                bVal = b.TravelDuration || '';
                break;
            case 'passportStatus':
                aVal = a.PassportStatus || '';
                bVal = b.PassportStatus || '';
                break;
            case 'receiveStatus':
                aVal = a.PassportReceiveStatus || 'لم يستلم';
                bVal = b.PassportReceiveStatus || 'لم يستلم';
                break;
            case 'notes':
                aVal = getAllNotes(a);
                bVal = getAllNotes(b);
                break;
            case 'note1':
                aVal = getSpecificNote(a, 1);
                bVal = getSpecificNote(b, 1);
                break;
            case 'note2':
                aVal = getSpecificNote(a, 2);
                bVal = getSpecificNote(b, 2);
                break;
            case 'note3':
                aVal = getSpecificNote(a, 3);
                bVal = getSpecificNote(b, 3);
                break;
            case 'note4':
                aVal = getSpecificNote(a, 4);
                bVal = getSpecificNote(b, 4);
                break;
            case 'medicalExamStatus':
                aVal = a.MedicalExamStatus || '';
                bVal = b.MedicalExamStatus || '';
                break;
            case 'address':
                aVal = a.Address || '';
                bVal = b.Address || '';
                break;
            case 'governorate':
                aVal = a.Governorate || '';
                bVal = b.Governorate || '';
                break;
            case 'age':
                aVal = parseInt(a.Age) || 0;
                bVal = parseInt(b.Age) || 0;
                break;
            case 'birthDate':
                aVal = a.DateOfBirth ? new Date(a.DateOfBirth).getTime() : 0;
                bVal = b.DateOfBirth ? new Date(b.DateOfBirth).getTime() : 0;
                break;
            case 'militaryStatus':
                aVal = a.MilitaryStatus || '';
                bVal = b.MilitaryStatus || '';
                break;
            case 'passportIssueDate':
                aVal = a.PassportIssueDate ? new Date(a.PassportIssueDate).getTime() : 0;
                bVal = b.PassportIssueDate ? new Date(b.PassportIssueDate).getTime() : 0;
                break;
            case 'passportExpiryDate':
                aVal = a.PassportExpiryDate ? new Date(a.PassportExpiryDate).getTime() : 0;
                bVal = b.PassportExpiryDate ? new Date(b.PassportExpiryDate).getTime() : 0;
                break;
            case 'licenseType':
                aVal = a.LicenseType || '';
                bVal = b.LicenseType || '';
                break;
            case 'licenseExpiryDate':
                aVal = a.LicenseExpiryDate ? new Date(a.LicenseExpiryDate).getTime() : 0;
                bVal = b.LicenseExpiryDate ? new Date(b.LicenseExpiryDate).getTime() : 0;
                break;
            case 'abilityTest':
                aVal = a.AbilityTest || '';
                bVal = b.AbilityTest || '';
                break;
            case 'receiveDate':
                aVal = a.ReceiveDate ? new Date(a.ReceiveDate).getTime() : 0;
                bVal = b.ReceiveDate ? new Date(b.ReceiveDate).getTime() : 0;
                break;
            case 'receiverName':
                aVal = a.FriendName || '';
                bVal = b.FriendName || '';
                break;
            case 'receiverPhone':
                aVal = a.FriendPhone || '';
                bVal = b.FriendPhone || '';
                break;
            case 'previousCompanies':
                try {
                    const prevCompaniesA = JSON.parse(a.PreviousCompanies || '[]');
                    const prevCompaniesB = JSON.parse(b.PreviousCompanies || '[]');
                    aVal = Array.isArray(prevCompaniesA) ? prevCompaniesA.length : 0;
                    bVal = Array.isArray(prevCompaniesB) ? prevCompaniesB.length : 0;
                } catch {
                    aVal = 0;
                    bVal = 0;
                }
                break;
            case 'applicationNumber':
                aVal = a.ApplicationNumber || '';
                bVal = b.ApplicationNumber || '';
                break;
            case 'fingerprintDate':
                aVal = a.FingerprintDate ? new Date(a.FingerprintDate).getTime() : 0;
                bVal = b.FingerprintDate ? new Date(b.FingerprintDate).getTime() : 0;
                break;
            case 'consulateDate':
                aVal = a.ConsulateEntryDate ? new Date(a.ConsulateEntryDate).getTime() : 0;
                bVal = b.ConsulateEntryDate ? new Date(b.ConsulateEntryDate).getTime() : 0;
                break;
            case 'documentsDeliveryStatus':
                aVal = a.DocumentsDeliveryStatus || 'لم يسلم';
                bVal = b.DocumentsDeliveryStatus || 'لم يسلم';
                break;
                case 'medicalInsuranceDate':
    aVal = a.MedicalInsuranceDate ? new Date(a.MedicalInsuranceDate).getTime() : 0;
    bVal = b.MedicalInsuranceDate ? new Date(b.MedicalInsuranceDate).getTime() : 0;
    break;

            default:
                return 0;
        }

        // ترتيب الأرقام والتواريخ
        if (sortConfig.key === 'passportId' || sortConfig.key === 'age' || 
            sortConfig.key.includes('Date') || sortConfig.key === 'previousCompanies') {
            if (sortConfig.direction === 'asc') {
                return aVal - bVal;
            } else {
                return bVal - aVal;
            }
        }

        // ترتيب النصوص
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
        
        if (sortConfig.direction === 'asc') {
            return aVal.localeCompare(bVal, 'ar', { numeric: true, sensitivity: 'base' });
        } else {
            return bVal.localeCompare(aVal, 'ar', { numeric: true, sensitivity: 'base' });
        }
    });
};

// تعديل دالة getSortIcon
const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
        return <ChevronDown size={16} className="text-gray-400 rotate-0" />;
    }
    
    if (sortConfig.direction === 'asc') {
        return <ChevronDown size={16} className="text-blue-400 rotate-180" />;
    } else {
        return <ChevronDown size={16} className="text-blue-400 rotate-0" />;
    }
};


  const renderFilters = () => (
      <div className="space-y-6">
          {/* البحث النصي */}
          <div className="relative">
              <Search className="absolute right-3 top-3 text-gray-400" />
              <input
                  type="text"
                  placeholder="البحث بالاسم، رقم الجواز، الرقم القومي..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-2.5 px-10 text-base focus:ring-2 focus:ring-blue-500"
              />
          </div>

          {/* فلتر حالة النشاط */}
          {canShowFilter('إظهار حالة النشاط') && (<div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">حالة النشاط</label>
              <select
                  value={filters.activeStatus}
                  onChange={(e) => setFilters({ ...filters, activeStatus: e.target.value })}
                  className="w-full md:w-1/3 border border-gray-300 rounded-lg p-2.5 text-base focus:ring-2 focus:ring-blue-500"
              >
                  <option value="active">النشطة فقط</option>
                  <option value="all">عرض الكل</option>
                  <option value="archived">المؤرشفة فقط</option>
              </select>
          </div>)}

              {canShowAdvancedFiltersBox() && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3">🔧 فلاتر متقدمة</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

<div>
  {canShowFilter('إظهار القوائم المحفوظة') && (<>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    القوائم المحفوظة (يمكن اختيار أكثر من قائمة)
  </label>

  {/* أزرار التحكم */}
  <div className="flex gap-2 mb-2">
    <button
      type="button"
      onClick={() =>
        setFilters(prev => ({
          ...prev,
          listIds: savedLists.map(l => l.ListID.toString())
        }))
      }
      className="bg-orange-100 text-orange-600 px-3 py-1 rounded-lg hover:bg-orange-200 text-sm"
    >
      تحديد كل القوائم
    </button>

    <button
      type="button"
      onClick={() => setFilters(prev => ({ ...prev, listIds: [], includeArchivedHistory: false }))}
      className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-200 text-sm"
    >
      إلغاء التحديد
    </button>
  </div>

  {/* البحث داخل القوائم المحفوظة */}
  <div className="mb-2">
    <div className="relative">
      <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
      <input
        type="text"
        placeholder="ابحث في القوائم..."
        value={savedListSearchTerm}
        onChange={e => setSavedListSearchTerm(e.target.value)}
        className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
      {savedListSearchTerm && (
        <button
          type="button"
          onClick={() => setSavedListSearchTerm('')}
          className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
        >
          <X size={16} />
        </button>
      )}
    </div>
  </div>

  {/* قائمة القوائم المحفوظة (تشبه المناديب) */}
  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white">
    {savedLists
      .filter(list =>
        !savedListSearchTerm ||
        list.ListName.toLowerCase().includes(savedListSearchTerm.toLowerCase())
      )
      .map(list => (
        <label
          key={list.ListID}
          className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
        >
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={filters.listIds?.includes(list.ListID.toString()) || false}
            onChange={e => {
              const id = list.ListID.toString();
              const current = filters.listIds || [];
              const next = e.target.checked
                ? [...current, id]
                : current.filter(x => x !== id);
              setFilters(prev => ({
                ...prev,
                listIds: next,
                includeArchivedHistory: next.length > 0 ? prev.includeArchivedHistory : false
              }));
            }}
          />
          <span className="text-sm">{list.ListName}</span>
        </label>
      ))}
  </div>

  {/* استثناء عناصر القوائم المختارة */}
  {canShowFilter('إظهار استثناء عناصر القوائم المختارة', ['إظهار القوائم المحفوظة']) && (
  <label className="inline-flex items-center gap-2 mt-2 text-xs text-gray-700">
    <input
      type="checkbox"
      checked={filters.excludeList || false}
      onChange={e =>
        setFilters(prev => ({
          ...prev,
          excludeList: e.target.checked,
          includeArchivedHistory: e.target.checked ? false : prev.includeArchivedHistory
        }))
      }
    />
    <span>استثناء عناصر القوائم المختارة</span>
  </label>
  )}

  {/* عرض الأرشيف السابق لنفس أشخاص القوائم المختارة */}
  {canShowFilter('إظهار عرض البيانات المؤرشفة للقوائم') && (
  <label
    className={`flex items-start gap-2 mt-2 p-2 rounded-lg border text-xs ${
      (filters.listIds?.length > 0 && !filters.excludeList)
        ? 'bg-amber-50 border-amber-200 text-amber-800 cursor-pointer'
        : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
    }`}
    title="يعرض السجلات المؤرشفة السابقة المطابقة لنفس الرقم القومي أو رقم الجواز لأشخاص القوائم المختارة"
  >
    <input
      type="checkbox"
      className="mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
      disabled={!(filters.listIds?.length > 0) || filters.excludeList}
      checked={!!filters.includeArchivedHistory && (filters.listIds?.length > 0) && !filters.excludeList}
      onChange={e =>
        setFilters(prev => ({ ...prev, includeArchivedHistory: e.target.checked }))
      }
    />
    <span>
      <span className="font-semibold">عرض البيانات المؤرشفة السابقة لنفس الأشخاص</span>
      <span className="block mt-0.5 text-[11px] leading-relaxed">
        يظهر الأشخاص الحاليين في القائمة، ومعهم أي سجلات مؤرشفة مطابقة لهم بالرقم القومي أو رقم الجواز عند تفعيل الاختيار فقط.
      </span>
    </span>
  </label>
  )}
  </>)}
</div>



                 <div>
    {canShowFilter('إظهار حالة التسليم') && (<>
    <label className="block text-sm font-medium text-gray-700 mb-1">حالة التسليم</label>
    <select
        value={filters.deliveryFilter}
        onChange={(e) => setFilters({ ...filters, deliveryFilter: e.target.value })}
        className="w-full border border-gray-300 rounded-lg p-2.5 text-base focus:ring-2 focus:ring-blue-500"
    >
        {deliveryStatuses.map(status => (
            <option key={status} value={status}>{status}</option>
        ))}
    </select>
    </>)}

    {/* حالة المعالجة تحت حالة التسليم في نفس العمود */}
    {canShowFilter('إظهار حالة المعالجة') && (<div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">حالة المعالجة</label>
        <select
            value={filters.processFilter}
            onChange={(e) => setFilters({ ...filters, processFilter: e.target.value })}
            className="w-full border border-gray-300 rounded-lg p-2.5 text-base focus:ring-2 focus:ring-blue-500"
        >
            {processStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
            ))}
        </select>
    </div>)}

    {/* فلتر مرحلة المعالجة الجديد */}
    {canShowFilter('إظهار مرحلة المعالجة') && (<div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">🔍 مرحلة المعالجة</label>
        <select
            value={filters.processingStageFilter}
            onChange={(e) => setFilters({ ...filters, processingStageFilter: e.target.value })}
            className="w-full border border-gray-300 rounded-lg p-2.5 text-base focus:ring-2 focus:ring-blue-500"
        >
            <option value="">عرض الكل</option>
            <option value="لم يبصم">لم يبصم (بدون تاريخ بصمة)</option>
            <option value="مبصم ولم يؤشر">مبصم ولم يؤشر (لم يتم التأشير)</option>
            <option value="مبصم ولم يسافر">مبصم ولم يسافر (بدون تاريخ سفر)</option>
        <option value="مأشر ولم يسافر">مأشر ولم يسافر (تأشيرة بدون سفر)</option>
            <option value="لم يسلم أوراق">لم يسلم أوراق (حالة التسليم: لم يسلم)</option>
            <option value="تم التأمين الطبي">تم التأمين الطبي ✅ (يوجد تاريخ تأمين)</option>
            <option value="لم يتم التأمين الطبي">لم يتم التأمين الطبي ❌ (بدون تاريخ تأمين)</option>
        </select>
        {filters.processingStageFilter && (
            <div className="mt-1 flex items-center gap-1">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    filters.processingStageFilter === 'لم يبصم' ? 'bg-red-100 text-red-700' :
                    filters.processingStageFilter === 'مبصم ولم يؤشر' ? 'bg-yellow-100 text-yellow-700' :
                 filters.processingStageFilter === 'مبصم ولم يسافر' ? 'bg-orange-100 text-orange-700' :
                    filters.processingStageFilter === 'لم يسلم أوراق' ? 'bg-pink-100 text-pink-700' :
                    filters.processingStageFilter === 'تم التأمين الطبي' ? 'bg-green-100 text-green-700' :
                    filters.processingStageFilter === 'لم يتم التأمين الطبي' ? 'bg-red-100 text-red-700' :
                    'bg-purple-100 text-purple-700'
                }`}>
                    🔍 {filters.processingStageFilter}
                </span>
                <button
                    type="button"
                    onClick={() => setFilters({ ...filters, processingStageFilter: '' })}
                    className="text-gray-400 hover:text-gray-600"
                >
                    <X size={14} />
                </button>
            </div>
        )}
    </div>)}

                  </div>
                 </div>
              </div>
              )}



          {/* الصف الأول من الفلاتر */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {canShowFilter('إظهار فلتر المناديب') && (<div>
    <label className="block text-sm font-medium text-gray-700 mb-2">المناديب (يمكن اختيار أكثر من مندوب)</label>
    
    {/* أزرار التحكم */}
    <div className="flex gap-2 mb-2">
        <button
            onClick={() => {
                const allDelegateIds = delegates.map(delegate => delegate.DelegateID.toString());
                setFilters({ ...filters, selectedDelegates: allDelegateIds });
            }}
            className="bg-orange-100 text-orange-600 px-3 py-1 rounded-lg hover:bg-orange-200 text-sm flex items-center gap-1"
        >
            <Check className="w-4 h-4" />
            تحديد الكل
        </button>
        <button
            onClick={() => setFilters({ ...filters, selectedDelegates: [] })}
            className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
        >
            <X className="w-4 h-4" />
            إلغاء تحديد الكل
        </button>
    </div>

    {/* صندوق البحث للمناديب */}
    <div className="mb-2">
        <div className="relative">
            <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
            <input
                type="text"
                placeholder="ابحث عن مندوب..."
                value={delegateSearchTerm || ''}
                onChange={(e) => setDelegateSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {delegateSearchTerm && (
                <button
                    onClick={() => setDelegateSearchTerm('')}
                    className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    </div>

    {/* قائمة المناديب المُفلترة */}
    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white">
        {delegates
            .filter(delegate => 
                !delegateSearchTerm || 
                delegate.DelegateName.toLowerCase().includes(delegateSearchTerm.toLowerCase())
            )
            .map(delegate => (
                <label key={delegate.DelegateID} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filters.selectedDelegates?.includes(delegate.DelegateID.toString()) || false}
                        onChange={(e) => {
                            const currentSelected = filters.selectedDelegates || [];
                            const newSelected = e.target.checked
                                ? [...currentSelected, delegate.DelegateID.toString()]
                                : currentSelected.filter(id => id !== delegate.DelegateID.toString());
                            setFilters({ ...filters, selectedDelegates: newSelected });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{delegate.DelegateName}</span>
                </label>
            ))
        }
        
        {/* رسالة عدم وجود نتائج للمناديب */}
        {delegateSearchTerm && delegates.filter(delegate => 
            delegate.DelegateName.toLowerCase().includes(delegateSearchTerm.toLowerCase())
        ).length === 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
                <Search size={20} className="mx-auto mb-2 opacity-50" />
                لا توجد مناديب تطابق البحث "{delegateSearchTerm}"
            </div>
        )}
    </div>

    {/* معلومات المناديب المحددة */}
    {filters.selectedDelegates?.length > 0 && (
        <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between">
                <span className="text-xs text-orange-700 font-medium">
                    محدد: {filters.selectedDelegates.length} مندوب
                </span>
                <button
                    onClick={() => setFilters({ ...filters, selectedDelegates: [] })}
                    className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                >
                    <X size={12} />
                    مسح الكل
                </button>
            </div>
            
            {/* عرض أسماء المناديب المحددة */}
            <div className="mt-2 flex flex-wrap gap-1">
                {filters.selectedDelegates.slice(0, 3).map(delegateId => {
                    const delegate = delegates.find(d => d.DelegateID.toString() === delegateId);
                    return delegate ? (
                        <span key={delegateId} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                            {delegate.DelegateName}
                            <button
                                onClick={() => {
                                    const newSelected = filters.selectedDelegates.filter(id => id !== delegateId);
                                    setFilters({ ...filters, selectedDelegates: newSelected });
                                }}
                                className="hover:text-orange-600"
                            >
                                <X size={10} />
                            </button>
                        </span>
                    ) : null;
                })}
                {filters.selectedDelegates.length > 3 && (
                    <span className="text-xs text-orange-600 px-2 py-1">
                        +{filters.selectedDelegates.length - 3} أخرى
                    </span>
                )}
            </div>
        </div>
    )}
</div>)}

              {canShowFilter('إظهار فلتر الشركات') && (<div>
    <label className="block text-sm font-medium text-gray-700 mb-2">الشركات (يمكن اختيار أكثر من شركة)</label>
    
    {/* أزرار التحكم */}
    <div className="flex gap-2 mb-2">
        <button
            onClick={selectAllCompanies}
            className="bg-blue-100 text-blue-600 px-3 py-1 rounded-lg hover:bg-blue-200 text-sm flex items-center gap-1"
        >
            <Check className="w-4 h-4" />
            تحديد الكل
        </button>
        <button
            onClick={unselectAllCompanies}
            className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
        >
            <X className="w-4 h-4" />
            إلغاء تحديد الكل
        </button>
    </div>

    {/* صندوق البحث */}
    <div className="mb-2">
        <div className="relative">
            <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
            <input
                type="text"
                placeholder="ابحث عن شركة..."
                value={companySearchTerm || ''}
                onChange={(e) => setCompanySearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {companySearchTerm && (
                <button
                    onClick={() => setCompanySearchTerm('')}
                    className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    </div>

    {/* قائمة الشركات المفلترة */}
    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white">
        {companies
            .filter(company => 
                !companySearchTerm || 
                company.CompanyName.toLowerCase().includes(companySearchTerm.toLowerCase())
            )
            .map(company => (
                <label key={company.CompanyID} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filters.selectedCompanies.includes(company.CompanyID.toString())}
                        onChange={(e) => {
                            const newSelected = e.target.checked
                                ? [...filters.selectedCompanies, company.CompanyID.toString()]
                                : filters.selectedCompanies.filter(id => id !== company.CompanyID.toString());
                            setFilters({ ...filters, selectedCompanies: newSelected });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{company.CompanyName}</span>
                </label>
            ))
        }
        
        {/* رسالة عدم وجود نتائج */}
        {companySearchTerm && companies.filter(company => 
            company.CompanyName.toLowerCase().includes(companySearchTerm.toLowerCase())
        ).length === 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
                <Search size={20} className="mx-auto mb-2 opacity-50" />
                لا توجد شركات تطابق البحث "{companySearchTerm}"
            </div>
        )}
    </div>

    {/* معلومات الشركات المحددة */}
    {filters.selectedCompanies.length > 0 && (
        <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
                <span className="text-xs text-blue-700 font-medium">
                    محدد: {filters.selectedCompanies.length} شركة
                </span>
                <button
                    onClick={() => setFilters({ ...filters, selectedCompanies: [] })}
                    className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                >
                    <X size={12} />
                    مسح الكل
                </button>
            </div>
            
            {/* عرض أسماء الشركات المحددة */}
            <div className="mt-2 flex flex-wrap gap-1">
                {filters.selectedCompanies.slice(0, 3).map(companyId => {
                    const company = companies.find(c => c.CompanyID.toString() === companyId);
                    return company ? (
                        <span key={companyId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {company.CompanyName}
                            <button
                                onClick={() => {
                                    const newSelected = filters.selectedCompanies.filter(id => id !== companyId);
                                    setFilters({ ...filters, selectedCompanies: newSelected });
                                }}
                                className="hover:text-blue-600"
                            >
                                <X size={10} />
                            </button>
                        </span>
                    ) : null;
                })}
                {filters.selectedCompanies.length > 3 && (
                    <span className="text-xs text-blue-600 px-2 py-1">
                        +{filters.selectedCompanies.length - 3} أخرى
                    </span>
                )}
            </div>
        </div>
    )}
</div>)}

{canShowFilter('إظهار فلتر المهن بالجواز') && (<div>
    <label className="block text-sm font-medium text-gray-700 mb-2">المهن بالجواز (يمكن اختيار أكثر من مهنة)</label>
    
    {/* أزرار التحكم */}
    <div className="flex gap-2 mb-2">
        <button
            onClick={selectAllProfessions}
            className="bg-green-100 text-green-600 px-3 py-1 rounded-lg hover:bg-green-200 text-sm flex items-center gap-1"
        >
            <Check className="w-4 h-4" />
            تحديد الكل
        </button>
        <button
            onClick={unselectAllProfessions}
            className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
        >
            <X className="w-4 h-4" />
            إلغاء تحديد الكل
        </button>
    </div>

    {/* صندوق البحث للمهن */}
    <div className="mb-2">
        <div className="relative">
            <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
            <input
                type="text"
                placeholder="ابحث عن مهنة..."
                value={professionSearchTerm || ''}
                onChange={(e) => setProfessionSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {professionSearchTerm && (
                <button
                    onClick={() => setProfessionSearchTerm('')}
                    className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    </div>

    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white">
        {professions
            .filter(profession => 
                !professionSearchTerm || 
                profession.ProfessionName.toLowerCase().includes(professionSearchTerm.toLowerCase())
            )
            .map(profession => (
                <label key={profession.ProfessionID} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filters.selectedProfessions.includes(profession.ProfessionID.toString())}
                        onChange={(e) => {
                            const newSelected = e.target.checked
                                ? [...filters.selectedProfessions, profession.ProfessionID.toString()]
                                : filters.selectedProfessions.filter(id => id !== profession.ProfessionID.toString());
                            setFilters({ ...filters, selectedProfessions: newSelected });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{profession.ProfessionName}</span>
                </label>
            ))
        }
        
        {/* رسالة عدم وجود نتائج للمهن */}
        {professionSearchTerm && professions.filter(profession => 
            profession.ProfessionName.toLowerCase().includes(professionSearchTerm.toLowerCase())
        ).length === 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
                <Search size={20} className="mx-auto mb-2 opacity-50" />
                لا توجد مهن تطابق البحث "{professionSearchTerm}"
            </div>
        )}
    </div>

    {filters.selectedProfessions.length > 0 && (
        <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
                <span className="text-xs text-green-700 font-medium">
                    محدد: {filters.selectedProfessions.length} مهنة
                </span>
                <button
                    onClick={() => setFilters({ ...filters, selectedProfessions: [] })}
                    className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                >
                    <X size={12} />
                    مسح الكل
                </button>
            </div>
        </div>
    )}
</div>)}
          </div>

          {/* الصف الثاني من الفلاتر */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {canShowFilter('إظهار فلتر مهن السفر') && (<div>
    <label className="block text-sm font-medium text-gray-700 mb-2">مهن السفر (يمكن اختيار أكثر من مهنة)</label>
    
    {/* أزرار التحكم */}
    <div className="flex gap-2 mb-2">
        <button
            onClick={selectAllTravelProfessions}
            className="bg-purple-100 text-purple-600 px-3 py-1 rounded-lg hover:bg-purple-200 text-sm flex items-center gap-1"
        >
            <Check className="w-4 h-4" />
            تحديد الكل
        </button>
        <button
            onClick={unselectAllTravelProfessions}
            className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
        >
            <X className="w-4 h-4" />
            إلغاء تحديد الكل
        </button>
    </div>

    {/* صندوق البحث لمهن السفر */}
    <div className="mb-2">
        <div className="relative">
            <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
            <input
                type="text"
                placeholder="ابحث عن مهنة سفر..."
                value={travelProfessionSearchTerm || ''}
                onChange={(e) => setTravelProfessionSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {travelProfessionSearchTerm && (
                <button
                    onClick={() => setTravelProfessionSearchTerm('')}
                    className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    </div>

    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white">
        {professions
            .filter(profession => 
                !travelProfessionSearchTerm || 
                profession.ProfessionName.toLowerCase().includes(travelProfessionSearchTerm.toLowerCase())
            )
            .map(profession => (
                <label key={profession.ProfessionID} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filters.selectedTravelProfessions.includes(profession.ProfessionID.toString())}
                        onChange={(e) => {
                            const newSelected = e.target.checked
                                ? [...filters.selectedTravelProfessions, profession.ProfessionID.toString()]
                                : filters.selectedTravelProfessions.filter(id => id !== profession.ProfessionID.toString());
                            setFilters({ ...filters, selectedTravelProfessions: newSelected });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{profession.ProfessionName}</span>
                </label>
            ))
        }
        
        {/* رسالة عدم وجود نتائج لمهن السفر */}
        {travelProfessionSearchTerm && professions.filter(profession => 
            profession.ProfessionName.toLowerCase().includes(travelProfessionSearchTerm.toLowerCase())
        ).length === 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
                <Search size={20} className="mx-auto mb-2 opacity-50" />
                لا توجد مهن تطابق البحث "{travelProfessionSearchTerm}"
            </div>
        )}
    </div>

    {filters.selectedTravelProfessions.length > 0 && (
        <div className="mt-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between">
                <span className="text-xs text-purple-700 font-medium">
                    محدد: {filters.selectedTravelProfessions.length} مهنة
                </span>
                <button
                    onClick={() => setFilters({ ...filters, selectedTravelProfessions: [] })}
                    className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                >
                    <X size={12} />
                    مسح الكل
                </button>
            </div>
        </div>
    )}
</div>)}

              {canShowFilter('إظهار فلتر مدة السفر') && (<div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">مدة السفر</label>
                  <select
                      value={filters.travelDuration}
                      onChange={(e) => setFilters({ ...filters, travelDuration: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-base focus:ring-2 focus:ring-blue-500"
                  >
                      <option value="">كل مدد السفر</option>
                      {travelDurations.map(duration => (
                          <option key={duration} value={duration}>{duration}</option>
                      ))}
                  </select>
              </div>)}

              {canShowFilter('إظهار فلتر حالة الجواز') && (<div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">حالة الجواز</label>
                  <select
                      value={filters.passportStatus}
                      onChange={(e) => setFilters({ ...filters, passportStatus: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-base focus:ring-2 focus:ring-blue-500"
                  >
                      <option value="">كل حالات الجواز</option>
                      {passportStatuses.map(status => (
                          <option key={status} value={status}>{status}</option>
                      ))}
                  </select>
              </div>)}
          </div>

          {/* الشركات السابقة - مُسترجع */}
          {canShowFilter('إظهار فلتر الشركات السابقة') && (<div>
              <label className="block text-sm font-medium text-gray-700 mb-2">الشركات السابقة</label>
              {/* ✅ حقل البحث */}
              <div className="relative mb-2">
                  <Search size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                      type="text"
                      placeholder="ابحث عن شركة سابقة..."
                      value={prevCompanySearchTerm}
                      onChange={(e) => setPrevCompanySearchTerm(e.target.value)}
                      className="w-full pr-7 pl-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      dir="rtl"
                  />
                  {prevCompanySearchTerm && (
                      <button
                          onClick={() => setPrevCompanySearchTerm('')}
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                      >
                          <X size={13} />
                      </button>
                  )}
              </div>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white">
                  {companies
                      .filter(company =>
                          !prevCompanySearchTerm ||
                          company.CompanyName.toLowerCase().includes(prevCompanySearchTerm.toLowerCase())
                      )
                      .map(company => (
                          <label key={company.CompanyID} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                              <input
                                  type="checkbox"
                                  checked={filters.previousCompanies.includes(company.CompanyID.toString())}
                                  onChange={(e) => {
                                      const newPrevCompanies = e.target.checked
                                          ? [...filters.previousCompanies, company.CompanyID.toString()]
                                          : filters.previousCompanies.filter(id => id !== company.CompanyID.toString());
                                      setFilters({ ...filters, previousCompanies: newPrevCompanies });
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm">{company.CompanyName}</span>
                          </label>
                      ))}
                  {companies.filter(c =>
                      !prevCompanySearchTerm ||
                      c.CompanyName.toLowerCase().includes(prevCompanySearchTerm.toLowerCase())
                  ).length === 0 && (
                      <p className="text-xs text-gray-400 w-full text-center py-2">لا توجد نتائج</p>
                  )}
              </div>
              {filters.previousCompanies.length > 0 && (
                  <div className="mt-2 text-xs text-blue-600">
                      محدد: {filters.previousCompanies.length} شركة سابقة
                      <button
                          onClick={() => setFilters({ ...filters, previousCompanies: [] })}
                          className="mr-2 text-red-500 hover:text-red-700"
                      >
                          مسح الكل
                      </button>
                  </div>
              )}
          </div>)}

          {/* فلاتر التواريخ */}
        {canShowAnyDateFilters() && (<div className="grid grid-cols-1 md:grid-cols-8 gap-3 md:gap-4">
              {canShowFilter('إظهار فلتر تاريخ الإضافة', ['إظهار فلتر التواريخ']) && (
              <div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700">تاريخ الإضافة</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.dateRange.from}
                          onChange={(e) => setFilters({
                              ...filters,
                              dateRange: { ...filters.dateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                          type="date"
                          value={filters.dateRange.to}
                          onChange={(e) => setFilters({
                              ...filters,
                              dateRange: { ...filters.dateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                  </div>
              </div>
              )}

              {canShowFilter('إظهار فلتر تاريخ الكشف الطبي', ['إظهار فلتر التواريخ']) && (
              <div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700">تاريخ الكشف الطبي</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.medicalDateRange.from}
                          onChange={(e) => setFilters({
                              ...filters,
                              medicalDateRange: { ...filters.medicalDateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                          type="date"
                          value={filters.medicalDateRange.to}
                          onChange={(e) => setFilters({
                              ...filters,
                              medicalDateRange: { ...filters.medicalDateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                  </div>
              </div>
              )}

              {canShowFilter('إظهار فلتر تاريخ عمل النت', ['إظهار فلتر التواريخ']) && (
              <div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700">تاريخ عمل النت</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.networkDateRange.from}
                          onChange={(e) => setFilters({
                              ...filters,
                              networkDateRange: { ...filters.networkDateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                          type="date"
                          value={filters.networkDateRange.to}
                          onChange={(e) => setFilters({
                              ...filters,
                              networkDateRange: { ...filters.networkDateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                  </div>
              </div>
              )}

              {canShowFilter('إظهار فلتر تاريخ البصمة', ['إظهار فلتر التواريخ']) && (
              <div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Fingerprint size={14} className="text-purple-600" />
                      تاريخ البصمة
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.fingerprintDateRange?.from || ''}
                          onChange={(e) => setFilters({
                              ...filters,
                              fingerprintDateRange: { ...filters.fingerprintDateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-purple-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                          type="date"
                          value={filters.fingerprintDateRange?.to || ''}
                          onChange={(e) => setFilters({
                              ...filters,
                              fingerprintDateRange: { ...filters.fingerprintDateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-purple-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-purple-500"
                      />
                  </div>
              </div>
              )}


{canShowFilter('إظهار فلتر تاريخ التأمين الطبي', ['إظهار فلتر التواريخ']) && (
<div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                      <span>🏥</span>
                      تاريخ التأمين الطبي
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.insuranceDateRange?.from || ''}
                          onChange={(e) => setFilters({
                              ...filters,
                              insuranceDateRange: { ...filters.insuranceDateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-green-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-green-500"
                      />
                      <input
                          type="date"
                          value={filters.insuranceDateRange?.to || ''}
                          onChange={(e) => setFilters({
                              ...filters,
                              insuranceDateRange: { ...filters.insuranceDateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-green-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-green-500"
                      />
                  </div>
              </div>
              )}

              {canShowFilter('إظهار فلتر تاريخ دخول القنصلية', ['إظهار فلتر التواريخ']) && (
              <div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Building size={14} className="text-indigo-600" />
                      تاريخ دخول القنصلية
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.consulateDateRange?.from || ''}
                          onChange={(e) => setFilters({
                              ...filters,
                              consulateDateRange: { ...filters.consulateDateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-indigo-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                          type="date"
                          value={filters.consulateDateRange?.to || ''}
                          onChange={(e) => setFilters({
                              ...filters,
                              consulateDateRange: { ...filters.consulateDateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-indigo-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                      />
                  </div>
              </div>
              )}

              {canShowFilter('إظهار فلتر تاريخ التأشير', ['إظهار فلتر التواريخ']) && (
              <div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700">تاريخ التأشير</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.visaDateRange.from}
                          onChange={(e) => setFilters({
                              ...filters,
                              visaDateRange: { ...filters.visaDateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                          type="date"
                          value={filters.visaDateRange.to}
                          onChange={(e) => setFilters({
                              ...filters,
                              visaDateRange: { ...filters.visaDateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                  </div>
              </div>
              )}

              {canShowFilter('إظهار فلتر تاريخ السفر', ['إظهار فلتر التواريخ']) && (
              <div className="space-y-2 max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:p-3 max-md:shadow-sm">
                  <label className="block text-sm font-medium text-gray-700">تاريخ السفر</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                          type="date"
                          value={filters.travelDateRange.from}
                          onChange={(e) => setFilters({
                              ...filters,
                              travelDateRange: { ...filters.travelDateRange, from: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                          type="date"
                          value={filters.travelDateRange.to}
                          onChange={(e) => setFilters({
                              ...filters,
                              travelDateRange: { ...filters.travelDateRange, to: e.target.value }
                          })}
                          className="w-full min-w-0 bg-white border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                  </div>
                  </div>
              )}
          </div>)}

          {/* فلاتر الملاحظات */}
          {canShowAnyNotesFilters() && (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mt-6">
              <h4 className="text-sm font-semibold text-yellow-800 mb-3">📝 فلاتر الملاحظات</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* فلتر جميع الملاحظات */}
                  {canShowFilter('إظهار فلتر جميع الملاحظات', ['إظهار فلاتر الملاحظات']) && (
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">البحث في جميع الملاحظات</label>
                      <div className="relative">
                          <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
                          <input
                              type="text"
                              placeholder="ابحث في جميع الملاحظات..."
                              value={noteFilters.allNotes || ''}
                              onChange={(e) => setNoteFilters({ ...noteFilters, allNotes: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          {noteFilters.allNotes && (
                              <button
                                  onClick={() => setNoteFilters({ ...noteFilters, allNotes: '' })}
                                  className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                              >
                                  <X size={16} />
                              </button>
                          )}
                      </div>
                  </div>
                  )}

                  {/* فلتر ملاحظة 1 */}
                  {canShowFilter('إظهار فلتر ملاحظة 1', ['إظهار فلاتر الملاحظات']) && (
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">البحث في ملاحظة 1</label>
                      <div className="relative">
                          <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
                          <input
                              type="text"
                              placeholder="ابحث في ملاحظة 1..."
                              value={noteFilters.note1 || ''}
                              onChange={(e) => setNoteFilters({ ...noteFilters, note1: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                          {noteFilters.note1 && (
                              <button
                                  onClick={() => setNoteFilters({ ...noteFilters, note1: '' })}
                                  className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                              >
                                  <X size={16} />
                              </button>
                          )}
                      </div>
                  </div>
                  )}

                  {/* فلتر ملاحظة 2 */}
                  {canShowFilter('إظهار فلتر ملاحظة 2', ['إظهار فلاتر الملاحظات']) && (
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">البحث في ملاحظة 2</label>
                      <div className="relative">
                          <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
                          <input
                              type="text"
                              placeholder="ابحث في ملاحظة 2..."
                              value={noteFilters.note2 || ''}
                              onChange={(e) => setNoteFilters({ ...noteFilters, note2: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          {noteFilters.note2 && (
                              <button
                                  onClick={() => setNoteFilters({ ...noteFilters, note2: '' })}
                                  className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                              >
                                  <X size={16} />
                              </button>
                          )}
                      </div>
                  </div>
                  )}

                  {/* فلتر ملاحظة 3 */}
                  {canShowFilter('إظهار فلتر ملاحظة 3', ['إظهار فلاتر الملاحظات']) && (
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">البحث في ملاحظة 3</label>
                      <div className="relative">
                          <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
                          <input
                              type="text"
                              placeholder="ابحث في ملاحظة 3..."
                              value={noteFilters.note3 || ''}
                              onChange={(e) => setNoteFilters({ ...noteFilters, note3: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                          {noteFilters.note3 && (
                              <button
                                  onClick={() => setNoteFilters({ ...noteFilters, note3: '' })}
                                  className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                              >
                                  <X size={16} />
                              </button>
                          )}
                      </div>
                  </div>
                  )}

                  {/* فلتر ملاحظة 4 */}
                  {canShowFilter('إظهار فلتر ملاحظة 4', ['إظهار فلاتر الملاحظات']) && (
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">البحث في ملاحظة 4</label>
                      <div className="relative">
                          <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
                          <input
                              type="text"
                              placeholder="ابحث في ملاحظة 4..."
                              value={noteFilters.note4 || ''}
                              onChange={(e) => setNoteFilters({ ...noteFilters, note4: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          />
                          {noteFilters.note4 && (
                              <button
                                  onClick={() => setNoteFilters({ ...noteFilters, note4: '' })}
                                  className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                              >
                                  <X size={16} />
                              </button>
                          )}
                      </div>
                  </div>
                  )}
              </div>

              {/* عرض الفلاتر النشطة */}
              {(noteFilters.allNotes || noteFilters.note1 || noteFilters.note2 || noteFilters.note3 || noteFilters.note4) && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-yellow-300">
                      <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-yellow-700">الفلاتر النشطة:</span>
                          <button
                              onClick={() => setNoteFilters({ allNotes: '', note1: '', note2: '', note3: '', note4: '' })}
                              className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                          >
                              <X size={12} />
                              مسح كل فلاتر الملاحظات
                          </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                          {noteFilters.allNotes && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                  كل الملاحظات: "{noteFilters.allNotes}"
                                  <button
                                      onClick={() => setNoteFilters({ ...noteFilters, allNotes: '' })}
                                      className="hover:text-blue-600"
                                  >
                                      <X size={10} />
                                  </button>
                              </span>
                          )}
                          {noteFilters.note1 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  ملاحظة 1: "{noteFilters.note1}"
                                  <button
                                      onClick={() => setNoteFilters({ ...noteFilters, note1: '' })}
                                      className="hover:text-green-600"
                                  >
                                      <X size={10} />
                                  </button>
                              </span>
                          )}
                          {noteFilters.note2 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                  ملاحظة 2: "{noteFilters.note2}"
                                  <button
                                      onClick={() => setNoteFilters({ ...noteFilters, note2: '' })}
                                      className="hover:text-purple-600"
                                  >
                                      <X size={10} />
                                  </button>
                              </span>
                          )}
                          {noteFilters.note3 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                                  ملاحظة 3: "{noteFilters.note3}"
                                  <button
                                      onClick={() => setNoteFilters({ ...noteFilters, note3: '' })}
                                      className="hover:text-orange-600"
                                  >
                                      <X size={10} />
                                  </button>
                              </span>
                          )}
                          {noteFilters.note4 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                  ملاحظة 4: "{noteFilters.note4}"
                                  <button
                                      onClick={() => setNoteFilters({ ...noteFilters, note4: '' })}
                                      className="hover:text-red-600"
                                  >
                                      <X size={10} />
                                  </button>
                              </span>
                          )}
                      </div>
                  </div>
              )}
          </div>
          )}
      </div>
  );


  const filteredSavedLists = savedLists.filter(list =>
  !savedListSearchTerm ||
  list.ListName.toLowerCase().includes(savedListSearchTerm.toLowerCase())
);




  const renderMobileColumnsSheet = () => {
      if (!showExportColumns) return null;
      return (
          <div className="md:hidden fixed inset-0 z-[70] bg-black/50 flex items-end" dir="rtl" onClick={(e) => { if (e.target === e.currentTarget) setShowExportColumns(false); }}>
              <div className="mobile-report-bottom-sheet bg-white w-full rounded-t-3xl shadow-2xl max-h-[88dvh] flex flex-col overflow-hidden">
                  <div className="bg-gradient-to-l from-slate-900 to-blue-900 text-white px-4 py-4 flex items-center justify-between">
                      <div>
                          <h3 className="text-lg font-bold">تخصيص الأعمدة</h3>
                          <p className="text-xs text-white/70 mt-1">اختار البيانات التي تظهر في كروت كشف الجوازات والتصدير</p>
                      </div>
                      <button onClick={() => setShowExportColumns(false)} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                          <X size={22} />
                      </button>
                  </div>

                  <div className="px-4 py-3 border-b bg-gray-50 grid grid-cols-2 gap-2">
                      <button onClick={selectAllColumns} className="bg-blue-600 text-white rounded-xl py-2.5 text-sm font-bold flex items-center justify-center gap-2">
                          <Check size={16} /> تحديد الكل
                      </button>
                      <button onClick={unselectAllColumns} className="bg-white text-gray-700 border border-gray-200 rounded-xl py-2.5 text-sm font-bold flex items-center justify-center gap-2">
                          <X size={16} /> إلغاء الكل
                      </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                      {Object.entries(exportColumns).map(([key, value]) => (
                          <label key={key} className={`flex items-center justify-between gap-3 p-3 rounded-2xl border cursor-pointer select-none ${value ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                              <span className={`text-sm font-semibold ${value ? 'text-blue-800' : 'text-gray-700'}`}>{getColumnLabel(key)}</span>
                              <input
                                  type="checkbox"
                                  checked={value}
                                  onChange={() => {
                                      const newValue = !value;
                                      setExportColumns({...exportColumns, [key]: newValue});
                                      setVisibleColumns({...visibleColumns, [key]: newValue});
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                              />
                          </label>
                      ))}

                      <label className="flex items-start justify-between gap-3 p-3 rounded-2xl border border-yellow-200 bg-yellow-50 cursor-pointer select-none">
                          <span className="text-sm font-bold text-yellow-800 leading-relaxed">🙈 إخفاء الملاحظات الأصلية من قاعدة البيانات</span>
                          <input
                              type="checkbox"
                              checked={hideOriginalNotes}
                              onChange={(e) => setHideOriginalNotes(e.target.checked)}
                              className="mt-0.5 w-5 h-5 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                          />
                      </label>
                  </div>

                  <div className="p-4 border-t bg-white">
                      <button onClick={() => setShowExportColumns(false)} className="w-full bg-slate-900 text-white rounded-2xl py-3 font-bold">
                          تم
                      </button>
                  </div>
              </div>
          </div>
      );
  };

  const openWorkListPeoplePicker = () => {
      const rows = getWorkingPassports();
      if (!hasSearched || !rows.length) {
          showToast('اعمل بحث أولاً وتأكد أن هناك نتائج معروضة', 'warning');
          return;
      }
      setWorkListPeopleSearch('');
      setWorkListPeoplePage(1);
      setWorkListSelectedPassportIds(new Set());
      setShowWorkListPeopleModal(true);
  };

  const workListPeopleRows = getWorkingPassports().filter(passport => {
      const q = String(workListPeopleSearch || '').trim().toLowerCase();
      if (!q) return true;
      const phoneText = (() => {
          try {
              if (Array.isArray(passport?.PhoneNumbers)) return passport.PhoneNumbers.join(' ');
              const parsed = JSON.parse(passport?.PhoneNumbers || '[]');
              if (Array.isArray(parsed)) return parsed.join(' ');
          } catch {}
          return String(passport?.PhoneNumbers || '');
      })();
      const delegateName = getDelegateNameById(passport?.DelegateID);
      const companyName = getCompanyNameById(passport?.CompanyID);
      return `${passport?.NameArabic || ''} ${passport?.PassportNumber || ''} ${phoneText} ${delegateName || ''} ${companyName || ''}`.toLowerCase().includes(q);
  });
  const workListPeoplePageSize = 50;
  const workListPeopleTotalPages = Math.max(1, Math.ceil(workListPeopleRows.length / workListPeoplePageSize));
  const safeWorkListPeoplePage = Math.min(workListPeoplePage, workListPeopleTotalPages);
  const workListPeoplePageRows = workListPeopleRows.slice((safeWorkListPeoplePage - 1) * workListPeoplePageSize, safeWorkListPeoplePage * workListPeoplePageSize);

  const toggleWorkListPassport = (passportId) => {
      setWorkListSelectedPassportIds(prev => {
          const next = new Set(prev);
          next.has(passportId) ? next.delete(passportId) : next.add(passportId);
          return next;
      });
  };

  const toggleWorkListPage = () => {
      const ids = workListPeoplePageRows.map(p => p.PassportID).filter(Boolean);
      const allSelected = ids.length > 0 && ids.every(id => workListSelectedPassportIds.has(id));
      setWorkListSelectedPassportIds(prev => {
          const next = new Set(prev);
          ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
          return next;
      });
  };

  const continueToWorkListPicker = () => {
      const ids = workListSelectedPassportIds;
      if (!ids.size) {
          showToast('حدد شخصاً واحداً على الأقل', 'warning');
          return;
      }
      const rows = getWorkingPassports().filter(p => ids.has(p.PassportID));
      setWorkListPickerItems(rows.map(p => ({ SourceType:'PASSPORT', SourceID:Number(p.PassportID), NameArabic:p.NameArabic || '' })));
      setShowWorkListPeopleModal(false);
      setShowWorkListPicker(true);
  };

  return (
      <div className="passport-report-page min-h-screen bg-gray-50 py-3 md:py-4" dir="rtl">
          <div className="max-w-full md:max-w-[98%] mx-auto px-3 md:px-4">
              {/* رأس الصفحة */}
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
                  <div className="flex items-center gap-4 w-full md:w-auto">
                      <button
                          onClick={() => navigate('/reports')}
                          className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-gray-700 transition-colors w-full md:w-auto"
                      >
                          <ArrowRight size={20} />
                          العودة للكشوفات
                      </button>
                  </div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2 text-center md:text-right">
                      <FileSpreadsheet size={24} className="text-blue-600" />
                      مركز التقارير المتقدم - الجوازات
                  </h1>
              </div>

 

              {/* معايير البحث */}
              <div className="bg-white rounded-2xl md:rounded-lg shadow-md p-4 md:p-6 mb-5 md:mb-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5 md:mb-6">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <Filter className="text-blue-600" />
                          معايير البحث المتقدم
                      </h3>
                      <div className="flex gap-2 md:gap-3 w-full md:w-auto">
                          <button
                              onClick={resetSearch}
                              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors flex-1 md:flex-none"
                          >
                              <X size={18} />
                              مسح الكل
                          </button>
                          <button
                              onClick={performSearch}
                              className="bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors font-medium flex-1 md:flex-none"
                              disabled={isLoading}
                          >
                              {isLoading ? (
                                  <RefreshCw size={18} className="animate-spin" />
                              ) : (
                                  <Search size={18} />
                              )}
                              {isLoading ? 'جاري البحث...' : 'بحث متقدم'}
                          </button>
                      </div>
                  </div>

                  {renderFilters()}
              </div>

              {/* عرض نتائج البحث */}
              {hasSearched && (
                  <>
                 {/* الإحصائيات */}
                 {searchStats && (
                     <div className="flex justify-center mb-6">
                     <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md p-4 text-white" style={{minWidth: '200px', maxWidth: '250px'}}>
                     <div className="text-center">
    <Users size={32} className="mx-auto opacity-30 mb-2" />
    <p className="text-sm opacity-90 font-medium">إجمالي النتائج</p>
    <h3 className="text-2xl font-bold mt-1">{getWorkingPassports().length}</h3>
    {filters.processingStageFilter && selectedPassports.length !== getWorkingPassports().length && (
        <p className="text-xs opacity-75 mt-1">
            (من أصل {selectedPassports.length} قبل الفلتر)
        </p>
    )}
    {getWorkingPassports().length > itemsPerPage && (
        <p className="text-xs opacity-75 mt-1">
            عرض {Math.min(itemsPerPage, getWorkingPassports().length)} في الصفحة الحالية
        </p>
    )}

    {/* عرض الفلاتر النشطة */}
{hasSearched && getActiveFiltersInfo().length > 0 && (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">🎯 الفلاتر النشطة:</h4>
        <div className="flex flex-wrap gap-2">
            {getActiveFiltersInfo().map((filter, index) => (
                <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                    {filter}
                </span>
            ))}
        </div>
    </div>
)}


</div>
                     </div>
                 </div>
                      )}

                      {/* أدوات التصدير والمعاينة - مُسترجعة */}
                      <div className="bg-white rounded-2xl md:rounded-lg shadow-md p-4 mb-5 md:mb-6">
                          <div className="flex items-center justify-between flex-wrap gap-4">
                              <h3 className="text-lg font-bold text-gray-900">أدوات التقارير المتقدمة</h3>
                              <div className="grid grid-cols-2 md:flex md:items-center gap-2 w-full md:w-auto">
                                  {canShowBtn('زر معاينة تفاعلية') && <button
                                      onClick={handlePreview}
                                      className="bg-purple-600 hover:bg-purple-700 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base"
                                      disabled={selectedPassports.length === 0}
                                  >
                                      <Eye size={18} />
                                      معاينة تفاعلية
                                  </button>}
                                  
                                  {canShowBtn('زر PDF متقدم') && <button
                                      onClick={exportToPDFPreviewStyle}
                                      className="bg-red-600 hover:bg-red-700 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base"
                                      disabled={selectedPassports.length === 0}
                                  >
                                      <FileText size={18} />
                                      PDF متقدم
                                  </button>}

                                  
{canShowBtn('زر Excel متقدم') && <button
    onClick={handleAdvancedExport}
    className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base"
    disabled={selectedPassports.length === 0}
>
    <FileSpreadsheet size={18} />
    Excel متقدم (XLSX)
</button>}

                                  
                                  {canShowBtn('زر طباعة احترافية') && <button
                                      onClick={handleAdvancedPrint}
                                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base"
                                      disabled={selectedPassports.length === 0}
                                  >
                                      <Printer size={18} />
                                      طباعة احترافية
                                  </button>}

                                  {canShowBtn('زر طباعة Agent') && <button
                                      onClick={openAgentPrintModal}
                                      className="bg-blue-800 hover:bg-blue-900 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                                      disabled={!hasSearched || getWorkingPassports().length === 0}
                                      title="طباعة نفس شكل التقرير الاحترافي عبر Masar Print Agent على جهاز آخر بالشبكة"
                                  >
                                      <Printer size={18} />
                                      طباعة Agent
                                  </button>}


                                  {canShowStrictBtn('زر طباعة باركود جماعية') && <button
                                      onClick={openBarcodeBatchPrintModal}
                                      className="bg-slate-800 hover:bg-slate-900 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                                      disabled={!hasSearched || getWorkingPassports().length === 0 || barcodeBatchPrinting}
                                      title="اختيار إحدى طرق طباعة الباركود المحفوظة وطباعة كل الأشخاص الظاهرين بالتتابع"
                                  >
                                      <Barcode size={18} />
                                      <span>طباعة باركود جماعية</span>
                                      {getWorkingPassports().length > 0 && (
                                          <span className="bg-white/15 px-2 py-0.5 rounded-full text-xs">
                                              {getWorkingPassports().length}
                                          </span>
                                      )}
                                  </button>}


                                  {canShowStrictBtn('زر تصدير جهات الاتصال') && <button
                                      onClick={exportDisplayedContacts}
                                      className="bg-green-700 hover:bg-green-800 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                                      disabled={!hasSearched || getWorkingPassports().length === 0 || isLoading}
                                      title="تصدير كل النتائج المعروضة حاليًا كجهات اتصال: الاسم - المندوب - الشركة - المحافظة - رقم الملف"
                                  >
                                      <Phone size={18} />
                                      <span>تصدير جهات الاتصال</span>
                                      {getWorkingPassports().length > 0 && (
                                          <span className="bg-white/15 px-2 py-0.5 rounded-full text-xs">
                                              {getWorkingPassports().length}
                                          </span>
                                      )}
                                  </button>}

{canUseWorkLists && <button
    onClick={openWorkListPeoplePicker}
    className="bg-cyan-700 hover:bg-cyan-800 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base disabled:opacity-50"
    disabled={!hasSearched || getWorkingPassports().length === 0}
    title="اختيار أشخاص محددين من النتائج الحالية وإضافتهم إلى قائمة عمل"
>
    <ListPlus size={18} />
    <span>إضافة لقائمة عمل</span>
</button>}

{canShowBtn('زر تصدير كصورة') && <button
    onClick={savePassportReportAsImages}
    className="bg-sky-500 hover:bg-sky-600 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base"
    disabled={selectedPassports.length === 0}
    title="حفظ الكشف كصور منسقة وواضحة"
>
    <Image size={18} />
    تصدير كصورة
</button>}

{canShowBtn('زر تصدير صور المناديب ZIP') && <button
    onClick={exportDelegateReportsAsZip}
    className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg flex items-center justify-center gap-2 text-sm md:text-base"
    disabled={delegateZipLoading || selectedPassports.length === 0}
    title="تصدير كشف صورة مستقل لكل مندوب داخل ملف ZIP واحد"
>
    <Image size={18} />
    {delegateZipLoading
        ? (delegateZipProgress
            ? `مناديب ${delegateZipProgress.current}/${delegateZipProgress.total}${delegateZipProgress.delegateName ? ` - ${delegateZipProgress.delegateName}` : ''}`
            : 'جارٍ تجهيز ZIP...')
        : 'تصدير صور المناديب ZIP'}
</button>}


{/* التكرار الذكي */}
{canShowBtn('زر التكرار الذكي') && <button
    onClick={navigateToSmartRepeat}
    disabled={!hasSearched || selectedPassports.length === 0}
    className="flex items-center justify-center gap-2 px-3 md:px-4 py-2 bg-purple-600 text-white rounded-xl md:rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
    title="التكرار الذكي - املأ نماذج تلقائياً"
>
    <Repeat size={18} />
    <span>التكرار الذكي</span>
    {getWorkingPassports().length > 0 && (
        <span className="bg-purple-800 px-2 py-0.5 rounded-full text-xs">
            {getWorkingPassports().length}
        </span>
    )}
</button>}



                                  {canShowBtn('زر تصدير الصور ZIP') && <button
  onClick={handleExportImagesZip}
  className="bg-teal-600 hover:bg-teal-700 text-white px-3 md:px-4 py-2 rounded-xl md:rounded-lg disabled:opacity-50 text-sm md:text-base"
  disabled={zipLoading || selectedPassports.length === 0}
  title="تجميع صورة الجواز والصورة الشخصية لكل نتيجة في ملف ZIP واحد"
>
  {zipLoading ? (zipProgress?.message || 'جاري تجهيز ZIP... انتظر') : 'تصدير الصور (ZIP)'}
</button>}


                              </div>
                          </div>
                      </div>

                      {/* اختيار الأعمدة - نسخة موبايل مستقلة */}
                      <div className="md:hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
                          <h3 className="text-base font-bold text-gray-900 mb-3">إعدادات عرض الكشف</h3>
                          <div className="grid grid-cols-1 gap-2">
                              {canShowBtn('زر ضبط الإعدادات') && <button
                                  onClick={() => setShowReportSettingsModal(true)}
                                  className="bg-amber-50 text-amber-800 px-4 py-3 rounded-2xl flex items-center justify-between gap-2 border border-amber-200 font-bold"
                              >
                                  <span className="flex items-center gap-2">
                                      <span>⚙️</span>
                                      ضبط إعدادات الصورة والطباعة
                                  </span>
                                  <ChevronDown size={18} className="rotate-90" />
                              </button>}
                              {canShowBtn('زر تخصيص الأعمدة') && <button
                                  onClick={() => setShowExportColumns(true)}
                                  className="bg-slate-900 text-white px-4 py-3 rounded-2xl flex items-center justify-between gap-2 font-bold"
                              >
                                  <span className="flex items-center gap-2">
                                      <Filter size={18} />
                                      تخصيص الأعمدة الظاهرة
                                  </span>
                                  <span className="bg-white/15 text-xs rounded-full px-2 py-1">
                                      {Object.values(exportColumns).filter(Boolean).length}
                                  </span>
                              </button>}
                          </div>
                      </div>
                      {renderMobileColumnsSheet()}

                      {/* اختيار الأعمدة */}
                      <div className="hidden md:block bg-white rounded-2xl md:rounded-lg shadow-md p-4 mb-4">
                          <div className="flex justify-between items-center mb-4">
                              <h3 className="text-lg font-bold text-gray-900">اختيار الأعمدة المطلوب عرضها</h3>
                              <div className="flex gap-2">
                                  {/* 🔧 زر ضبط إعدادات التقارير */}
                                  {canShowBtn('زر ضبط الإعدادات') && <button
                                      onClick={() => setShowReportSettingsModal(true)}
                                      className="bg-amber-100 text-amber-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-amber-200 transition-colors border border-amber-300"
                                      title="ضبط إعدادات الصورة والطباعة"
                                  >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14M12 2v2M12 20v2M2 12h2M20 12h2"/>
                                      </svg>
                                      ضبط الإعدادات
                                      {(imageExportSettings.colorTheme !== 'blue' || !imageExportSettings.showHeader || !imageExportSettings.showFooter || !imageExportSettings.showTotal || !imageExportSettings.showPageNum || imageExportSettings.customTitle || imageExportSettings.maxRowsPerImage !== 25) ? (
                                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                      ) : null}
                                  </button>}

                                  {canShowBtn('زر تخصيص الأعمدة') && <button
                                      onClick={() => setShowExportColumns(!showExportColumns)}
                                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg 
                                             flex items-center gap-2 hover:bg-gray-200 transition-colors"
                                  >
                                      <Filter size={18} />
                                      تخصيص الأعمدة
                                      <ChevronDown size={18} className={`transition-transform ${showExportColumns ? 'rotate-180' : ''}`} />
                                  </button>}
                                  
                                  {showExportColumns && (
                                      <>
                                          <button
                                              onClick={selectAllColumns}
                                              className="bg-blue-100 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-200 text-sm flex items-center gap-1"
                                          >
                                              <Check className="w-4 h-4" />
                                              تحديد الكل
                                          </button>
                                          <button
                                              onClick={unselectAllColumns}
                                              className="bg-gray-100 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
                                          >
                                              <X className="w-4 h-4" />
                                              إلغاء تحديد الكل
                                          </button>
                                      </>
                                  )}
                              </div>
                          </div>

                          {showExportColumns && (
                              <div className="border-t pt-4">
                                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                      {Object.entries(exportColumns).map(([key, value]) => (
                                          <label key={key} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                                              <input
                                                  type="checkbox"
                                                  checked={value}
                                                  onChange={() => {
                                                      const newValue = !value;
                                                      setExportColumns({...exportColumns, [key]: newValue});
                                                      setVisibleColumns({...visibleColumns, [key]: newValue});
                                                  }}
                                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                                              />
                                              <span className="text-sm">{getColumnLabel(key)}</span>
                                          </label>
                                      ))}
                                  </div>

                                  {/* خيار إخفاء الملاحظات الأصلية */}
                                  <div className="mt-4 pt-3 border-t border-gray-200">
                                      <label className="inline-flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-yellow-50 border border-yellow-200 bg-yellow-50">
                                          <input
                                              type="checkbox"
                                              checked={hideOriginalNotes}
                                              onChange={(e) => setHideOriginalNotes(e.target.checked)}
                                              className="w-4 h-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                          />
                                          <span className="text-sm font-medium text-yellow-800">
                                              🙈 إخفاء الملاحظات الأصلية من قاعدة البيانات (عرض المؤقتة فقط في الجدول والتصدير)
                                          </span>
                                      </label>
                                  </div>
                              </div>
                          )}
                      </div>

                      {/* مؤشر الترتيب الحالي */}
{sortConfig.key && (
    <div className="hidden md:block bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-700">
                مُرتب حسب: ${
    sortConfig.key === 'nameArabic' ? 'الاسم بالعربي' :
    sortConfig.key === 'passportId' ? 'رقم الملف' :
    sortConfig.key === 'governorate' ? 'المحافظة' :
    sortConfig.key === 'company' ? 'الشركة' :
    sortConfig.key === 'blacklistCompanies' ? 'الشركات الممنوع فيها' :
    sortConfig.key === 'blacklistReasons' ? 'سبب المنع' :
    sortConfig.key === 'notes' ? 'الملاحظات' :
sortConfig.key === 'note1' ? 'ملاحظة 1' :
sortConfig.key === 'note2' ? 'ملاحظة 2' :
sortConfig.key === 'note3' ? 'ملاحظة 3' :
sortConfig.key === 'note4' ? 'ملاحظة 4' : sortConfig.key                    } ({sortConfig.direction === 'asc' ? 'تصاعدي' : 'تنازلي'})
                </span>
            </div>
            <button
                onClick={() => setSortConfig({ key: null, direction: 'asc' })}
                className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
            >
                <X size={14} />
                إلغاء الترتيب
            </button>
        </div>
    </div>
)}


{/* أدوات ترتيب مخصصة للموبايل فقط */}
{renderMobileSortControls()}

{/* عرض النتائج على الموبايل بنظام كروت — الكمبيوتر يظل بالجدول الأصلي */}
{renderMobileReportCards()}

{/* عرض الجدول - استبدل هذا القسم كاملاً */}
<div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
    <div className="overflow-x-auto">
        <table className="w-full">
   <thead className="bg-blue-600 text-white">
    <tr>
        {/* عمود الإخفاء المؤقت */}
        <th className="px-2 py-3 text-center text-sm font-semibold">
            إخفاء
        </th>

        {visibleColumns.serialNumber && (
            <th className="px-4 py-3 text-right text-sm font-semibold">م</th>
        )}

        {visibleColumns.passportId && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('passportId')}
            >
                <div className="flex items-center justify-between">
                    <span>رقم الملف</span>
                    {getSortIcon('passportId')}
                </div>
            </th>
        )}
        {visibleColumns.nameArabic && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('nameArabic')}
            >
                <div className="flex items-center justify-between">
                    <span>الاسم بالعربي</span>
                    {getSortIcon('nameArabic')}
                </div>
            </th>
        )}
        {visibleColumns.nameEnglish && (
            <th className="px-4 py-3 text-right text-sm font-semibold">
                الاسم بالإنجليزي
            </th>
        )}
        {visibleColumns.passportNumber && (
            <th className="px-4 py-3 text-right text-sm font-semibold">
                رقم الجواز
            </th>
        )}
        {visibleColumns.nationalId && (
            <th className="px-4 py-3 text-right text-sm font-semibold">
                الرقم القومي
            </th>
        )}
        {visibleColumns.phoneNumbers && (
            <th className="px-4 py-3 text-right text-sm font-semibold">
                أرقام الهواتف
            </th>
        )}
        {visibleColumns.delegate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('delegate')}
            >
                <div className="flex items-center justify-between">
                    <span>المندوب</span>
                    {getSortIcon('delegate')}
                </div>
            </th>
        )}
        {visibleColumns.company && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('company')}
            >
                <div className="flex items-center justify-between">
                    <span>الشركة</span>
                    {getSortIcon('company')}
                </div>
            </th>
        )}
        {visibleColumns.previousCompanies && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('previousCompanies')}
            >
                <div className="flex items-center justify-between">
                    <span>الشركات السابقة</span>
                    {getSortIcon('previousCompanies')}
                </div>
            </th>
        )}
        {visibleColumns.blacklistCompanies && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('blacklistCompanies')}
            >
                <div className="flex items-center justify-between">
                    <span>الشركات الممنوع فيها</span>
                    {getSortIcon('blacklistCompanies')}
                </div>
            </th>
        )}
        {visibleColumns.blacklistReasons && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('blacklistReasons')}
            >
                <div className="flex items-center justify-between">
                    <span>سبب المنع</span>
                    {getSortIcon('blacklistReasons')}
                </div>
            </th>
        )}
        {visibleColumns.passportProfession && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('passportProfession')}
            >
                <div className="flex items-center justify-between">
                    <span>المهنة بالجواز</span>
                    {getSortIcon('passportProfession')}
                </div>
            </th>
        )}
        {visibleColumns.travelProfession && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('travelProfession')}
            >
                <div className="flex items-center justify-between">
                    <span>مهنة السفر</span>
                    {getSortIcon('travelProfession')}
                </div>
            </th>
        )}
        {visibleColumns.medicalExamDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('medicalExamDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ الكشف</span>
                    {getSortIcon('medicalExamDate')}
                </div>
            </th>
        )}
        {visibleColumns.networkDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('networkDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ النت</span>
                    {getSortIcon('networkDate')}
                </div>
            </th>
        )}
        {visibleColumns.visaDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('visaDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ التأشير</span>
                    {getSortIcon('visaDate')}
                </div>
            </th>
        )}
        {visibleColumns.travelDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('travelDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ السفر</span>
                    {getSortIcon('travelDate')}
                </div>
            </th>
        )}
        {visibleColumns.borderNumber && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('borderNumber')}
            >
                <div className="flex items-center justify-between">
                    <span>رقم الحدود</span>
                    {getSortIcon('borderNumber')}
                </div>
            </th>
        )}
        {visibleColumns.violationNotes && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('violationNotes')}
            >
                <div className="flex items-center justify-between">
                    <span>المخالفة / ملاحظتها</span>
                    {getSortIcon('violationNotes')}
                </div>
            </th>
        )}
        {visibleColumns.travelDuration && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('travelDuration')}
            >
                <div className="flex items-center justify-between">
                    <span>مدة السفر</span>
                    {getSortIcon('travelDuration')}
                </div>
            </th>
        )}
        {visibleColumns.passportStatus && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('passportStatus')}
            >
                <div className="flex items-center justify-between">
                    <span>حالة الجواز</span>
                    {getSortIcon('passportStatus')}
                </div>
            </th>
        )}
        {visibleColumns.receiveStatus && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('receiveStatus')}
            >
                <div className="flex items-center justify-between">
                    <span>حالة الاستلام</span>
                    {getSortIcon('receiveStatus')}
                </div>
            </th>
        )}
        {visibleColumns.notes && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('notes')}
            >
                <div className="flex items-center justify-between">
                    <span>الملاحظات</span>
                    {getSortIcon('notes')}
                </div>
            </th>
        )}
        {visibleColumns.note1 && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('note1')}
            >
                <div className="flex items-center justify-between">
                    <span>ملاحظة 1</span>
                    {getSortIcon('note1')}
                </div>
            </th>
        )}
        {visibleColumns.note2 && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('note2')}
            >
                <div className="flex items-center justify-between">
                    <span>ملاحظة 2</span>
                    {getSortIcon('note2')}
                </div>
            </th>
        )}
        {visibleColumns.note3 && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('note3')}
            >
                <div className="flex items-center justify-between">
                    <span>ملاحظة 3</span>
                    {getSortIcon('note3')}
                </div>
            </th>
        )}
        {visibleColumns.note4 && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('note4')}
            >
                <div className="flex items-center justify-between">
                    <span>ملاحظة 4</span>
                    {getSortIcon('note4')}
                </div>
            </th>
        )}
        {visibleColumns.medicalExamStatus && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('medicalExamStatus')}
            >
                <div className="flex items-center justify-between">
                    <span>حالة الكشف الطبي</span>
                    {getSortIcon('medicalExamStatus')}
                </div>
            </th>
        )}
        {visibleColumns.address && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('address')}
            >
                <div className="flex items-center justify-between">
                    <span>العنوان</span>
                    {getSortIcon('address')}
                </div>
            </th>
        )}
        {visibleColumns.governorate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('governorate')}
            >
                <div className="flex items-center justify-between">
                    <span>المحافظة</span>
                    {getSortIcon('governorate')}
                </div>
            </th>
        )}
        {visibleColumns.age && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('age')}
            >
                <div className="flex items-center justify-between">
                    <span>العمر</span>
                    {getSortIcon('age')}
                </div>
            </th>
        )}
        {visibleColumns.birthDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('birthDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ الميلاد</span>
                    {getSortIcon('birthDate')}
                </div>
            </th>
        )}
        {visibleColumns.militaryStatus && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('militaryStatus')}
            >
                <div className="flex items-center justify-between">
                    <span>الموقف من التجنيد</span>
                    {getSortIcon('militaryStatus')}
                </div>
            </th>
        )}
        {visibleColumns.passportIssueDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('passportIssueDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ إصدار الجواز</span>
                    {getSortIcon('passportIssueDate')}
                </div>
            </th>
        )}
        {visibleColumns.passportExpiryDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('passportExpiryDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ انتهاء الجواز</span>
                    {getSortIcon('passportExpiryDate')}
                </div>
            </th>
        )}
        {visibleColumns.licenseType && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('licenseType')}
            >
                <div className="flex items-center justify-between">
                    <span>نوع الرخصة</span>
                    {getSortIcon('licenseType')}
                </div>
            </th>
        )}
        {visibleColumns.licenseExpiryDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('licenseExpiryDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ انتهاء الرخصة</span>
                    {getSortIcon('licenseExpiryDate')}
                </div>
            </th>
        )}
        {visibleColumns.abilityTest && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('abilityTest')}
            >
                <div className="flex items-center justify-between">
                    <span>نتيجة اختبار القدرات</span>
                    {getSortIcon('abilityTest')}
                </div>
            </th>
        )}
        {visibleColumns.receiveDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('receiveDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ الاستلام</span>
                    {getSortIcon('receiveDate')}
                </div>
            </th>
        )}
        {visibleColumns.receiverName && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('receiverName')}
            >
                <div className="flex items-center justify-between">
                    <span>اسم المستلم</span>
                    {getSortIcon('receiverName')}
                </div>
            </th>
        )}
        {visibleColumns.receiverPhone && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('receiverPhone')}
            >
                <div className="flex items-center justify-between">
                    <span>رقم هاتف المستلم</span>
                    {getSortIcon('receiverPhone')}
                </div>
            </th>
        )}
        {visibleColumns.applicationNumber && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('applicationNumber')}
            >
                <div className="flex items-center justify-between">
                    <span>رقم الطلب</span>
                    {getSortIcon('applicationNumber')}
                </div>
            </th>
        )}
        {visibleColumns.fingerprintDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('fingerprintDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ البصمة</span>
                    {getSortIcon('fingerprintDate')}
                </div>
            </th>
        )}
        {visibleColumns.consulateDate && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('consulateDate')}
            >
                <div className="flex items-center justify-between">
                    <span>تاريخ دخول القنصلية</span>
                    {getSortIcon('consulateDate')}
                </div>
            </th>
        )}
        {visibleColumns.documentsDeliveryStatus && (
            <th 
                className="px-4 py-3 text-right text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                onClick={() => handleSort('documentsDeliveryStatus')}
            >
                <div className="flex items-center justify-between">
                    <span>حالة تسليم الأوراق</span>
                    {getSortIcon('documentsDeliveryStatus')}
                </div>
            </th>
        )}


        {visibleColumns.medicalInsuranceDate && (
    <th
        className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-blue-700 whitespace-nowrap"
        onClick={() => setSortConfig({ key: 'medicalInsuranceDate', direction: sortConfig.key === 'medicalInsuranceDate' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}
    >
        <div className="flex items-center gap-1">
            🏥 تاريخ التأمين الطبي
            {sortConfig.key === 'medicalInsuranceDate' && (
                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
            )}
        </div>
    </th>
)}

    </tr>
</thead>
<tbody className="divide-y divide-gray-200">
{getWorkingPassports().length > 0 ? (
    getPaginatedData(getWorkingPassports()).map((passport, index) => {
        const globalIndex = (currentPage - 1) * itemsPerPage + index;
        return (
            <tr 
                key={passport.PassportID} 
                className={`hover:bg-gray-50 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
            >
                {/* خانة الإخفاء المؤقت */}
                <td className="px-2 py-3 text-center">
                    <input
                        type="checkbox"
                        checked={!!tempHidden[passport.PassportID]}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setTempHidden(prev => {
                                const next = { ...prev };
                                if (checked) {
                                    next[passport.PassportID] = true;
                                } else {
                                    delete next[passport.PassportID];
                                }
                                return next;
                            });
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                </td>

                {visibleColumns.serialNumber && (
                    <td className="px-4 py-3 text-sm font-medium text-gray-600">
                        {globalIndex + 1}
                    </td>
                )}

                            {visibleColumns.passportId && (
                                <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                                    {getPassportFileNumber(passport)}
                                </td>
                            )}
                            {visibleColumns.nameArabic && (
                                <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                    {passport.NameArabic}
                                    {passport.IsActive === 0 && (
                                        <span className="mr-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                                            📁 مؤرشف
                                        </span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.nameEnglish && (
                                <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                    {passport.NameEnglish || '-'}
                                </td>
                            )}
                            {visibleColumns.passportNumber && (
                                <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                                    {passport.PassportNumber}
                                </td>
                            )}
                            {visibleColumns.nationalId && (
                                <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                                    {passport.NationalID || '-'}
                                </td>
                            )}
                            {visibleColumns.phoneNumbers && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="flex items-center gap-1">
                                        <Phone size={14} className="text-gray-400" />
                                        <span className="text-xs">
                                            {(() => {
                                                try {
                                                    const phones = JSON.parse(passport.PhoneNumbers || '[]');
                                                    if (Array.isArray(phones)) {
                                                        return phones.filter(phone => phone && phone.trim()).join(' - ') || '-';
                                                    } else {
                                                        return passport.PhoneNumbers || '-';
                                                    }
                                                } catch {
                                                    return passport.PhoneNumbers || '-';
                                                }
                                            })()}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.delegate && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="flex items-center gap-1">
                                        <User size={14} className="text-gray-400" />
                                        <span className="text-xs">
                                            {delegates.find(d => d.DelegateID === passport.DelegateID)?.DelegateName || '-'}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.company && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="flex items-center gap-1">
                                        <Building size={14} className="text-gray-400" />
                                        <span className="text-xs">
                                            {companies.find(c => c.CompanyID === passport.CompanyID)?.CompanyName || '-'}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.previousCompanies && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="max-w-40 truncate" title={(() => {
                                        try {
                                            const prevCompanies = JSON.parse(passport.PreviousCompanies || '[]');
                                            if (Array.isArray(prevCompanies) && prevCompanies.length > 0) {
                                                const companyNames = prevCompanies
                                                    .map(companyId => {
                                                        const company = companies.find(c => c.CompanyID.toString() === companyId?.toString());
                                                        return company?.CompanyName;
                                                    })
                                                    .filter(Boolean);
                                                
                                                if (companyNames.length > 0) {
                                                    return companyNames.join(' ، ');
                                                }
                                            }
                                            return '-';
                                        } catch {
                                            return '-';
                                        }
                                    })()}>
                                        <span className="text-xs text-blue-600">
                                            {(() => {
                                                try {
                                                    const prevCompanies = JSON.parse(passport.PreviousCompanies || '[]');
                                                    if (Array.isArray(prevCompanies) && prevCompanies.length > 0) {
                                                        const companyNames = prevCompanies
                                                            .map(companyId => {
                                                                const company = companies.find(c => c.CompanyID.toString() === companyId?.toString());
                                                                return company?.CompanyName;
                                                            })
                                                            .filter(Boolean);
                                                        
                                                        if (companyNames.length > 0) {
                                                            return companyNames.join(' ، ');
                                                        }
                                                    }
                                                    return '-';
                                                } catch {
                                                    return '-';
                                                }
                                            })()}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.blacklistCompanies && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="max-w-48 whitespace-pre-wrap" title={passport.BlacklistCompanies || '-'}>
                                        <span className={`text-xs font-semibold ${passport.BlacklistCompanies && passport.BlacklistCompanies !== '-' ? 'text-red-700' : 'text-gray-400'}`}>
                                            {passport.BlacklistCompanies || '-'}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.blacklistReasons && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="max-w-56 whitespace-pre-wrap" title={passport.BlacklistReasons || '-'}>
                                        <span className="text-xs text-gray-700">
                                            {passport.BlacklistReasons || '-'}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.passportProfession && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="flex items-center gap-1">
                                        <Briefcase size={14} className="text-gray-400" />
                                        <span className="text-xs">
                                            {getPassportProfessionName(passport)}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.travelProfession && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="flex items-center gap-1">
                                        <Package size={14} className="text-gray-400" />
                                        <span className="text-xs">
                                            {getTravelProfessionName(passport)}
                                        </span>
                                    </div>
                                </td>
                            )}
                        {visibleColumns.medicalExamDate && (
    <td className="px-4 py-3 text-sm">
        {passport.MedicalExamDate && passport.MedicalExamDate !== null ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <Calendar size={12} className="mr-1" />
                {(() => {
                    try {
                        const date = new Date(passport.MedicalExamDate);
                        return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('ar-EG');
                    } catch {
                        return '-';
                    }
                })()}
            </span>
        ) : (
            <span className="text-gray-400">-</span>
        )}
    </td>
)}
                         {visibleColumns.networkDate && (
    <td className="px-4 py-3 text-sm">
        {passport.NetworkDate && passport.NetworkDate !== null ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Calendar size={12} className="mr-1" />
                {(() => {
                    try {
                        const date = new Date(passport.NetworkDate);
                        return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('ar-EG');
                    } catch {
                        return '-';
                    }
                })()}
            </span>
        ) : (
            <span className="text-gray-400">-</span>
        )}
    </td>
)}
                            {visibleColumns.visaDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.VisaDate ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(passport.VisaDate).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.travelDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.TravelDate ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(passport.TravelDate).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.borderNumber && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs font-mono">{passport.BorderNumber || '-'}</span>
                                </td>
                            )}
                            {visibleColumns.violationNotes && (
                                <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                                    <span className="whitespace-pre-wrap break-words">{passport.ViolationNotes || '-'}</span>
                                </td>
                            )}
                            {visibleColumns.travelDuration && (
                                <td className="px-4 py-3 text-sm">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                        {passport.TravelDuration || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.passportStatus && (
                                <td className="px-4 py-3 text-sm">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        passport.PassportStatus === 'جديد' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {passport.PassportStatus || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.receiveStatus && (
                                <td className="px-4 py-3 text-sm">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        passport.PassportReceiveStatus === 'استلم العميل' ? 'bg-green-100 text-green-800' :
                                        passport.PassportReceiveStatus === 'لم يستلم' ? 'bg-red-100 text-red-800' :
                                        'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {passport.PassportReceiveStatus || 'لم يستلم'}
                                    </span>
                                </td>
                            )}
{visibleColumns.notes && (
    <td className="px-4 py-3 text-sm text-gray-700">
        <div className="flex flex-col gap-1 max-w-40">
            {/* الملاحظات الأساسية من قاعدة البيانات - تُخفى بناءً على الإعداد */}
            {!hideOriginalNotes && (
                <div className="truncate" title={getAllNotes(passport)}>
                    <span className="text-xs text-gray-600">
                        {getAllNotes(passport) || '-'}
                    </span>
                </div>
            )}

            {/* الملاحظة المؤقتة للكشف الحالي فقط */}
            <input
                type="text"
                value={tempNotes[passport.PassportID] || ''}
                onChange={(e) =>
                    setTempNotes(prev => ({
                        ...prev,
                        [passport.PassportID]: e.target.value
                    }))
                }
                placeholder="ملاحظة مؤقتة للكشف الحالي"
                className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-xs focus:ring-blue-500 focus:border-blue-500"
            />
        </div>
    </td>
)}

{visibleColumns.note1 && (
    <td className="px-4 py-3 text-sm text-gray-700">
        <span className="text-xs text-gray-600">
            {getSpecificNote(passport, 1) || '-'}
        </span>
    </td>
)}
{visibleColumns.note2 && (
    <td className="px-4 py-3 text-sm text-gray-700">
        <span className="text-xs text-gray-600">
            {getSpecificNote(passport, 2) || '-'}
        </span>
    </td>
)}
{visibleColumns.note3 && (
    <td className="px-4 py-3 text-sm text-gray-700">
        <span className="text-xs text-gray-600">
            {getSpecificNote(passport, 3) || '-'}
        </span>
    </td>
)}
{visibleColumns.note4 && (
    <td className="px-4 py-3 text-sm text-gray-700">
        <span className="text-xs text-gray-600">
            {getSpecificNote(passport, 4) || '-'}
        </span>
    </td>
)}
                            
                            {/* 🔥 أضف هذا الجزء هنا - البيانات الجديدة */}
                            {visibleColumns.medicalExamStatus && (
    <td className="px-4 py-3 text-sm">
        {passport.MedicalExamStatus && passport.MedicalExamStatus.trim() !== '' && passport.MedicalExamStatus !== 'اختر الحالة' ? (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                passport.MedicalExamStatus === 'لائق' ? 'bg-green-100 text-green-800' :
                passport.MedicalExamStatus === 'غير لائق' ? 'bg-red-100 text-red-800' :
                passport.MedicalExamStatus === 'مؤجل' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
            }`}>
                {passport.MedicalExamStatus}
            </span>
        ) : (
            <span className="text-gray-400 text-xs">-</span>
        )}
    </td>
)}
                            {visibleColumns.address && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs">
                                        {passport.Address || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.governorate && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs">
                                        {passport.Governorate || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.age && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs">
                                        {passport.Age || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.birthDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.DateOfBirth ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(passport.DateOfBirth).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.militaryStatus && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs">
                                        {passport.MilitaryStatus || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.passportIssueDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.PassportIssueDate ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(passport.PassportIssueDate).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.passportExpiryDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.PassportExpiryDate ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(passport.PassportExpiryDate).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.licenseType && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs">
                                        {passport.LicenseType || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.licenseExpiryDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.LicenseExpiryDate ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(passport.LicenseExpiryDate).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.abilityTest && (
                                <td className="px-4 py-3 text-sm">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        passport.AbilityTest === 'لائق' ? 'bg-green-100 text-green-800' : 
                                        passport.AbilityTest === 'غير لائق' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                        {passport.AbilityTest || '-'}
                                    </span>
                                </td>
                            )}
                       {visibleColumns.receiveDate && (
    <td className="px-4 py-3 text-sm">
        {(passport.ReceiveDate && passport.PassportReceiveStatus && passport.PassportReceiveStatus !== 'لم يستلم') ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Calendar size={12} className="mr-1" />
                {new Date(passport.ReceiveDate).toLocaleDateString('ar-EG')}
            </span>
        ) : (
            <span className="text-gray-400">-</span>
        )}
    </td>
)}
                            {visibleColumns.receiverName && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs">
                                        {passport.FriendName || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.receiverPhone && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <div className="flex items-center gap-1">
                                        <Phone size={14} className="text-gray-400" />
                                        <span className="text-xs font-mono">
                                            {passport.FriendPhone || '-'}
                                        </span>
                                    </div>
                                </td>
                            )}
                            {visibleColumns.applicationNumber && (
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className="text-xs font-mono">
                                        {passport.ApplicationNumber || '-'}
                                    </span>
                                </td>
                            )}
                            {visibleColumns.fingerprintDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.FingerprintDate ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            <Fingerprint size={12} className="mr-1" />
                                            {new Date(passport.FingerprintDate).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.consulateDate && (
                                <td className="px-4 py-3 text-sm">
                                    {passport.ConsulateEntryDate ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                            <Building size={12} className="mr-1" />
                                            {new Date(passport.ConsulateEntryDate).toLocaleDateString('ar-EG')}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                            )}
                            {visibleColumns.documentsDeliveryStatus && (
                                <td className="px-4 py-3 text-sm">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        passport.DocumentsDeliveryStatus === 'سلم' ? 'bg-green-100 text-green-800' : 
                                        passport.DocumentsDeliveryStatus === 'سلم البعض وينقصه شيء' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        <Package size={12} className="mr-1" />
                                        {passport.DocumentsDeliveryStatus || 'لم يسلم'}
                                    </span>
                                </td>
                            )}


                            {visibleColumns.medicalInsuranceDate && (
    <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
        {passport.MedicalInsuranceDate ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✅ {new Date(passport.MedicalInsuranceDate).toLocaleDateString('ar-EG')}
            </span>
        ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600">
                ❌ لا يوجد
            </span>
        )}
    </td>
)}

                            {/* 🔥 نهاية البيانات الجديدة */}
                            </tr>
                    );
                    })
                ) : (
                    <tr>
                      <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="px-4 py-8 text-center text-gray-500">
                            <div className="flex flex-col items-center justify-center py-6">
                                <AlertCircle size={48} className="text-gray-300 mb-4" />
                                <p className="text-lg font-medium">لا توجد نتائج للبحث المحدد</p>
                                <p className="text-sm text-gray-400 mt-1">جرب تعديل معايير البحث</p>
                            </div>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
        </div>

{/* مكون التنقل بين الصفحات */}
{getWorkingPassports().length > 0 && (
    <PaginationControls data={getWorkingPassports()} />
)}

</div>

                      {/* معلومات إضافية */}
                     <div className="mt-4 text-center text-sm text-gray-500">
    عرض {getPaginatedData(getWorkingPassports()).length} جواز في الصفحة الحالية من أصل {getWorkingPassports().length} جواز
</div>

                  </>
              )}

              {/* رسائل النجاح والخطأ */}
              {toast && (
                  <div className={`
                      fixed bottom-4 right-4 z-50 
                      p-4 rounded-lg shadow-lg 
                      flex items-center gap-3
                      animate-slide-up
                      ${toast.type === 'success' ? 'bg-green-100 text-green-800' : 
                        toast.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                        toast.type === 'info' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'}
                  `}>
                      {toast.message}
                      <button 
                          onClick={() => setToast(null)} 
                          className="ml-3 hover:opacity-75"
                      >
                          <X size={16} />
                      </button>
                  </div>
              )}
          </div>

          {showAgentPrintModal && (
              <div className="fixed inset-0 bg-black/60 z-[10060] flex items-center justify-center p-3 md:p-5" dir="rtl">
                  <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
                      <div className="bg-gradient-to-l from-blue-900 to-indigo-700 text-white px-5 py-4 flex items-start justify-between gap-4">
                          <div>
                              <h2 className="text-lg md:text-xl font-black flex items-center gap-2">
                                  <Printer size={22} /> طباعة Agent
                              </h2>
                              <p className="text-xs md:text-sm text-blue-50 mt-1 leading-6">
                                  نفس شكل وتنسيق الطباعة الاحترافية، لكن يتم التنفيذ على طابعة جهاز Agent المتصل بالشبكة.
                              </p>
                          </div>
                          <button
                              type="button"
                              onClick={closeAgentPrintModal}
                              disabled={agentPrintSending}
                              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 shrink-0 disabled:opacity-50"
                          >
                              <X size={21} />
                          </button>
                      </div>

                      <div className="p-4 md:p-5 overflow-y-auto space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                                  <div className="text-xs text-blue-700 font-bold">عدد الأشخاص</div>
                                  <div className="text-3xl font-black text-blue-950 mt-1">{getWorkingPassports().length}</div>
                              </div>
                              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                                  <div className="text-xs text-slate-600 font-bold">مقاس الورق</div>
                                  <div className="font-black text-slate-900 mt-1">A4 أفقي</div>
                                  <div className="text-xs text-slate-500 mt-1">مطابق للطباعة الاحترافية</div>
                              </div>
                              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                                  <div className="text-xs text-slate-600 font-bold">الأعمدة</div>
                                  <div className="text-2xl font-black text-slate-900 mt-1">
                                      {AGENT_REPORT_COLUMN_ORDER.filter(key => visibleColumns[key]).length}
                                  </div>
                              </div>
                          </div>

                          {agentPrintLoading ? (
                              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 text-center text-blue-800 font-bold">
                                  جاري تحميل أجهزة Agent والطابعات المتاحة...
                              </div>
                          ) : (
                              <>
                                  <div>
                                      <div className="flex items-center justify-between gap-3 mb-2">
                                          <label className="font-black text-gray-900">جهاز Masar Print Agent</label>
                                          <button
                                              type="button"
                                              onClick={loadAgentPrintDevices}
                                              disabled={agentPrintSending}
                                              className="text-xs font-bold text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-lg disabled:opacity-50"
                                          >
                                              تحديث الأجهزة
                                          </button>
                                      </div>
                                      <select
                                          value={agentPrintForm.agentId}
                                          onChange={(e) => handleAgentSelectionChange(e.target.value)}
                                          disabled={agentPrintSending}
                                          className="w-full border border-gray-300 rounded-xl px-3 py-3 bg-white focus:ring-2 focus:ring-blue-500"
                                      >
                                          <option value="">اختر جهاز Agent...</option>
                                          {agentPrintAgents.map(agent => {
                                              const supportsReports = Array.isArray(agent.capabilities) && agent.capabilities.includes('professional_report_html_exact');
                                              const online = !!agent.isOnline;
                                              return (
                                                  <option
                                                      key={agent.agentId}
                                                      value={agent.agentId}
                                                      disabled={!online || !supportsReports}
                                                  >
                                                      {agent.agentName || agent.computerName || agent.agentId}
                                                      {online ? ' - متصل' : ' - غير متصل'}
                                                      {!supportsReports ? ' - يحتاج تحديث Agent' : ''}
                                                  </option>
                                              );
                                          })}
                                      </select>
                                      {agentPrintAgents.length === 0 && (
                                          <div className="mt-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3 text-sm leading-6">
                                              لا توجد أجهزة Agent مسجلة حاليًا. شغّل Agent على جهاز الطابعة واضغط Save & Start ثم اضغط تحديث الأجهزة.
                                          </div>
                                      )}
                                      {agentPrintAgents.length > 0 && !agentPrintAgents.some(agent => agent.isOnline && Array.isArray(agent.capabilities) && agent.capabilities.includes('professional_report_html_exact')) && (
                                          <div className="mt-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3 text-sm leading-6">
                                              الأجهزة المسجلة تستخدم نسخة Agent قديمة أو غير متصلة. ثبّت Agent v27 Exact HTML على جهاز الطابعة ثم اضغط تحديث الأجهزة.
                                          </div>
                                      )}
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px] gap-3">
                                      <div>
                                          <label className="block font-black text-gray-900 mb-2">الطابعة</label>
                                          <select
                                              value={agentPrintForm.printerName}
                                              onChange={(e) => setAgentPrintForm(prev => ({ ...prev, printerName: e.target.value }))}
                                              disabled={agentPrintSending || !agentPrintForm.agentId}
                                              className="w-full border border-gray-300 rounded-xl px-3 py-3 bg-white focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                                          >
                                              <option value="">اختر الطابعة...</option>
                                              {getAgentPrinters(getAgentPrintSelectedAgent()).map(printer => (
                                                  <option key={printer.name} value={printer.name}>
                                                      {printer.name}{printer.isDefault ? ' - الافتراضية' : ''}
                                                  </option>
                                              ))}
                                          </select>
                                      </div>
                                      <div>
                                          <label className="block font-black text-gray-900 mb-2">عدد النسخ</label>
                                          <input
                                              type="number"
                                              min="1"
                                              max="20"
                                              value={agentPrintForm.copies}
                                              onChange={(e) => setAgentPrintForm(prev => ({ ...prev, copies: e.target.value }))}
                                              disabled={agentPrintSending}
                                              className="w-full border border-gray-300 rounded-xl px-3 py-3 text-center font-black focus:ring-2 focus:ring-blue-500"
                                          />
                                      </div>
                                  </div>
                              </>
                          )}

                          {agentPrintStatus && (
                              <div className={`rounded-2xl border p-4 text-sm font-bold leading-6 ${
                                  agentPrintStatus.type === 'success'
                                      ? 'bg-green-50 border-green-200 text-green-800'
                                      : agentPrintStatus.type === 'error'
                                          ? 'bg-red-50 border-red-200 text-red-800'
                                          : 'bg-blue-50 border-blue-200 text-blue-800'
                              }`}>
                                  {agentPrintStatus.message}
                              </div>
                          )}

                          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-sm text-gray-700 leading-7">
                              <div className="font-black text-gray-900">ما الذي سيتم طباعته؟</div>
                              <div>• كل النتائج الظاهرة بعد البحث واستبعاد الأسماء المخفية مؤقتًا.</div>
                              <div>• نفس العنوان والأعمدة والترتيب وإعدادات إظهار الرأس والإجمالي والتذييل.</div>
                              <div>• التقرير يُقسّم تلقائيًا إلى صفحات A4 ويكرر عناوين الجدول في كل صفحة.</div>
                          </div>
                      </div>

                      <div className="border-t border-gray-200 bg-gray-50 px-4 md:px-5 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3">
                          <button
                              type="button"
                              onClick={closeAgentPrintModal}
                              disabled={agentPrintSending}
                              className="bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 px-5 py-2.5 rounded-xl font-bold disabled:opacity-50"
                          >
                              إغلاق
                          </button>
                          <button
                              type="button"
                              onClick={handleAgentProfessionalPrint}
                              disabled={agentPrintLoading || agentPrintSending || !agentPrintForm.agentId || !agentPrintForm.printerName}
                              className="bg-blue-800 hover:bg-blue-900 text-white px-6 py-2.5 rounded-xl font-black flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                              <Printer size={18} />
                              {agentPrintSending ? 'جاري التنفيذ عبر Agent...' : 'طباعة التقرير عبر Agent'}
                          </button>
                      </div>
                  </div>
              </div>
          )}

          {showBarcodeBatchPrintModal && (
    <div className="fixed inset-0 bg-black/60 z-[10050] flex items-center justify-center p-3 md:p-5" dir="rtl">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-l from-green-800 to-emerald-700 text-white px-5 py-4 flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg md:text-xl font-black flex items-center gap-2">
                        <Printer size={22} /> طباعة باركود جماعية
                    </h2>
                    <p className="text-xs md:text-sm text-green-50 mt-1 leading-6">
                        اختر طريقة محفوظة فقط، وسيتم طباعة الأشخاص الظاهرين واحدًا تلو الآخر بنفس ترتيب الكشف.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={closeBarcodeBatchPrintModal}
                    className="p-2 rounded-xl bg-white/10 hover:bg-white/20 shrink-0"
                    title={barcodeBatchPrinting ? 'إيقاف الطباعة بعد الملصق الجاري' : 'إغلاق'}
                >
                    <X size={21} />
                </button>
            </div>

            <div className="p-4 md:p-5 overflow-y-auto space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
                        <div className="text-xs text-green-700 font-bold">عدد الأشخاص الذين ستتم طباعتهم</div>
                        <div className="text-3xl font-black text-green-900 mt-1">{getWorkingPassports().length}</div>
                        <div className="text-xs text-green-700 mt-1">النتائج الظاهرة بعد استبعاد المخفي مؤقتًا</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                        <div className="text-xs text-slate-600 font-bold">آلية التنفيذ</div>
                        <div className="font-black text-slate-900 mt-1">طباعة متتابعة بالترتيب</div>
                        <div className="text-xs text-slate-500 mt-1">لا توجد هنا إعدادات أو تعديل للباركود</div>
                    </div>
                </div>

                {barcodeBatchProgress && (
                    <div className="border border-blue-200 bg-blue-50 rounded-2xl p-4">
                        <div className="flex items-center justify-between gap-3 mb-2 text-sm font-bold text-blue-900">
                            <span>{barcodeBatchPrinting ? 'جاري إرسال أوامر الطباعة...' : 'آخر حالة للطباعة'}</span>
                            <span>{barcodeBatchProgress.current}/{barcodeBatchProgress.total}</span>
                        </div>
                        <div className="h-3 bg-white rounded-full overflow-hidden border border-blue-100">
                            <div
                                className="h-full bg-blue-600 transition-all duration-300"
                                style={{ width: `${barcodeBatchProgress.total ? Math.round((barcodeBatchProgress.current / barcodeBatchProgress.total) * 100) : 0}%` }}
                            />
                        </div>
                        <div className="mt-2 text-sm text-blue-900">
                            <span className="font-bold">الحالي: </span>{barcodeBatchProgress.name || '—'}
                        </div>
                        <div className="mt-1 text-xs text-blue-700">
                            تمت طباعة {barcodeBatchProgress.printed || 0} شخص بنجاح
                        </div>
                        {barcodeBatchStopRequested && (
                            <div className="mt-3 bg-red-100 border border-red-200 text-red-800 rounded-xl px-3 py-2 text-sm font-black">
                                جارٍ إيقاف باقي الطباعة... لن يتم إرسال أي شخص جديد، وقد يكتمل الملصق الذي وصل للطابعة بالفعل.
                            </div>
                        )}
                    </div>
                )}

                {barcodeBatchProfilesLoading ? (
                    <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-2xl p-5 text-center font-bold">
                        جاري تحميل طرق الطباعة المحفوظة من قاعدة البيانات...
                    </div>
                ) : barcodeBatchProfiles.length > 0 ? (
                    <div className="space-y-2">
                        <div className="font-black text-gray-900">طرق الطباعة المحفوظة</div>
                        {barcodeBatchProfiles.map(profile => {
                            const profileCopies = Math.max(1, Number(profile?.settings?.printCopies) || 1);
                            const isThisPrinting = barcodeBatchPrintingProfileId === profile.id;
                            return (
                                <button
                                    key={profile.id}
                                    type="button"
                                    onClick={() => handleBatchBarcodePrint(profile)}
                                    disabled={barcodeBatchPrinting}
                                    className="w-full text-right flex items-center justify-between gap-3 border border-gray-200 hover:border-green-300 hover:bg-green-50 rounded-2xl px-4 py-3 disabled:opacity-60 disabled:cursor-not-allowed transition"
                                >
                                    <span className="min-w-0">
                                        <span className="block font-black text-gray-900 truncate">{profile.name}</span>
                                        <span className="block text-xs text-gray-500 mt-1">
                                            {profileCopies > 1 ? `${profileCopies} نسخ لكل شخص` : 'نسخة واحدة لكل شخص'} — اضغط لبدء الطباعة
                                        </span>
                                    </span>
                                    <span className="shrink-0 bg-green-700 text-white rounded-xl px-3 py-2 text-xs font-black">
                                        {isThisPrinting ? 'جاري الطباعة...' : 'اختيار وطباعة'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-2xl p-5 leading-7">
                        <div className="font-black">لا توجد طرق طباعة محفوظة.</div>
                        <div className="text-sm mt-1">احفظ طريقة من صفحة الجوازات أولًا، وبعدها ستظهر هنا تلقائيًا. لن يظهر زر ضبط الباركود داخل مركز التقارير.</div>
                    </div>
                )}
            </div>

            <div className="border-t border-gray-200 bg-gray-50 px-4 md:px-5 py-4 flex items-center justify-end gap-3">
                {barcodeBatchPrinting ? (
                    <button
                        type="button"
                        onClick={stopBarcodeBatchPrint}
                        disabled={barcodeBatchStopRequested}
                        className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-black disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <X size={18} />
                        {barcodeBatchStopRequested ? 'جارٍ إيقاف باقي الطباعة...' : 'إيقاف الطباعة الآن'}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={closeBarcodeBatchPrintModal}
                        className="bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 px-5 py-2.5 rounded-xl font-bold"
                    >
                        إغلاق
                    </button>
                )}
            </div>
        </div>
    </div>
)}

          {/* ═══════════════════════════════════════════════════
               Modal ضبط إعدادات التقارير (صورة + طباعة)
          ═══════════════════════════════════════════════════ */}
          {showReportSettingsModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 max-md:items-end max-md:p-0" dir="rtl">
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto max-md:max-w-none max-md:w-full max-md:h-[92dvh] max-md:max-h-[92dvh] max-md:rounded-t-3xl max-md:rounded-b-none max-md:overflow-hidden max-md:flex max-md:flex-col">

                      {/* رأس الـ Modal */}
                      <div className="flex items-center justify-between p-5 max-md:p-4 border-b bg-gradient-to-r from-amber-50 to-orange-50 rounded-t-2xl">
                          <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14M12 2v2M12 20v2M2 12h2M20 12h2"/>
                                  </svg>
                              </div>
                              <div>
                                  <h2 className="text-lg font-bold text-gray-900">ضبط إعدادات التقارير</h2>
                                  <p className="text-xs text-gray-500">إعدادات الصورة خاصة بالتصدير كصورة فقط • الطباعة تظل بنفس شكلها</p>
                              </div>
                          </div>
                          <button onClick={() => setShowReportSettingsModal(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors">
                              <X size={20} />
                          </button>
                      </div>

                      <div className="p-5 space-y-6 max-md:flex-1 max-md:overflow-y-auto max-md:p-4 max-md:space-y-4">

                          {/* ─── قسم إعدادات الصورة ─── */}
                          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                              <div className="flex items-center gap-2 mb-4">
                                  <span className="text-lg">🖼️</span>
                                  <h3 className="text-sm font-bold text-slate-700">إعدادات الصورة</h3>
                                  <span className="text-xs text-slate-400 mr-auto bg-slate-200 px-2 py-0.5 rounded-full">تنطبق على "تصدير كصورة" فقط</span>
                              </div>

                              {/* الجزء العلوي - تحكم منفصل */}
                              <div className="mb-3">
                                  <div className="text-xs font-semibold text-gray-500 mb-2 px-1">📌 الجزء العلوي للصورة</div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                      <label className="flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 border border-slate-200 cursor-pointer hover:border-amber-400 transition-colors">
                                          <input type="checkbox" className="w-4 h-4 accent-amber-500"
                                              checked={imageExportSettings.showHeader}
                                              onChange={(e) => setImageExportSettings(prev => ({ ...prev, showHeader: e.target.checked }))} />
                                          <div>
                                              <div className="text-xs font-semibold text-gray-700">إظهار الجزء العلوي</div>
                                              <div className="text-xs text-gray-400">العنوان + التاريخ</div>
                                          </div>
                                      </label>
                                      <label className={`flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 border cursor-pointer transition-colors ${imageExportSettings.showHeader ? 'border-slate-200 hover:border-amber-400' : 'border-slate-100 opacity-40 cursor-not-allowed'}`}>
                                          <input type="checkbox" className="w-4 h-4 accent-amber-500"
                                              checked={imageExportSettings.showTotal}
                                              disabled={!imageExportSettings.showHeader}
                                              onChange={(e) => setImageExportSettings(prev => ({ ...prev, showTotal: e.target.checked }))} />
                                          <div>
                                              <div className="text-xs font-semibold text-gray-700">إجمالي الجوازات</div>
                                              <div className="text-xs text-gray-400">بطاقة العدد</div>
                                          </div>
                                      </label>
                                      <label className={`flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 border cursor-pointer transition-colors ${imageExportSettings.showHeader ? 'border-slate-200 hover:border-amber-400' : 'border-slate-100 opacity-40 cursor-not-allowed'}`}>
                                          <input type="checkbox" className="w-4 h-4 accent-amber-500"
                                              checked={imageExportSettings.showPageNum}
                                              disabled={!imageExportSettings.showHeader}
                                              onChange={(e) => setImageExportSettings(prev => ({ ...prev, showPageNum: e.target.checked }))} />
                                          <div>
                                              <div className="text-xs font-semibold text-gray-700">رقم الصفحة</div>
                                              <div className="text-xs text-gray-400">بطاقة الصفحة</div>
                                          </div>
                                      </label>
                                  </div>
                              </div>

                              {/* الجزء السفلي */}
                              <div className="mb-3">
                                  <div className="text-xs font-semibold text-gray-500 mb-2 px-1">📌 الجزء السفلي للصورة</div>
                                  <label className="flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 border border-slate-200 cursor-pointer hover:border-amber-400 transition-colors w-full sm:w-1/3">
                                      <input type="checkbox" className="w-4 h-4 accent-amber-500"
                                          checked={imageExportSettings.showFooter}
                                          onChange={(e) => setImageExportSettings(prev => ({ ...prev, showFooter: e.target.checked }))} />
                                      <div>
                                          <div className="text-xs font-semibold text-gray-700">إظهار الجزء السفلي</div>
                                          <div className="text-xs text-gray-400">نظام إدارة الجوازات</div>
                                      </div>
                                  </label>
                              </div>

                              {/* عدد الصفوف والعنوان */}
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                                  {/* عدد الصفوف */}
                                  <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-1">🔢 صفوف في كل صورة</div>
                                      <input
                                          type="number"
                                          min="5"
                                          max="200"
                                          value={imageExportSettings.maxRowsPerImage}
                                          onChange={(e) => setImageExportSettings(prev => ({ ...prev, maxRowsPerImage: parseInt(e.target.value) || 25 }))}
                                          className="w-full border border-gray-200 rounded px-2 py-1 text-center text-sm font-bold focus:outline-none focus:ring-1 focus:ring-amber-400"
                                      />
                                  </div>

                                  {/* العنوان المخصص */}
                                  <div className="bg-white rounded-lg px-3 py-2 border border-slate-200 sm:col-span-2">
                                      <div className="text-xs font-semibold text-gray-700 mb-1">✏️ عنوان مخصص <span className="text-gray-400 font-normal">(يُستخدم في الصورة والطباعة)</span></div>
                                      <input
                                          type="text"
                                          placeholder="مثال: كشف رقم 5"
                                          value={imageExportSettings.customTitle}
                                          onChange={(e) => setImageExportSettings(prev => ({ ...prev, customTitle: e.target.value }))}
                                          className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400"
                                          dir="rtl"
                                      />
                                  </div>
                              </div>

                              {/* اختيار اللون */}
                              <div>
                                  <div className="text-xs font-semibold text-gray-600 mb-2">🎨 لون الصورة</div>
                                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                      {Object.entries(colorThemes).map(([key, thm]) => (
                                          <button
                                              key={key}
                                              onClick={() => setImageExportSettings(prev => ({ ...prev, colorTheme: key }))}
                                              title={thm.label}
                                              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                                                  imageExportSettings.colorTheme === key
                                                      ? 'border-gray-800 shadow-md scale-105'
                                                      : 'border-gray-200 hover:border-gray-400'
                                              }`}
                                              style={{ background: imageExportSettings.colorTheme === key ? thm.cardBg : 'white' }}
                                          >
                                              <span className="w-4 h-4 rounded-full flex-shrink-0 border border-white shadow-sm" style={{ background: thm.dot }} />
                                              <span className="text-gray-700 truncate">{thm.label}</span>
                                          </button>
                                      ))}
                                  </div>
                              </div>
                          </div>

                          {/* ─── قسم إعدادات الطباعة ─── */}
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                              <div className="flex items-center gap-2 mb-3">
                                  <span className="text-lg">🖨️</span>
                                  <h3 className="text-sm font-bold text-blue-700">إعدادات الطباعة الاحترافية</h3>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                  <div className="flex items-start gap-1.5 bg-white rounded-lg p-2 border border-blue-100">
                                      <span className="text-blue-500 mt-0.5">✓</span>
                                      <div><span className="font-semibold text-blue-800">العنوان المخصص</span><br/><span className="text-blue-600">يُستخدم من حقل العنوان أعلاه</span></div>
                                  </div>
                                  <div className="flex items-start gap-1.5 bg-white rounded-lg p-2 border border-blue-100">
                                      <span className="text-blue-500 mt-0.5">✓</span>
                                      <div><span className="font-semibold text-blue-800">إخفاء الجزء العلوي</span><br/><span className="text-blue-600">إعداد "الجزء العلوي" أعلاه يطبق</span></div>
                                  </div>
                                  <div className="flex items-start gap-1.5 bg-white rounded-lg p-2 border border-blue-100">
                                      <span className="text-blue-500 mt-0.5">✓</span>
                                      <div><span className="font-semibold text-blue-800">إخفاء الجزء السفلي</span><br/><span className="text-blue-600">إعداد "الجزء السفلي" أعلاه يطبق</span></div>
                                  </div>
                              </div>
                              <p className="text-xs text-blue-500 mt-2">
                                  🎨 اللون لا يؤثر على الطباعة — تظهر دائماً بالأزرق الرسمي الأسود.
                              </p>
                          </div>

                          {/* معاينة اللون المختار */}
                          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                              <div className="text-xs font-semibold text-gray-600 px-3 py-2 bg-gray-50 border-b">👁️ معاينة اللون المختار</div>
                              <div style={{ background: colorThemes[imageExportSettings.colorTheme]?.bg || colorThemes.blue.bg, padding: '16px' }}>
                                  <div style={{ background: colorThemes[imageExportSettings.colorTheme]?.cardBg || colorThemes.blue.cardBg, borderRadius: '8px', padding: '12px', border: `2px solid ${colorThemes[imageExportSettings.colorTheme]?.cardBorder || '#1976d2'}`, textAlign: 'center' }}>
                                      <div style={{ color: colorThemes[imageExportSettings.colorTheme]?.titleColor || '#0d47a1', fontWeight: 'bold', fontSize: '14px', marginBottom: '8px' }}>
                                          📋 {imageExportSettings.customTitle || 'كشف الجوازات'}
                                      </div>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                          <thead>
                                              <tr>
                                                  {['م', 'الاسم', 'الشركة', 'المهنة'].map(h => (
                                                      <th key={h} style={{ padding: '6px 8px', background: colorThemes[imageExportSettings.colorTheme]?.headerBg || colorThemes.blue.headerBg, color: 'white', border: `1px solid ${colorThemes[imageExportSettings.colorTheme]?.headerBorder || '#0d47a1'}` }}>{h}</th>
                                                  ))}
                                              </tr>
                                          </thead>
                                          <tbody>
                                              {[['1','محمد أحمد','شركة النور','مهندس'],['2','علي محمود','مؤسسة السلام','فني']].map((row, i) => (
                                                  <tr key={i}>
                                                      {row.map((cell, j) => (
                                                          <td key={j} style={{ padding: '5px 8px', border: `1px solid ${colorThemes[imageExportSettings.colorTheme]?.cellBorder || '#bbdefb'}`, background: i % 2 === 0 ? colorThemes[imageExportSettings.colorTheme]?.rowOdd : colorThemes[imageExportSettings.colorTheme]?.rowEven, textAlign: 'center' }}>{cell}</td>
                                                      ))}
                                                  </tr>
                                              ))}
                                          </tbody>
                                      </table>
                                  </div>
                              </div>
                          </div>

                      </div>

                      {/* أزرار الـ Modal */}
                      <div className="flex justify-between items-center px-5 py-4 border-t bg-gray-50 rounded-b-2xl max-md:rounded-b-none max-md:flex-col max-md:gap-2 max-md:px-4">
                          <button
                              onClick={() => setImageExportSettings({ showHeader: true, showTotal: true, showPageNum: true, showFooter: true, customTitle: '', maxRowsPerImage: 25, colorTheme: 'blue' })}
                              className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 px-3 py-2 hover:bg-gray-200 rounded-lg transition-colors max-md:w-full max-md:order-2"
                          >
                              <RefreshCw size={14} />
                              إعادة تعيين
                          </button>
                          <button
                              onClick={() => setShowReportSettingsModal(false)}
                              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 max-md:w-full max-md:order-1"
                          >
                              <Check size={16} />
                              حفظ الإعدادات
                          </button>
                      </div>
                  </div>
              </div>
          )}

          {/* ─── نافذة اختيار نوع الصور للتصدير ZIP ─── */}
          {showWorkListPeopleModal && (
            <div className="fixed inset-0 z-[1300] bg-black/55 backdrop-blur-sm flex items-center justify-center p-3" dir="rtl">
              <div className="w-full max-w-4xl max-h-[92vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
                <div className="px-5 py-4 border-b bg-slate-50 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-black text-lg text-slate-900 flex items-center gap-2"><ListPlus size={20}/> اختر الأشخاص</h3>
                    <p className="text-xs text-slate-500 mt-1">حدد أشخاصاً من النتائج الحالية ثم اختر قائمة العمل.</p>
                  </div>
                  <button onClick={()=>setShowWorkListPeopleModal(false)} className="p-2 rounded-xl hover:bg-slate-200"><X size={20}/></button>
                </div>
                <div className="p-4 border-b bg-white flex items-center gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search size={17} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input value={workListPeopleSearch} onChange={e=>{setWorkListPeopleSearch(e.target.value);setWorkListPeoplePage(1);}} placeholder="ابحث بالاسم أو الهاتف أو رقم الجواز أو المندوب أو الشركة..." className="w-full pr-10 pl-3 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-cyan-100"/>
                  </div>
                  <button onClick={toggleWorkListPage} className="px-3 py-2.5 rounded-xl border bg-white font-bold">تحديد/إلغاء الصفحة</button>
                  <span className="text-sm font-black text-cyan-800">محدد: {workListSelectedPassportIds.size.toLocaleString('ar-EG')}</span>
                </div>
                <div className="overflow-y-auto flex-1 p-3">
                  <div className="space-y-2">
                    {workListPeoplePageRows.map(passport => {
                      const selected = workListSelectedPassportIds.has(passport.PassportID);
                      const delegateName = getDelegateNameById(passport.DelegateID);
                      const companyName = getCompanyNameById(passport.CompanyID);
                      return <label key={passport.PassportID} className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer ${selected?'bg-cyan-50 border-cyan-300':'bg-white border-slate-200 hover:bg-slate-50'}`}>
                        <input type="checkbox" checked={selected} onChange={()=>toggleWorkListPassport(passport.PassportID)} className="w-5 h-5"/>
                        <div className="flex-1 min-w-0"><div className="font-black text-slate-900">{passport.NameArabic || '-'}</div><div className="text-xs text-slate-500 mt-1 truncate">{delegateName || '-'} • {companyName || '-'} • ملف {getPassportFileNumber(passport) || passport.PassportID}</div></div>
                        <div className="text-xs font-bold text-slate-500" dir="ltr">{(() => {try {const a=JSON.parse(passport.PhoneNumbers||'[]');return Array.isArray(a)?(a[0]||''):String(passport.PhoneNumbers||'');} catch {return String(passport.PhoneNumbers||'');}})()}</div>
                      </label>;
                    })}
                    {!workListPeoplePageRows.length&&<div className="py-12 text-center text-slate-500">لا توجد نتائج مطابقة.</div>}
                  </div>
                </div>
                <div className="px-5 py-4 border-t bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-1">
                    <button disabled={safeWorkListPeoplePage<=1} onClick={()=>setWorkListPeoplePage(1)} className="px-3 py-2 rounded-lg border bg-white disabled:opacity-40">الأولى</button>
                    <button disabled={safeWorkListPeoplePage<=1} onClick={()=>setWorkListPeoplePage(p=>Math.max(1,p-1))} className="px-3 py-2 rounded-lg border bg-white disabled:opacity-40">السابق</button>
                    <span className="px-3 text-sm font-bold">{safeWorkListPeoplePage} / {workListPeopleTotalPages}</span>
                    <button disabled={safeWorkListPeoplePage>=workListPeopleTotalPages} onClick={()=>setWorkListPeoplePage(p=>Math.min(workListPeopleTotalPages,p+1))} className="px-3 py-2 rounded-lg border bg-white disabled:opacity-40">التالي</button>
                    <button disabled={safeWorkListPeoplePage>=workListPeopleTotalPages} onClick={()=>setWorkListPeoplePage(workListPeopleTotalPages)} className="px-3 py-2 rounded-lg border bg-white disabled:opacity-40">الأخيرة</button>
                  </div>
                  <div className="flex gap-2"><button onClick={()=>setShowWorkListPeopleModal(false)} className="px-4 py-2.5 rounded-xl border bg-white font-bold">إلغاء</button><button onClick={continueToWorkListPicker} disabled={!workListSelectedPassportIds.size} className="px-5 py-2.5 rounded-xl bg-cyan-700 hover:bg-cyan-800 text-white font-black disabled:opacity-40 inline-flex items-center gap-2"><ListPlus size={17}/> اختر قائمة العمل</button></div>
                </div>
              </div>
            </div>
          )}

          <WorkListPickerModal
            open={showWorkListPicker}
            onClose={()=>setShowWorkListPicker(false)}
            items={workListPickerItems}
            onDone={(result)=>showToast(`تمت إضافة ${Number(result?.added||0).toLocaleString('ar-EG')} شخص إلى قائمة العمل${Number(result?.skipped||0)>0?` — ${Number(result.skipped).toLocaleString('ar-EG')} موجود بالفعل`:''}`,'success')}
          />

          {showImageTypeModal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center max-md:items-end"
              style={{ background: 'rgba(0,0,0,0.45)' }}
              onClick={(e) => { if (e.target === e.currentTarget) setShowImageTypeModal(false); }}
            >
              <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden max-md:max-w-none max-md:mx-0 max-md:rounded-t-3xl max-md:rounded-b-none max-md:max-h-[88dvh] max-md:flex max-md:flex-col"
                dir="rtl"
                style={{ animation: 'slideUp 0.25s ease-out' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-l from-blue-600 to-blue-700">
                  <div className="flex items-center gap-2 text-white">
                    <span className="text-xl">🗜️</span>
                    <h2 className="font-bold text-base">اختر أنواع الصور للتصدير</h2>
                  </div>
                  <button
                    onClick={() => setShowImageTypeModal(false)}
                    className="text-white/70 hover:text-white transition-colors rounded-lg p-1"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Body */}
                <div className="px-5 py-5 space-y-3 max-md:overflow-y-auto max-md:flex-1">
                  <p className="text-xs text-gray-500 mb-1">يمكنك اختيار نوع أو أكثر — سيتم تضمينها جميعاً في ملف ZIP واحد.</p>
                  {imageTypeOptions.map(opt => (
                    <label
                      key={opt.key}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                        selectedImageTypes[opt.key]
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-all ${
                        selectedImageTypes[opt.key]
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300 bg-white'
                      }`}>
                        {selectedImageTypes[opt.key] && (
                          <Check size={12} color="white" strokeWidth={3} />
                        )}
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={!!selectedImageTypes[opt.key]}
                        onChange={() =>
                          setSelectedImageTypes(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))
                        }
                      />
                      <span className={`text-sm font-medium ${selectedImageTypes[opt.key] ? 'text-blue-700' : 'text-gray-700'}`}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50">
                  <button
                    onClick={() => setShowImageTypeModal(false)}
                    className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleConfirmImageZipExport}
                    disabled={zipLoading || !Object.values(selectedImageTypes).some(Boolean)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl font-medium text-sm transition-colors"
                  >
                    <Download size={15} />
                    {zipLoading ? 'جارٍ التصدير...' : 'تصدير ZIP'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* CSS للرسوم المتحركة */}
          <style dangerouslySetInnerHTML={{
              __html: `
              @keyframes slideUp {
                  from { opacity: 0; transform: translateY(20px); }
                  to { opacity: 1; transform: translateY(0); }
              }
              .animate-slide-up {
                  animation: slideUp 0.3s ease-out forwards;
              }
              @keyframes mobileSheetUp {
                  from { transform: translateY(100%); opacity: 0.75; }
                  to { transform: translateY(0); opacity: 1; }
              }
              @media (max-width: 767px) {
                  html, body, #root { max-width: 100vw !important; overflow-x: hidden !important; }
                  .passport-report-page, .passport-report-page * { box-sizing: border-box; }
                  .passport-report-page { width: 100vw; max-width: 100vw; overflow-x: hidden; }
                  .passport-report-page input,
                  .passport-report-page select,
                  .passport-report-page textarea,
                  .passport-report-page button { min-width: 0; }
                  .passport-report-page input[type="date"],
                  .passport-report-page select,
                  .passport-report-page input[type="text"] {
                      width: 100%;
                      max-width: 100%;
                      min-height: 44px;
                      font-size: 16px;
                  }
                  .passport-report-page input[type="date"] {
                      direction: ltr;
                      text-align: center;
                      color: #111827;
                      background: #fff;
                  }
                  .mobile-report-bottom-sheet { animation: mobileSheetUp 0.22s ease-out forwards; }
              }
              `
          }} />
      </div>
  );
};

export default PassportReport;